import React, { useEffect, useRef, useMemo, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { CityData, ZoneType, NavEdge, NavNode } from '../../types/map';
import { GameState, Card, InformantTip, SpotterDevice, ItemCategory } from '../../types/game';
import { getItemIconSvgHtml } from '../ui/ItemIcon';
import { findPathBetweenPoints, createStraightLinePath, PathResult, snapPointToEdges } from '../../engine/pathfinding';
// Signal types used for rendering
import { Signal } from '../../types/game';
import { getAwarenessMultiplier, computeMovementExposureGain, computeMovementSignalStrength } from '../../engine/signal';
import { computePassiveSignalMultiplier } from '../../engine/passiveEffects';
import { theme } from '../../styles/theme';
import { tuning } from '../../data/tuning';
import { buildZoneLookup, zoneAtPoint } from '../../pipeline/processor';
import { ITEMS } from '../../data/items';

export type TransitType = 'subway' | 'tram' | 'bus' | 'ferry';

export interface HoverInfo {
  type: 'street' | 'transit' | 'stop';
  name: string;
  lineName?: string;
  zone?: string;
  signalMultiplier?: number;
  roadClass?: 'major' | 'minor' | 'residential' | 'footpath';
  roadSignalWeight?: number;
}

export interface MovementMode {
  card: Card;
  maxRange: number; // meters
  isShortcut?: boolean; // true = straight-line movement through buildings
  isVehicle?: boolean; // true = can't use footpaths
}

export interface SweepMode {
  card: Card;
  radius: number; // meters
}

interface MapRendererProps {
  cityData: CityData;
  gameState?: GameState;
  visibleTransitTypes: Set<TransitType>;
  onHover?: (info: HoverInfo | null) => void;
  movementMode?: MovementMode | null;
  altMovementMode?: boolean;
  onMovementComplete?: (path: PathResult) => void;
  onMovementPartial?: (path: PathResult) => void;
  onMovementCancel?: () => void;
  sweepMode?: SweepMode | null;
  onSweepPlace?: (lat: number, lng: number) => void;
  hideMode?: boolean;
  rooftopMode?: boolean;
  onHideSelect?: (buildingFeatureId: number, buildingInfo?: { centroid: { lat: number; lng: number }; levels: number | null }) => void;
  decoyMode?: boolean;
  decoyRadius?: number; // meters — for preview circle at cursor
  onDecoyPlace?: (lat: number, lng: number) => void;
  wireTapMode?: boolean; // Wire Tap card: click a street to place tap
  onWireTapPlace?: (lat: number, lng: number) => void;
  burnerPhoneMode?: boolean; // Burner Phone item-use: click anywhere to drop a decoy signal
  burnerPhoneRadius?: number; // meters — for the placement preview circle
  onBurnerPhonePlace?: (lat: number, lng: number) => void;
  jammerMode?: boolean; // Jammer item-use: click anywhere to drop a signal-absorption zone
  jammerRadius?: number; // meters — for the placement preview circle
  onJammerPlace?: (lat: number, lng: number) => void;
  misdirectMode?: boolean; // Misdirect card: click to place fake movement path
  onMisdirectPlace?: (lat: number, lng: number) => void;
  /** Called on every mousemove while placing a Misdirect path so the UI can
   * show a live distance readout (same pattern as onDoppelgangerPreview). */
  onMisdirectPreview?: (info: { distance: number; overRange: boolean } | null) => void;
  doppelgangerMode?: boolean; // Doppelgänger card: click a point up to 800m away to set the decoy's path
  onDoppelgangerPlace?: (lat: number, lng: number) => void;
  /** Called on every mousemove while placing a Doppelgänger path so the UI
   * can show a live distance readout (mirrors onMovementPreview for Move
   * cards). `null` = no valid path under cursor / placement mode exited. */
  onDoppelgangerPreview?: (info: { distance: number; overRange: boolean } | null) => void;
  doppelganger?: { pathCoords: [number, number][]; currentPos: { lat: number; lng: number } } | null;
  overwatchMode?: boolean; // Overwatch card: hover to aim a 300m road segment anchored to the player
  onOverwatchPlace?: (lat: number, lng: number) => void;
  /** Called on every mousemove while placing an Overwatch segment so the UI
   * can show a live distance readout. `overRange` is always false here —
   * the segment auto-truncates at 300m and the preview never exceeds that. */
  onOverwatchPreview?: (info: { distance: number } | null) => void;
  overwatchSegment?: [number, number][] | null;
  taxiSelectMode?: boolean; // Taxi card: click a road to pick destination
  onTaxiSelectPlace?: (lat: number, lng: number) => void;
  reconPlaneMode?: boolean; // Recon Plane card: click-drag-release to place a rotating strip
  reconPlanePending?: { center: { lat: number; lng: number }; angleDeg: number; lengthM: number; widthM: number } | null;
  onReconPlanePlace?: (center: { lat: number; lng: number }, angleDeg: number) => void;
  stakeoutMode?: boolean; // Stakeout card: two-click placement, first for building, second for angle
  stakeoutPendingBuilding?: { position: { lat: number; lng: number }; buildingFeatureId: number } | null;
  onStakeoutBuildingSelect?: (buildingFeatureId: number, centroid: { lat: number; lng: number }) => void;
  onStakeoutAngleCommit?: (clickLat: number, clickLng: number) => void;
  surveillanceVanMode?: boolean; // Surveillance Van: hold-and-drag placement
  onSurveillanceVanRoutePlace?: (coords: [number, number][]) => void;
  onSurveillanceVanPreview?: (info: { distance: number; overBudget: boolean } | null) => void;
  /** Transient signal pulse — renders a fading SVG ring next to the player
   *  marker showing the last passive signal strength (0..1). id increments
   *  on each new pulse so repeat pulses at the same position retrigger the
   *  animation. Null when nothing is playing. */
  signalPulse?: { strength: number; lat: number; lng: number; id: number; exposureGain?: number } | null;
  /** Run-scoped state (passives etc.) — needed so the movement preview can
   *  feed computePassiveSignalMultiplier into the same signal math as the
   *  real signal generation, so the banner matches what the player actually
   *  emits when they commit the move. */
  runState?: import('../../types/run').RunState | null;
  onMovementPreview?: (info: { distance: number; signalStrength: number; exposureGain: number; nearFootpath: boolean } | null) => void;
  teleportMode?: boolean; // test mode: click to teleport player to nearest road
  onTeleport?: (lat: number, lng: number) => void;
  recenterTrigger?: number; // increment to fly-to the player position (or recenterTarget)
  recenterTarget?: { lat: number; lng: number } | null; // optional override — if set, fly here instead of player
  /** Optional zoom to snap to on recenter. null/undefined means keep current zoom.
   * Used e.g. on movement-card selection to zoom into a comfortable placement view. */
  recenterZoom?: number | null;
  showEnemySignals?: boolean;
  showOwnSignals?: boolean;
  /** Counter-Surveillance active this turn. Gates the visibility of
   * agent passive signals from the prior turn — C-Surv bypasses the
   * blind-by-default rule that hides enemy passive signals. */
  counterSurveillanceActive?: boolean;
  signalMaxAge?: number;
  showZones?: boolean;
  showRoads?: boolean;
  onFacingChange?: (degrees: number) => void;
  agentVisible?: boolean; // true when player can see agent at all (cone + dot)
  /**
   * When set, the agent marker dot is drawn independently of the cone. If
   * omitted, falls back to `agentVisible` (back-compat). Used to implement
   * cone-only reveals like the Cold Opening passive, where the player should
   * see where the agent is facing but not the exact position marker.
   */
  agentDotVisible?: boolean;
  spotterReveal?: boolean; // true when agent is revealed by a spotter device
  informantTips?: InformantTip[]; // informant POI markers on map
  spotters?: SpotterDevice[]; // spotter zones with dashed circles
  agentFacing?: number; // degrees — agent's current facing direction (for vision cone rendering)
  showAgentBelief?: boolean; // when true, shows the agent's belief marker + uncertainty radius (debug)
  debriefPaths?: { playerPath: [number, number][]; agentPath: [number, number][] } | null;
  /** Agent movement coords to animate when agent is visible — set once per turn, cleared after animation */
  agentAnimationPath?: [number, number][] | null;
  onAgentAnimationDone?: () => void;
  onAltRoutesCount?: (count: number) => void;
}

// Circle cursor matching the hover radius — 24px diameter (12px radius)
const CROSSHAIR_CURSOR = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24'%3E%3Ccircle cx='12' cy='12' r='11' fill='none' stroke='white' stroke-width='1.5' opacity='0.7'/%3E%3Ccircle cx='12' cy='12' r='1.5' fill='white' opacity='0.9'/%3E%3C/svg%3E") 12 12, crosshair`;

/**
 * Wrap a vehicle / transit SVG in a blue circle + white outline badge
 * with the icon itself tinted white and centered. The original icons
 * (car, taxi, subway, tram, bus, ferry) are too thin and hard to spot
 * against the map without a high-contrast backing. We pull the inner
 * content out of the fetched SVG, drop it on a blue disk with a white
 * ring, and return a self-contained badge SVG. All source icons use
 * viewBox="0 0 26 26" so the transform math is shared.
 *
 * `color` is the badge fill (player-marker blue, taxi yellow, etc.).
 */
function wrapVehicleIconAsBadge(svgText: string, color: string): string {
  const innerMatch = svgText.match(/<svg[^>]*>([\s\S]*?)<\/svg>/i);
  const inner = innerMatch ? innerMatch[1] : svgText;
  // Final viewBox is 36x36 so we have padding around a 26x26 icon for
  // the ring. Icon content is centered via translate(5,5) (36-26)/2.
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36" width="72" height="72"><circle cx="18" cy="18" r="17" fill="${color}"/><circle cx="18" cy="18" r="15.5" fill="none" stroke="#ffffff" stroke-width="1.75"/><g transform="translate(5 5)" fill="#ffffff">${inner}</g></svg>`;
}

// Crosshair cursor for movement mode
const MOVE_CURSOR = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24'%3E%3Cline x1='12' y1='2' x2='12' y2='22' stroke='%2344aaff' stroke-width='1.5' opacity='0.8'/%3E%3Cline x1='2' y1='12' x2='22' y2='12' stroke='%2344aaff' stroke-width='1.5' opacity='0.8'/%3E%3Ccircle cx='12' cy='12' r='3' fill='none' stroke='%2344aaff' stroke-width='1' opacity='0.6'/%3E%3C/svg%3E") 12 12, crosshair`;


export function MapRenderer({ cityData, gameState, visibleTransitTypes, onHover, movementMode, altMovementMode = false, onMovementComplete, onMovementPartial, onMovementCancel: _onMovementCancel, sweepMode, onSweepPlace, hideMode, rooftopMode, onHideSelect, decoyMode, decoyRadius = 500, onDecoyPlace, wireTapMode = false, onWireTapPlace, burnerPhoneMode = false, burnerPhoneRadius = 400, onBurnerPhonePlace, jammerMode = false, jammerRadius = 350, onJammerPlace, teleportMode = false, onTeleport, recenterTrigger = 0, recenterTarget = null, recenterZoom = null, showEnemySignals = true, showOwnSignals = false, counterSurveillanceActive = false, signalMaxAge = 3, showZones = false, showRoads = false, onFacingChange, agentVisible = false, agentDotVisible, spotterReveal = false, informantTips = [], spotters = [], agentFacing = 0, showAgentBelief = false, debriefPaths = null, misdirectMode = false, onMisdirectPlace, onMisdirectPreview, doppelgangerMode = false, onDoppelgangerPlace, onDoppelgangerPreview, doppelganger = null, overwatchMode = false, onOverwatchPlace, onOverwatchPreview, overwatchSegment = null, taxiSelectMode = false, onTaxiSelectPlace, reconPlaneMode = false, reconPlanePending = null, onReconPlanePlace, stakeoutMode = false, stakeoutPendingBuilding = null, onStakeoutBuildingSelect, onStakeoutAngleCommit, surveillanceVanMode = false, onSurveillanceVanRoutePlace, onSurveillanceVanPreview, signalPulse = null, runState = null, onMovementPreview, agentAnimationPath = null, onAgentAnimationDone, onAltRoutesCount }: MapRendererProps) {
  // Cone always follows agentVisible. The dot follows agentDotVisible when
  // explicitly provided, otherwise falls back to agentVisible (back-compat).
  // Cold Opening passes agentVisible=true + agentDotVisible=false to show
  // cone-only reveals.
  const dotVisible = agentDotVisible ?? agentVisible;
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  // Flips to a unique value when addDataLayers finishes so device-rendering
  // effects that depend on pre-created sources re-fire after sources exist.
  // Without this, loading from a save with placed stakeouts/vans/wiretaps
  // loses them because the effects fire before map.on('load') runs.
  const [mapLayersReady, setMapLayersReady] = useState(0);
  const spaceHeldRef = useRef(false);
  const movementModeRef = useRef<MovementMode | null>(null);
  const currentPathRef = useRef<PathResult | null>(null);
  const isAnimatingRef = useRef(false);
  const lastCursorLngLatRef = useRef<{ lng: number; lat: number } | null>(null);

  const onMovementPreviewRef = useRef(onMovementPreview);
  onMovementPreviewRef.current = onMovementPreview;

  // Keep refs in sync with props so handlers always see latest values
  movementModeRef.current = movementMode ?? null;
  const gameStateRef = useRef(gameState);
  gameStateRef.current = gameState;
  const onMovementCompleteRef = useRef(onMovementComplete);
  onMovementCompleteRef.current = onMovementComplete;
  const onFacingChangeRef = useRef(onFacingChange);
  onFacingChangeRef.current = onFacingChange;
  const onMovementPartialRef = useRef(onMovementPartial);
  onMovementPartialRef.current = onMovementPartial;
  const altMovementModeRef = useRef(altMovementMode);
  altMovementModeRef.current = altMovementMode;
  const onAltRoutesCountRef = useRef(onAltRoutesCount);
  onAltRoutesCountRef.current = onAltRoutesCount;

  // Alt movement state: stored routes and hovered route index
  const altRoutesRef = useRef<PathResult[]>([]);
  const altHoveredIndexRef = useRef<number>(-1);

  // Memoize the zone polygon lookup used by the movement preview (Fade buff
  // condition check). Rebuilding it on every mousemove is measurable on
  // bigger cities.
  const zoneLookup = useMemo(
    () => buildZoneLookup(cityData.layers.zones),
    [cityData.layers.zones],
  );
  const runStateRef = useRef(runState);
  runStateRef.current = runState;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    // Clamp zoom-out and panning to stay near the city bounds. We pad the raw
    // bounds by 50% on each side so the player can zoom out enough to see the
    // whole playable area with a healthy grey margin, and pan a long way into
    // the empty space when hunting for something near an edge. Tighter clamps
    // felt claustrophobic; this gives breathing room without letting them get
    // completely lost in the void.
    const b = cityData.bounds;
    const latPad = (b.north - b.south) * 0.5;
    const lngPad = (b.east - b.west) * 0.5;
    const clampedBounds: [[number, number], [number, number]] = [
      [b.west - lngPad, b.south - latPad],
      [b.east + lngPad, b.north + latPad],
    ];

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
        sources: {},
        layers: [
          {
            id: 'background',
            type: 'background',
            paint: { 'background-color': theme.colors.background },
          },
        ],
      },
      center: [cityData.center.lng, cityData.center.lat],
      zoom: 14,
      // minZoom stays permissive — maxBounds does the real "can't zoom out
      // past the city" work. MapLibre refuses to zoom out once the bounds
      // would no longer fill the viewport, so the effective minZoom is
      // computed automatically from the city size.
      minZoom: 11,
      maxZoom: 18,
      maxBounds: clampedBounds,
      attributionControl: false,
    });

    mapRef.current = map;

    // Lock rotation — north always up
    map.dragRotate.disable();
    map.touchZoomRotate.disableRotation();

    // Set default crosshair cursor
    map.getCanvas().style.cursor = CROSSHAIR_CURSOR;

    // WASD panning — smooth with acceleration/deceleration
    const MAX_PAN_SPEED = 7;  // px per frame at full speed
    const ACCEL = 0.7;        // px/frame² ramp up
    const DECEL = 1.4;        // px/frame² ramp down (faster than accel for snappy stop)
    const wasdKeys = new Set(['KeyW', 'KeyA', 'KeyS', 'KeyD']);
    let wasdRaf: number | null = null;
    const activeWasd = new Set<string>();
    let panVx = 0, panVy = 0;

    const wasdTick = () => {
      // Target direction from held keys
      let tx = 0, ty = 0;
      if (activeWasd.has('KeyW')) ty = -1;
      if (activeWasd.has('KeyS')) ty = 1;
      if (activeWasd.has('KeyA')) tx = -1;
      if (activeWasd.has('KeyD')) tx = 1;
      // Normalize diagonal
      const len = Math.sqrt(tx * tx + ty * ty) || 1;
      tx /= len; ty /= len;

      // Accelerate toward target, decelerate when no input
      if (tx !== 0) {
        panVx += tx * ACCEL;
        panVx = Math.max(-MAX_PAN_SPEED, Math.min(MAX_PAN_SPEED, panVx));
      } else {
        if (Math.abs(panVx) < DECEL) panVx = 0;
        else panVx -= Math.sign(panVx) * DECEL;
      }
      if (ty !== 0) {
        panVy += ty * ACCEL;
        panVy = Math.max(-MAX_PAN_SPEED, Math.min(MAX_PAN_SPEED, panVy));
      } else {
        if (Math.abs(panVy) < DECEL) panVy = 0;
        else panVy -= Math.sign(panVy) * DECEL;
      }

      if (Math.abs(panVx) > 0.1 || Math.abs(panVy) > 0.1) {
        map.panBy([panVx, panVy], { duration: 0 });
        wasdRaf = requestAnimationFrame(wasdTick);
      } else {
        panVx = 0; panVy = 0;
        wasdRaf = null;
      }
    };
    const startWasdPan = () => {
      if (wasdRaf == null) wasdRaf = requestAnimationFrame(wasdTick);
    };
    const _stopWasdPan = () => {
      // Don't cancel immediately — let deceleration play out
      // The raf loop self-stops when velocity reaches zero
    }; void _stopWasdPan;
    const killWasdPan = () => {
      if (wasdRaf != null) { cancelAnimationFrame(wasdRaf); wasdRaf = null; }
      panVx = 0; panVy = 0;
    };

    // Z/X zoom — smooth with acceleration/deceleration (same pattern as WASD pan)
    const MAX_ZOOM_SPEED = 0.06; // zoom levels per frame
    const ZOOM_ACCEL = 0.008;
    const ZOOM_DECEL = 0.015;
    let zoomVelocity = 0;
    let zoomRaf: number | null = null;
    let zoomInHeld = false;
    let zoomOutHeld = false;

    const zoomTick = () => {
      const target = zoomInHeld ? 1 : zoomOutHeld ? -1 : 0;

      if (target !== 0) {
        zoomVelocity += target * ZOOM_ACCEL;
        zoomVelocity = Math.max(-MAX_ZOOM_SPEED, Math.min(MAX_ZOOM_SPEED, zoomVelocity));
      } else {
        if (Math.abs(zoomVelocity) < ZOOM_DECEL) zoomVelocity = 0;
        else zoomVelocity -= Math.sign(zoomVelocity) * ZOOM_DECEL;
      }

      if (Math.abs(zoomVelocity) > 0.001) {
        map.setZoom(map.getZoom() + zoomVelocity);
        zoomRaf = requestAnimationFrame(zoomTick);
      } else {
        zoomVelocity = 0;
        zoomRaf = null;
      }
    };
    const startZoom = () => {
      if (zoomRaf == null) zoomRaf = requestAnimationFrame(zoomTick);
    };
    const killZoom = () => {
      if (zoomRaf != null) { cancelAnimationFrame(zoomRaf); zoomRaf = null; }
      zoomVelocity = 0;
    };

    // Space key: switch to grab cursor for panning
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        spaceHeldRef.current = true;
        map.getCanvas().style.cursor = 'grab';
      }
      if (wasdKeys.has(e.code)) {
        activeWasd.add(e.code);
        startWasdPan();
      }
      if (e.code === 'KeyX') { zoomInHeld = true; startZoom(); }
      if (e.code === 'KeyZ') { zoomOutHeld = true; startZoom(); }
      if (e.code === 'KeyC' && !e.repeat) {
        const gs = gameStateRef.current;
        if (gs) map.flyTo({ center: [gs.player.position.lng, gs.player.position.lat], duration: 500 });
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        spaceHeldRef.current = false;
        map.getCanvas().style.cursor = movementModeRef.current ? MOVE_CURSOR : CROSSHAIR_CURSOR;
      }
      if (wasdKeys.has(e.code)) {
        activeWasd.delete(e.code);
        // Keep raf running so deceleration plays out
        if (activeWasd.size === 0) startWasdPan();
      }
      if (e.code === 'KeyX') { zoomInHeld = false; startZoom(); }
      if (e.code === 'KeyZ') { zoomOutHeld = false; startZoom(); }
    };
    // Show grabbing cursor while actively dragging with space held
    const onDragStart = () => {
      if (spaceHeldRef.current) {
        map.getCanvas().style.cursor = 'grabbing';
      }
    };
    const onDragEnd = () => {
      map.getCanvas().style.cursor = spaceHeldRef.current ? 'grab' : (movementModeRef.current ? MOVE_CURSOR : CROSSHAIR_CURSOR);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    map.on('dragstart', onDragStart);
    map.on('dragend', onDragEnd);

    map.on('load', () => {
      addDataLayers(map, cityData, gameState);
      addMovementLayers(map);
      addProximityHover(map, spaceHeldRef, movementModeRef, hideModeRef, isDraggingConeRef, onHover);
      // Signal that all pre-created sources/layers are ready. Device-rendering
      // effects include mapLayersReady in their deps so they re-fire now
      // rather than silently returning when sources didn't exist yet.
      setMapLayersReady((n: number) => n + 1);
    });

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      killWasdPan();
      killZoom();
      map.remove();
      mapRef.current = null;
    };
  }, [cityData]);

  // Movement mode: set up handlers ONCE on enter, tear down on exit.
  // All changing values (gameState, maxRange, callbacks) are read from refs.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Determine if we're entering or leaving movement mode
    const entering = !!movementMode;

    if (!entering) {
      // Leaving movement mode — clear path and restore cursor
      updateMovementPath(map, null);
      if (!spaceHeldRef.current) {
        map.getCanvas().style.cursor = CROSSHAIR_CURSOR;
      }
      currentPathRef.current = null;
      onMovementPreviewRef.current?.(null);
      return;
    }

    // Entering movement mode — set up handlers
    const canvas = map.getCanvas();
    canvas.style.cursor = MOVE_CURSOR;
    // Disable MapLibre's box-zoom (shift+drag) during movement so Shift is free for footpath toggle
    map.boxZoom.disable();

    let lastMoveTime = 0;
    let lastValidPath: PathResult | null = null;

    // Track Shift key for footpath inclusion toggle
    const shiftHeldRef = { current: false };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift' && !shiftHeldRef.current) {
        shiftHeldRef.current = true;
        // Re-compute path with footpaths included
        const cursor = lastCursorLngLatRef.current;
        if (cursor) computePathForCursor(cursor.lng, cursor.lat);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        shiftHeldRef.current = false;
        // Re-compute path with footpaths excluded
        const cursor = lastCursorLngLatRef.current;
        if (cursor) computePathForCursor(cursor.lng, cursor.lat);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    const computePathForCursor = (lng: number, lat: number) => {
      const mode = movementModeRef.current;
      const maxRange = mode?.maxRange;
      if (!maxRange) return;

      const gs = gameStateRef.current;
      if (!gs) return;
      const playerPos = gs.player.position;

      // Shortcut: straight-line through buildings (no pathfinding)
      if (mode?.isShortcut) {
        const path = createStraightLinePath(
          playerPos.lat, playerPos.lng,
          lat, lng,
          maxRange,
          cityData.navigation.nodes,
          cityData.navigation.edges, // used to block shortcuts to the same road
        );
        if (path) {
          lastValidPath = path;
          currentPathRef.current = path;
          updateMovementPath(map, path, maxRange);
        } else {
          // Over range or same-road — show line to cursor in red
          const overPath = createStraightLinePath(
            playerPos.lat, playerPos.lng,
            lat, lng,
            maxRange * 10,
            cityData.navigation.nodes,
          );
          if (overPath) {
            currentPathRef.current = lastValidPath;
            updateMovementPath(map, lastValidPath, maxRange, maxRange);
          }
        }
        return;
      }

      // Filter out footpaths unless Shift is held or in a vehicle (vehicles already exclude footpaths)
      // Exception: if the player is currently on a footpath, include all footpaths so they can move freely
      const shiftHeld = shiftHeldRef.current;
      const playerNodeId = gs.player.nearestNodeId;
      // Player is "on a footpath" only if ALL edges at their node are footpaths
      // (i.e., they're in the middle of a park path, not at a road intersection that connects to one)
      const edgesAtNode = cityData.navigation.edges.filter(
        e => e.from === playerNodeId || e.to === playerNodeId
      );
      const playerOnFootpath = edgesAtNode.length > 0 && edgesAtNode.every(e => e.classification === 'footpath');
      const navEdges = mode?.isVehicle
        ? cityData.navigation.edges.filter(e => e.classification !== 'footpath')
        : (shiftHeld || playerOnFootpath)
          ? cityData.navigation.edges
          : cityData.navigation.edges.filter(e => e.classification !== 'footpath');

      const path = findPathBetweenPoints(
        playerPos.lat, playerPos.lng,
        lat, lng,
        cityData.navigation.nodes,
        navEdges,
        maxRange,
      );

      if (path) {
        lastValidPath = path;
        currentPathRef.current = path;
        updateMovementPath(map, path, maxRange);
        // Compute signal preview for the movement banner using the same
        // full-pipeline helper as handleMovementComplete, so the number
        // the banner shows matches what the player actually emits on commit.
        if (onMovementPreviewRef.current && path.edges.length > 0) {
          const exposure = gs.player.exposure;
          // Full exposure pipeline — matches handleMovementComplete exactly
          // so the hover preview number reflects every active modifier
          // (disguise, low profile, vehicle major-road doubling, diminishing).
          const totalExpGain = computeMovementExposureGain({
            edges: path.edges,
            currentExposure: exposure,
            isInVehicle: gs.player.isInVehicle,
            disguiseActive: gs.player.disguiseTurnsLeft > 0,
            lowProfileActive: gs.player.lowProfileActive,
          });
          const postMoveExposure = Math.min(tuning.exposureMax, exposure + totalExpGain);

          // Compute the passive multiplier for this path if we have runState.
          // Falls back to 1 (no effect) if runState isn't plumbed through.
          const rs = runStateRef.current;
          const passiveMultiplier = rs
            ? computePassiveSignalMultiplier(
                {
                  edges: path.edges,
                  isInVehicle: gs.player.isInVehicle,
                  isFirstSignalThisTurn: gs.player.signalsGeneratedThisTurn === 0,
                  origin: 'movement',
                },
                rs,
              )
            : 1;

          // Determine the zone at the end of the path for Fade's residential check.
          const endCoord = path.coordinates[path.coordinates.length - 1];
          const endZone = endCoord
            ? zoneAtPoint(endCoord[0], endCoord[1], zoneLookup)
            : null;

          const { finalStrength } = computeMovementSignalStrength({
            edges: path.edges,
            cardSignalModifier: mode?.card.signalModifier ?? 1,
            activeBuffs: gs.player.activeBuffs,
            isInVehicle: gs.player.isInVehicle,
            ghostActive: gs.player.ghostActive,
            coldExitTurnsLeft: gs.player.coldExitTurnsLeft,
            jammerTurnsLeft: gs.player.jammerTurnsLeft,
            jammerPosition: gs.player.jammerPosition,
            jammerRadius: gs.player.jammerRadius,
            disguiseTurnsLeft: gs.player.disguiseTurnsLeft,
            endPos: { lat, lng },
            endZone,
            exposure: postMoveExposure,
            passiveMultiplier,
          });

          // Check if cursor is near a footpath (within ~150m)
          const nearFootpath = !shiftHeld && !playerOnFootpath && cityData.navigation.edges.some(e => {
            if (e.classification !== 'footpath' || !e.geometry || e.geometry.length === 0) return false;
            const [eLng, eLat] = e.geometry[0];
            const dLat = (eLat - lat) * 111000;
            const dLng = (eLng - lng) * 111000 * Math.cos(lat * Math.PI / 180);
            return Math.sqrt(dLat * dLat + dLng * dLng) < 150;
          });
          onMovementPreviewRef.current({
            distance: path.totalDistance,
            signalStrength: finalStrength,
            exposureGain: Math.round(totalExpGain),
            nearFootpath,
          });
        }
      } else {
        // Over range — try with much larger budget to show red distance
        const overPath = findPathBetweenPoints(
          playerPos.lat, playerPos.lng,
          lat, lng,
          cityData.navigation.nodes,
          navEdges,
          maxRange * 10,
        );
        if (overPath) {
          currentPathRef.current = lastValidPath;
          updateMovementPath(map, lastValidPath, maxRange, maxRange);
        } else {
          currentPathRef.current = lastValidPath;
          updateMovementPath(map, lastValidPath, maxRange);
        }
      }
    };

    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      if (isAnimatingRef.current || !movementModeRef.current) return;
      // Skip normal hover preview when alt movement mode is active
      if (altMovementModeRef.current && !movementModeRef.current?.isShortcut) return;

      const now = performance.now();
      if (now - lastMoveTime < 66) return;
      lastMoveTime = now;

      const { lng, lat } = e.lngLat;
      lastCursorLngLatRef.current = { lng, lat };
      computePathForCursor(lng, lat);
    };

    const onClick = (e: maplibregl.MapMouseEvent) => {
      if (isAnimatingRef.current || !movementModeRef.current) return;
      // Skip normal click when alt movement mode is active (alt mode handles its own click)
      if (altMovementModeRef.current && !movementModeRef.current?.isShortcut) return;

      const path = currentPathRef.current;
      if (path && path.coordinates.length >= 2) {
        // cmd/ctrl+click = partial move, plain click = full move
        if (e.originalEvent.metaKey || e.originalEvent.ctrlKey) {
          animatePlayerMovement(map, path, isAnimatingRef, () => {
            // After animation, reset lastValidPath so the next path starts fresh
            lastValidPath = null;
            currentPathRef.current = null;
            onMovementPartialRef.current?.(path);
          });
        } else {
          animatePlayerMovement(map, path, isAnimatingRef, () => {
            onMovementCompleteRef.current?.(path);
          });
        }
      }
    };

    const suppressContextMenu = (e: MouseEvent) => {
      if (movementModeRef.current) {
        e.preventDefault();
      }
    };
    canvas.addEventListener('contextmenu', suppressContextMenu);

    map.on('mousemove', onMouseMove);
    map.on('click', onClick);

    return () => {
      map.off('mousemove', onMouseMove);
      map.off('click', onClick);
      canvas.removeEventListener('contextmenu', suppressContextMenu);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      map.boxZoom.enable();
    };
    // Only depends on whether we're in movement mode at all — NOT on gameState/maxRange/callbacks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!movementMode, cityData]);

  // --- Alt Movement Mode ---
  // When altMovementMode is true and a Move card is selected (not shortcut),
  // clicking the map generates 3-5 diverse route options. Hovering over a route
  // highlights it, and clicking a highlighted route executes the movement.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const active = altMovementMode && !!movementMode && !movementMode.isShortcut;
    if (!active) {
      // Clear any existing alt routes when mode deactivates
      clearAltRoutes(map);
      altRoutesRef.current = [];
      altHoveredIndexRef.current = -1;
      return;
    }

    // Helper: get filtered nav edges (same logic as normal movement)
    function getFilteredEdges(): NavEdge[] {
      const mode = movementModeRef.current;
      const gs = gameStateRef.current;
      if (!mode || !gs) return cityData.navigation.edges;

      // Alt movement mode: always include footpaths (no shift needed)
      return mode.isVehicle
        ? cityData.navigation.edges.filter(e => e.classification !== 'footpath')
        : cityData.navigation.edges;
    }

    // Helper: truncate a PathResult to maxRange meters
    function truncatePathToRange(path: PathResult, maxRange: number): PathResult {
      if (path.totalDistance <= maxRange) return path;

      let accumulated = 0;
      const truncatedCoords: [number, number][] = [path.coordinates[0]];
      const truncatedEdges: NavEdge[] = [];

      for (let i = 1; i < path.coordinates.length; i++) {
        const dx = (path.coordinates[i][0] - path.coordinates[i - 1][0]) * 111000 * Math.cos(path.coordinates[i][1] * Math.PI / 180);
        const dy = (path.coordinates[i][1] - path.coordinates[i - 1][1]) * 111000;
        const segLen = Math.sqrt(dx * dx + dy * dy);

        if (accumulated + segLen >= maxRange) {
          // Interpolate to the exact max range point
          const remaining = maxRange - accumulated;
          const t = segLen > 0 ? remaining / segLen : 0;
          const interpLng = path.coordinates[i - 1][0] + (path.coordinates[i][0] - path.coordinates[i - 1][0]) * t;
          const interpLat = path.coordinates[i - 1][1] + (path.coordinates[i][1] - path.coordinates[i - 1][1]) * t;
          truncatedCoords.push([interpLng, interpLat]);
          break;
        }

        accumulated += segLen;
        truncatedCoords.push(path.coordinates[i]);
      }

      // Include edges that fit within the truncated distance
      let edgeDist = 0;
      for (const edge of path.edges) {
        if (edgeDist + edge.length > maxRange) break;
        truncatedEdges.push(edge);
        edgeDist += edge.length;
      }

      return {
        nodes: path.nodes,
        edges: truncatedEdges,
        coordinates: truncatedCoords,
        totalDistance: Math.min(path.totalDistance, maxRange),
      };
    }

    // Helper: generate diverse routes from player to target
    function generateAltRoutes(playerLat: number, playerLng: number, targetLat: number, targetLng: number, maxRange: number): PathResult[] {
      const allEdges = getFilteredEdges();
      const nodes = cityData.navigation.nodes;
      const routes: PathResult[] = [];

      // Iterative shortest path with edge removal:
      // 1. Find shortest path from player to target
      // 2. Remove middle edges from that path
      // 3. Find next shortest path — forced to take different roads
      // 4. Repeat
      // All paths use findPathBetweenPoints which handles snapping to exact player/target positions
      let availableEdges = allEdges;

      for (let attempt = 0; attempt < 8; attempt++) {
        if (routes.length >= 6) break;

        const path = findPathBetweenPoints(
          playerLat, playerLng,
          targetLat, targetLng,
          nodes, availableEdges,
          maxRange * 3,
        );
        if (!path || path.coordinates.length < 2) break;

        const truncated = truncatePathToRange(path, maxRange);
        if (truncated.coordinates.length < 2) break;

        // Check this route isn't a near-duplicate of an existing one (>70% shared edges)
        const routeEdgeIds = new Set(truncated.edges.map(e => e.id));
        let isDup = false;
        for (const existing of routes) {
          const existingEdgeIds = new Set(existing.edges.map(e => e.id));
          let shared = 0;
          for (const id of routeEdgeIds) {
            if (existingEdgeIds.has(id)) shared++;
          }
          const overlap = routeEdgeIds.size > 0 ? shared / routeEdgeIds.size : 0;
          if (overlap > 0.7) { isDup = true; break; }
        }

        if (!isDup) {
          routes.push(truncated);
        }

        // Remove the middle portion of edges from the FULL path to force divergence
        const edgesToRemove = new Set<string>();
        const pathEdges = path.edges;
        // Keep first ~20% and last ~20% of edges so endpoints are still reachable
        const keepStart = Math.max(1, Math.floor(pathEdges.length * 0.2));
        const keepEnd = Math.max(1, Math.floor(pathEdges.length * 0.2));
        for (let i = keepStart; i < pathEdges.length - keepEnd; i++) {
          edgesToRemove.add(pathEdges[i].id);
        }
        // For very short paths, remove at least one middle edge
        if (edgesToRemove.size === 0 && pathEdges.length >= 3) {
          edgesToRemove.add(pathEdges[Math.floor(pathEdges.length / 2)].id);
        }
        if (edgesToRemove.size === 0) break; // can't diversify further

        availableEdges = availableEdges.filter(e => !edgesToRemove.has(e.id));
      }

      return routes;
    }

    // Helper: compute signal + exposure preview for a single alt-route.
    // Uses the shared computeMovementExposureGain + computeMovementSignalStrength
    // helpers so alt-route tooltips show the SAME numbers the player would
    // actually commit — Disguise, Low Profile, Vehicle, passives, all of it.
    function computeRoutePreview(path: PathResult) {
      const mode = movementModeRef.current;
      const gs = gameStateRef.current;
      if (!mode || !gs || !onMovementPreviewRef.current || path.edges.length === 0) return;

      const exposure = gs.player.exposure;
      const totalExpGain = computeMovementExposureGain({
        edges: path.edges,
        currentExposure: exposure,
        isInVehicle: gs.player.isInVehicle,
        disguiseActive: gs.player.disguiseTurnsLeft > 0,
        lowProfileActive: gs.player.lowProfileActive,
      });
      const postMoveExposure = Math.min(tuning.exposureMax, exposure + totalExpGain);

      const rs = runStateRef.current;
      const passiveMultiplier = rs
        ? computePassiveSignalMultiplier(
            {
              edges: path.edges,
              isInVehicle: gs.player.isInVehicle,
              isFirstSignalThisTurn: gs.player.signalsGeneratedThisTurn === 0,
              origin: 'movement',
            },
            rs,
          )
        : 1;

      // End-of-route zone for Fade's residential check
      const endCoord = path.coordinates[path.coordinates.length - 1];
      const endZone = endCoord ? zoneAtPoint(endCoord[0], endCoord[1], zoneLookup) : null;
      const endPos = endCoord
        ? { lat: endCoord[1], lng: endCoord[0] }
        : gs.player.position;

      const { finalStrength } = computeMovementSignalStrength({
        edges: path.edges,
        cardSignalModifier: mode.card.signalModifier ?? 1,
        activeBuffs: gs.player.activeBuffs,
        isInVehicle: gs.player.isInVehicle,
        ghostActive: gs.player.ghostActive,
        coldExitTurnsLeft: gs.player.coldExitTurnsLeft,
        jammerTurnsLeft: gs.player.jammerTurnsLeft,
        jammerPosition: gs.player.jammerPosition,
        jammerRadius: gs.player.jammerRadius,
        disguiseTurnsLeft: gs.player.disguiseTurnsLeft,
        endPos,
        endZone,
        exposure: postMoveExposure,
        passiveMultiplier,
      });

      onMovementPreviewRef.current({
        distance: path.totalDistance,
        signalStrength: finalStrength,
        exposureGain: Math.round(totalExpGain),
        nearFootpath: false,
      });
    }

    // Helper: update map display of alt routes
    function displayAltRoutes(routes: PathResult[], hoveredIndex: number) {
      if (!map) return;
      const altSrc = map.getSource('alt-routes') as maplibregl.GeoJSONSource;
      const hoverSrc = map.getSource('alt-routes-hover') as maplibregl.GeoJSONSource;
      const endpointSrc = map.getSource('alt-routes-endpoints') as maplibregl.GeoJSONSource;
      if (!altSrc || !hoverSrc || !endpointSrc) return;

      // All routes as features
      const features = routes.map((r, i) => ({
        type: 'Feature' as const,
        properties: { index: i },
        geometry: {
          type: 'LineString' as const,
          coordinates: r.coordinates,
        },
      }));
      altSrc.setData({ type: 'FeatureCollection', features });

      // Endpoint markers
      const endpointFeatures = routes.map((r, i) => ({
        type: 'Feature' as const,
        properties: { index: i },
        geometry: {
          type: 'Point' as const,
          coordinates: r.coordinates[r.coordinates.length - 1],
        },
      }));
      endpointSrc.setData({ type: 'FeatureCollection', features: endpointFeatures });

      // Hovered route
      if (hoveredIndex >= 0 && hoveredIndex < routes.length) {
        hoverSrc.setData({
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: routes[hoveredIndex].coordinates,
            },
          }],
        });
      } else {
        hoverSrc.setData({ type: 'FeatureCollection', features: [] });
      }
    }

    // Track Shift key for footpath toggle in alt mode
    const altShiftHeldRef = { current: false };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') altShiftHeldRef.current = true;
      if (e.key === 'Escape') {
        // Clear alt routes on ESC
        clearAltRoutes(map);
        altRoutesRef.current = [];
        altHoveredIndexRef.current = -1;
        onMovementPreviewRef.current?.(null);
        onAltRoutesCountRef.current?.(0);
        const snapSrc = map.getSource('alt-snap-cursor') as maplibregl.GeoJSONSource | undefined;
        if (snapSrc) snapSrc.setData({ type: 'FeatureCollection', features: [] });
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') altShiftHeldRef.current = false;
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    // --- Mouse move: snap cursor + find which alt route is closest to cursor ---
    let lastMoveTime = 0;
    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      if (isAnimatingRef.current || !movementModeRef.current) return;

      // Show snap cursor on nearest road node when no routes displayed yet
      const snapSrc = map.getSource('alt-snap-cursor') as maplibregl.GeoJSONSource | undefined;
      if (altRoutesRef.current.length === 0) {
        if (snapSrc) {
          // Snap to nearest point on any road edge (not just nodes)
          const curLng = e.lngLat.lng, curLat = e.lngLat.lat;
          let bestLng = curLng, bestLat = curLat, bestDist = Infinity;
          for (const edge of cityData.navigation.edges) {
            if (!edge.geometry || edge.geometry.length < 2) continue;
            for (let i = 0; i < edge.geometry.length - 1; i++) {
              const [ax, ay] = edge.geometry[i];
              const [bx, by] = edge.geometry[i + 1];
              const dx = bx - ax, dy = by - ay;
              const lenSq = dx * dx + dy * dy;
              if (lenSq === 0) continue;
              let t = ((curLng - ax) * dx + (curLat - ay) * dy) / lenSq;
              t = Math.max(0, Math.min(1, t));
              const px = ax + t * dx, py = ay + t * dy;
              const d = (curLng - px) ** 2 + (curLat - py) ** 2;
              if (d < bestDist) { bestDist = d; bestLng = px; bestLat = py; }
            }
          }
          snapSrc.setData({
            type: 'FeatureCollection',
            features: [{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [bestLng, bestLat] } }],
          });
        }
        return;
      }
      // Hide snap cursor once routes are shown
      if (snapSrc) snapSrc.setData({ type: 'FeatureCollection', features: [] });

      const now = performance.now();
      if (now - lastMoveTime < 50) return;
      lastMoveTime = now;

      const { lng, lat } = e.lngLat;
      lastCursorLngLatRef.current = { lng, lat };

      // Find closest route to cursor
      const routes = altRoutesRef.current;
      let closestIdx = -1;
      let closestDist = Infinity;

      for (let ri = 0; ri < routes.length; ri++) {
        const coords = routes[ri].coordinates;
        for (let ci = 0; ci < coords.length; ci++) {
          const dx = (coords[ci][0] - lng) * 111000 * Math.cos(lat * Math.PI / 180);
          const dy = (coords[ci][1] - lat) * 111000;
          const d = dx * dx + dy * dy;
          if (d < closestDist) {
            closestDist = d;
            closestIdx = ri;
          }
        }
      }

      // Only hover if within ~30m of a route
      const maxHoverDist = 30;
      if (Math.sqrt(closestDist) > maxHoverDist) {
        closestIdx = -1;
      }

      if (closestIdx !== altHoveredIndexRef.current) {
        altHoveredIndexRef.current = closestIdx;
        displayAltRoutes(routes, closestIdx);

        // Fire signal preview for hovered route
        if (closestIdx >= 0 && closestIdx < routes.length) {
          computeRoutePreview(routes[closestIdx]);
          map.getCanvasContainer().style.cursor = 'pointer';
        } else {
          onMovementPreviewRef.current?.(null);
          map.getCanvasContainer().style.cursor = MOVE_CURSOR;
        }
      }
    };

    // --- Click handler ---
    const onClick = (e: maplibregl.MapMouseEvent) => {
      if (isAnimatingRef.current || !movementModeRef.current) return;
      if (movementModeRef.current.isShortcut) return; // shortcut mode uses normal handler

      const hoveredIdx = altHoveredIndexRef.current;
      const routes = altRoutesRef.current;

      // If hovering a route, select it
      if (hoveredIdx >= 0 && hoveredIdx < routes.length) {
        const chosenPath = routes[hoveredIdx];
        if (chosenPath.coordinates.length >= 2) {
          // Clear alt route display
          clearAltRoutes(map);
          altRoutesRef.current = [];
          altHoveredIndexRef.current = -1;
          onAltRoutesCountRef.current?.(0);

          // Animate and complete movement
          animatePlayerMovement(map, chosenPath, isAnimatingRef, () => {
            onMovementCompleteRef.current?.(chosenPath);
          });
        }
        return;
      }

      // No route hovered — generate new routes to clicked point
      const { lng, lat } = e.lngLat;
      const gs = gameStateRef.current;
      const mode = movementModeRef.current;
      if (!gs || !mode) return;

      const playerPos = gs.player.position;
      const maxRange = mode.maxRange;

      const newRoutes = generateAltRoutes(playerPos.lat, playerPos.lng, lat, lng, maxRange);
      if (newRoutes.length === 0) return;

      altRoutesRef.current = newRoutes;
      altHoveredIndexRef.current = -1;
      onAltRoutesCountRef.current?.(newRoutes.length);

      // Also hide the normal movement path preview
      updateMovementPath(map, null);

      displayAltRoutes(newRoutes, -1);
    };

    map.on('mousemove', onMouseMove);
    map.on('click', onClick);

    return () => {
      map.off('mousemove', onMouseMove);
      map.off('click', onClick);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      clearAltRoutes(map);
      altRoutesRef.current = [];
      altHoveredIndexRef.current = -1;
      onAltRoutesCountRef.current?.(0);
      // Clear snap cursor
      const snapSrc = map.getSource('alt-snap-cursor') as maplibregl.GeoJSONSource | undefined;
      if (snapSrc) snapSrc.setData({ type: 'FeatureCollection', features: [] });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [altMovementMode, !!movementMode, cityData]);

  // --- Sweep mode: yellow circle preview following cursor, click to place ---
  const sweepModeRef = useRef<SweepMode | null>(null);
  sweepModeRef.current = sweepMode ?? null;
  const onSweepPlaceRef = useRef(onSweepPlace);
  onSweepPlaceRef.current = onSweepPlace;

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !sweepMode) return;

    // Add sweep preview source/layer
    const SWEEP_SOURCE = 'sweep-preview';
    const SWEEP_FILL = 'sweep-preview-fill';
    const SWEEP_OUTLINE = 'sweep-preview-outline';
    const SWEEP_LABEL = 'sweep-preview-label';

    const setupSweep = () => {
      if (!map.getSource(SWEEP_SOURCE)) {
        map.addSource(SWEEP_SOURCE, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
      }
      if (!map.getLayer(SWEEP_FILL)) {
        map.addLayer({
          id: SWEEP_FILL,
          type: 'fill',
          source: SWEEP_SOURCE,
          paint: {
            'fill-color': '#aa88ff',
            'fill-opacity': 0.1,
          },
        });
      }
      if (!map.getLayer(SWEEP_OUTLINE)) {
        map.addLayer({
          id: SWEEP_OUTLINE,
          type: 'line',
          source: SWEEP_SOURCE,
          paint: {
            'line-color': '#aa88ff',
            'line-width': 2,
            'line-opacity': 0.6,
            'line-dasharray': [4, 3],
          },
        });
      }

      // Set cursor
      const canvas = map.getCanvasContainer();
      canvas.style.cursor = MOVE_CURSOR.replace(/%2344aaff/g, '%23aa88ff'); // purple crosshair
    };

    let lastPreviewLng = 0;
    let lastPreviewLat = 0;
    const PREVIEW_THRESHOLD = 0.00005; // ~5m — skip update if cursor hasn't moved significantly

    const updatePreview = (lng: number, lat: number) => {
      // Skip if cursor hasn't moved enough
      if (Math.abs(lng - lastPreviewLng) < PREVIEW_THRESHOLD && Math.abs(lat - lastPreviewLat) < PREVIEW_THRESHOLD) return;
      lastPreviewLng = lng;
      lastPreviewLat = lat;

      const source = map.getSource(SWEEP_SOURCE) as maplibregl.GeoJSONSource;
      if (!source) return;

      const circleCoords = createCirclePolygon(lng, lat, sweepModeRef.current?.radius ?? 1500);
      source.setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [circleCoords],
          },
        }],
      });
    };

    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      updatePreview(e.lngLat.lng, e.lngLat.lat);
    };

    const onClick = (e: maplibregl.MapMouseEvent) => {
      if (onSweepPlaceRef.current) {
        onSweepPlaceRef.current(e.lngLat.lat, e.lngLat.lng);
      }
    };

    if (map.isStyleLoaded()) {
      setupSweep();
    } else {
      map.once('styledata', setupSweep);
    }

    map.on('mousemove', onMouseMove);
    map.on('click', onClick);

    return () => {
      map.off('mousemove', onMouseMove);
      map.off('click', onClick);

      // Clean up layers/source
      if (map.getLayer(SWEEP_LABEL)) map.removeLayer(SWEEP_LABEL);
      if (map.getLayer(SWEEP_OUTLINE)) map.removeLayer(SWEEP_OUTLINE);
      if (map.getLayer(SWEEP_FILL)) map.removeLayer(SWEEP_FILL);
      if (map.getSource(SWEEP_SOURCE)) map.removeSource(SWEEP_SOURCE);

      // Reset cursor if not in movement mode
      if (!movementModeRef.current) {
        const canvas = map.getCanvasContainer();
        canvas.style.cursor = CROSSHAIR_CURSOR;
      }
    };
  }, [!!sweepMode]);

  // --- Decoy mode: click anywhere to place a fake signal ---
  const decoyModeRef = useRef(false);
  decoyModeRef.current = !!decoyMode;
  const onDecoyPlaceRef = useRef(onDecoyPlace);
  onDecoyPlaceRef.current = onDecoyPlace;

  const decoyRadiusRef = useRef(decoyRadius);
  decoyRadiusRef.current = decoyRadius;

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !decoyMode) return;

    // Orange crosshair cursor for decoy placement
    const canvas = map.getCanvasContainer();
    canvas.style.cursor = MOVE_CURSOR.replace(/%2344aaff/g, '%23ffaa44');

    // Create preview circle source/layers
    const previewSourceId = 'decoy-preview';
    if (!map.getSource(previewSourceId)) {
      map.addSource(previewSourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'decoy-preview-fill',
        type: 'fill',
        source: previewSourceId,
        paint: {
          'fill-color': 'rgba(255, 170, 68, 0.1)',
        },
      });
      map.addLayer({
        id: 'decoy-preview-line',
        type: 'line',
        source: previewSourceId,
        paint: {
          'line-color': '#ffaa44',
          'line-width': 1.5,
          'line-dasharray': [4, 4],
          'line-opacity': 0.6,
        },
      });
    }

    // Ensure decoy preview renders above all other map elements
    try {
      map.moveLayer('decoy-preview-fill');
      map.moveLayer('decoy-preview-line');
    } catch { /* layers might not exist yet */ }

    // Generate a circle polygon for the preview (reuse shared helper)
    const makeCircle = (lng: number, lat: number, radiusM: number): GeoJSON.Feature => {
      const coords = createCirclePolygon(lng, lat, radiusM);
      return {
        type: 'Feature',
        properties: {},
        geometry: { type: 'Polygon', coordinates: [coords] },
      };
    };

    let lastDecoyLng = 0;
    let lastDecoyLat = 0;
    const DECOY_THRESHOLD = 0.00005; // ~5m — skip update if cursor hasn't moved

    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      const { lng, lat } = e.lngLat;
      if (Math.abs(lng - lastDecoyLng) < DECOY_THRESHOLD && Math.abs(lat - lastDecoyLat) < DECOY_THRESHOLD) return;
      lastDecoyLng = lng;
      lastDecoyLat = lat;

      const src = map.getSource(previewSourceId) as maplibregl.GeoJSONSource;
      if (src) {
        src.setData({
          type: 'FeatureCollection',
          features: [makeCircle(lng, lat, decoyRadiusRef.current)],
        });
      }
    };

    const onClick = (e: maplibregl.MapMouseEvent) => {
      if (onDecoyPlaceRef.current) {
        onDecoyPlaceRef.current(e.lngLat.lat, e.lngLat.lng);
      }
    };

    map.on('mousemove', onMouseMove);
    map.on('click', onClick);

    return () => {
      map.off('mousemove', onMouseMove);
      map.off('click', onClick);
      // Clear the preview
      const src = map.getSource(previewSourceId) as maplibregl.GeoJSONSource;
      if (src) src.setData({ type: 'FeatureCollection', features: [] });
      if (!movementModeRef.current && !sweepModeRef.current) {
        canvas.style.cursor = CROSSHAIR_CURSOR;
      }
    };
  }, [!!decoyMode]);

  // --- Burner Phone placement ---
  // Mirrors the Decoy placement pattern but with a fixed radius (no size
  // selection modal) and a yellow-ish tint so the player can tell it
  // apart from a real Decoy drop at a glance. Committed burner signals
  // still appear in the normal signals layer, not here — this is just
  // the hover preview.
  const onBurnerPhonePlaceRef = useRef(onBurnerPhonePlace);
  onBurnerPhonePlaceRef.current = onBurnerPhonePlace;
  const burnerPhoneRadiusRef = useRef(burnerPhoneRadius);
  burnerPhoneRadiusRef.current = burnerPhoneRadius;

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !burnerPhoneMode) return;
    const canvas = map.getCanvasContainer();
    // Slightly more yellow than Decoy's orange so the two placement modes
    // read as different actions.
    canvas.style.cursor = MOVE_CURSOR.replace(/%2344aaff/g, '%23ffdd44');

    const previewSourceId = 'burner-phone-preview';
    if (!map.getSource(previewSourceId)) {
      map.addSource(previewSourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'burner-phone-preview-fill',
        type: 'fill',
        source: previewSourceId,
        paint: { 'fill-color': 'rgba(255, 221, 68, 0.1)' },
      });
      map.addLayer({
        id: 'burner-phone-preview-line',
        type: 'line',
        source: previewSourceId,
        paint: {
          'line-color': '#ffdd44',
          'line-width': 1.5,
          'line-dasharray': [4, 4],
          'line-opacity': 0.65,
        },
      });
    }
    try {
      map.moveLayer('burner-phone-preview-fill');
      map.moveLayer('burner-phone-preview-line');
    } catch { /* layers might not exist yet */ }

    let lastLng = 0;
    let lastLat = 0;
    const THRESHOLD = 0.00005; // ~5m

    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      const { lng, lat } = e.lngLat;
      if (Math.abs(lng - lastLng) < THRESHOLD && Math.abs(lat - lastLat) < THRESHOLD) return;
      lastLng = lng;
      lastLat = lat;
      const src = map.getSource(previewSourceId) as maplibregl.GeoJSONSource;
      if (src) {
        src.setData({
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            properties: {},
            geometry: { type: 'Polygon', coordinates: [createCirclePolygon(lng, lat, burnerPhoneRadiusRef.current)] },
          }],
        });
      }
    };
    const onClick = (e: maplibregl.MapMouseEvent) => {
      if (onBurnerPhonePlaceRef.current) {
        onBurnerPhonePlaceRef.current(e.lngLat.lat, e.lngLat.lng);
      }
    };
    map.on('mousemove', onMouseMove);
    map.on('click', onClick);

    return () => {
      map.off('mousemove', onMouseMove);
      map.off('click', onClick);
      const src = map.getSource(previewSourceId) as maplibregl.GeoJSONSource;
      if (src) src.setData({ type: 'FeatureCollection', features: [] });
      canvas.style.cursor = CROSSHAIR_CURSOR;
    };
  }, [!!burnerPhoneMode]);

  // --- Jammer placement ---
  // Same pattern as Burner Phone / Decoy but with a distinct
  // gray/cyan color so the player can tell at a glance this is a
  // suppression zone (eats signals) rather than a decoy emitter
  // (creates signals). Committed zone still renders via the existing
  // jammer zone layer — this is just the hover preview.
  const onJammerPlaceRef = useRef(onJammerPlace);
  onJammerPlaceRef.current = onJammerPlace;
  const jammerRadiusRef = useRef(jammerRadius);
  jammerRadiusRef.current = jammerRadius;

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !jammerMode) return;
    const canvas = map.getCanvasContainer();
    canvas.style.cursor = MOVE_CURSOR.replace(/%2344aaff/g, '%2388aacc');

    const previewSourceId = 'jammer-preview';
    if (!map.getSource(previewSourceId)) {
      map.addSource(previewSourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'jammer-preview-fill',
        type: 'fill',
        source: previewSourceId,
        paint: { 'fill-color': 'rgba(136, 170, 204, 0.12)' },
      });
      map.addLayer({
        id: 'jammer-preview-line',
        type: 'line',
        source: previewSourceId,
        paint: {
          'line-color': '#88aacc',
          'line-width': 1.5,
          'line-dasharray': [2, 2],
          'line-opacity': 0.75,
        },
      });
    }
    try {
      map.moveLayer('jammer-preview-fill');
      map.moveLayer('jammer-preview-line');
    } catch { /* layers might not exist yet */ }

    let lastLng = 0;
    let lastLat = 0;
    const THRESHOLD = 0.00005;

    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      const { lng, lat } = e.lngLat;
      if (Math.abs(lng - lastLng) < THRESHOLD && Math.abs(lat - lastLat) < THRESHOLD) return;
      lastLng = lng;
      lastLat = lat;
      const src = map.getSource(previewSourceId) as maplibregl.GeoJSONSource;
      if (src) {
        src.setData({
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            properties: {},
            geometry: { type: 'Polygon', coordinates: [createCirclePolygon(lng, lat, jammerRadiusRef.current)] },
          }],
        });
      }
    };
    const onClick = (e: maplibregl.MapMouseEvent) => {
      if (onJammerPlaceRef.current) {
        onJammerPlaceRef.current(e.lngLat.lat, e.lngLat.lng);
      }
    };
    map.on('mousemove', onMouseMove);
    map.on('click', onClick);

    return () => {
      map.off('mousemove', onMouseMove);
      map.off('click', onClick);
      const src = map.getSource(previewSourceId) as maplibregl.GeoJSONSource;
      if (src) src.setData({ type: 'FeatureCollection', features: [] });
      canvas.style.cursor = CROSSHAIR_CURSOR;
    };
  }, [!!jammerMode]);

  // --- Misdirect mode: click map to place fake movement path ---
  const onMisdirectPlaceRef = useRef(onMisdirectPlace);
  onMisdirectPlaceRef.current = onMisdirectPlace;
  const onMisdirectPreviewRef = useRef(onMisdirectPreview);
  onMisdirectPreviewRef.current = onMisdirectPreview;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !misdirectMode) return;
    const canvas = map.getCanvasContainer();
    canvas.style.cursor = MOVE_CURSOR;

    // Live path preview as the user moves the cursor (yellow for misdirect)
    const MISDIRECT_PREVIEW_SOURCE = 'misdirect-preview-path';
    const MISDIRECT_PREVIEW_LAYER = 'misdirect-preview-line';
    if (!map.getSource(MISDIRECT_PREVIEW_SOURCE)) {
      map.addSource(MISDIRECT_PREVIEW_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }
    if (!map.getLayer(MISDIRECT_PREVIEW_LAYER)) {
      map.addLayer({
        id: MISDIRECT_PREVIEW_LAYER,
        type: 'line',
        source: MISDIRECT_PREVIEW_SOURCE,
        paint: {
          'line-color': '#ffdd44',
          'line-width': 3,
          'line-dasharray': [2, 3],
          'line-opacity': 0.8,
        },
      });
    }

    const MISDIRECT_RANGE = 300;
    let misdirectConfirmed = false;

    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      if (!gameState) return;
      const playerPos = gameState.player.position;
      // Try within range first
      let path = findPathBetweenPoints(
        playerPos.lat, playerPos.lng,
        e.lngLat.lat, e.lngLat.lng,
        cityData.navigation.nodes,
        cityData.navigation.edges,
        MISDIRECT_RANGE,
      );
      let overRange = false;
      if (!path) {
        // Over range — show path but truncated to max range
        path = findPathBetweenPoints(
          playerPos.lat, playerPos.lng,
          e.lngLat.lat, e.lngLat.lng,
          cityData.navigation.nodes,
          cityData.navigation.edges,
          MISDIRECT_RANGE * 5,
        );
        overRange = true;
      }
      if (path && path.coordinates.length >= 2) {
        const src = map.getSource(MISDIRECT_PREVIEW_SOURCE) as maplibregl.GeoJSONSource;
        if (src) {
          src.setData({
            type: 'FeatureCollection',
            features: [{
              type: 'Feature',
              properties: {},
              geometry: { type: 'LineString', coordinates: path.coordinates },
            }],
          });
          // Color: yellow if in range, red tint if over
          if (map.getLayer(MISDIRECT_PREVIEW_LAYER)) {
            map.setPaintProperty(MISDIRECT_PREVIEW_LAYER, 'line-color', overRange ? '#ff6644' : '#ffdd44');
          }
        }
        // Report live distance for the UI readout. When overRange, the
        // truncated path the agent will actually see is capped at the range,
        // so report the truncated length rather than the extended preview.
        const reportedDist = overRange ? MISDIRECT_RANGE : path.totalDistance;
        onMisdirectPreviewRef.current?.({ distance: reportedDist, overRange });
      } else {
        onMisdirectPreviewRef.current?.(null);
      }
    };

    const onClick = (e: maplibregl.MapMouseEvent) => {
      misdirectConfirmed = true;
      if (onMisdirectPlaceRef.current) onMisdirectPlaceRef.current(e.lngLat.lat, e.lngLat.lng);
    };
    map.on('mousemove', onMouseMove);
    map.on('click', onClick);
    return () => {
      map.off('mousemove', onMouseMove);
      map.off('click', onClick);
      // ALWAYS clear the preview on exit. Previously the cleanup kept the
      // preview visible on commit to avoid a flicker, but the persistent
      // yellow misdirect-path line (rendered from gameState.player.misdirectPath)
      // duplicates it — and worse, when the click was over-range the preview
      // showed a longer path than the truncated persistent line, so both
      // showed overlapped (red past the yellow endpoint). Clearing the
      // preview unconditionally lets the persistent layer be the only line
      // the player sees post-commit.
      const src = map.getSource(MISDIRECT_PREVIEW_SOURCE) as maplibregl.GeoJSONSource;
      if (src) src.setData({ type: 'FeatureCollection', features: [] });
      void misdirectConfirmed;
      onMisdirectPreviewRef.current?.(null);
      canvas.style.cursor = CROSSHAIR_CURSOR;
    };
  }, [misdirectMode, gameState?.player.position.lat, gameState?.player.position.lng]);

  // --- Doppelgänger mode: click map to set the decoy's walking path ---
  const onDoppelgangerPlaceRef = useRef(onDoppelgangerPlace);
  onDoppelgangerPlaceRef.current = onDoppelgangerPlace;
  const onDoppelgangerPreviewRef = useRef(onDoppelgangerPreview);
  onDoppelgangerPreviewRef.current = onDoppelgangerPreview;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !doppelgangerMode) return;
    const canvas = map.getCanvasContainer();
    canvas.style.cursor = MOVE_CURSOR;

    const DOPPEL_PREVIEW_SOURCE = 'doppelganger-preview-path';
    const DOPPEL_PREVIEW_LAYER = 'doppelganger-preview-line';
    const DOPPEL_LABEL_SOURCE = 'doppelganger-preview-label';
    const DOPPEL_LABEL_LAYER = 'doppelganger-preview-label-text';
    if (!map.getSource(DOPPEL_PREVIEW_SOURCE)) {
      map.addSource(DOPPEL_PREVIEW_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }
    if (!map.getLayer(DOPPEL_PREVIEW_LAYER)) {
      map.addLayer({
        id: DOPPEL_PREVIEW_LAYER,
        type: 'line',
        source: DOPPEL_PREVIEW_SOURCE,
        paint: {
          'line-color': '#ffdd44',
          'line-width': 3,
          'line-opacity': 0.85,
        },
      });
    }
    // Floating distance label at the end of the preview path — mirrors
    // the movement-label layer used by Move cards. Text tints red when
    // the cursor is past the 800m cap so the "(truncated)" state reads
    // at a glance without looking at the banner.
    if (!map.getSource(DOPPEL_LABEL_SOURCE)) {
      map.addSource(DOPPEL_LABEL_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }
    if (!map.getLayer(DOPPEL_LABEL_LAYER)) {
      map.addLayer({
        id: DOPPEL_LABEL_LAYER,
        type: 'symbol',
        source: DOPPEL_LABEL_SOURCE,
        layout: {
          'text-field': ['get', 'label'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 14,
          'text-offset': [0, -1.5],
          'text-anchor': 'bottom',
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': ['match', ['get', 'over'], 'true', theme.colors.accentRed, '#ffdd44'],
          'text-halo-color': theme.colors.background,
          'text-halo-width': 2,
        },
      });
    }

    const DOPPEL_RANGE = 800;
    let doppelConfirmed = false;

    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      if (!gameState) return;
      const playerPos = gameState.player.position;
      let path = findPathBetweenPoints(
        playerPos.lat, playerPos.lng,
        e.lngLat.lat, e.lngLat.lng,
        cityData.navigation.nodes,
        cityData.navigation.edges,
        DOPPEL_RANGE,
      );
      let overRange = false;
      if (!path) {
        path = findPathBetweenPoints(
          playerPos.lat, playerPos.lng,
          e.lngLat.lat, e.lngLat.lng,
          cityData.navigation.nodes,
          cityData.navigation.edges,
          DOPPEL_RANGE * 3,
        );
        overRange = true;
      }
      if (path && path.coordinates.length >= 2) {
        // Truncate to DOPPEL_RANGE meters along the line, tracking the
        // actual truncated distance so the banner can show how long the
        // path is (mirrors the Move card preview).
        const coords = path.coordinates;
        let acc = 0;
        let endIdx = coords.length - 1;
        let truncDist = 0;
        for (let i = 0; i < coords.length - 1; i++) {
          const [lng1, lat1] = coords[i];
          const [lng2, lat2] = coords[i + 1];
          const dLat = (lat2 - lat1) * 111000;
          const dLng = (lng2 - lng1) * 111000 * Math.cos(lat1 * Math.PI / 180);
          const segLen = Math.sqrt(dLat * dLat + dLng * dLng);
          if (acc + segLen >= DOPPEL_RANGE) {
            endIdx = i + 1;
            truncDist = DOPPEL_RANGE;
            break;
          }
          acc += segLen;
          truncDist = acc;
        }
        const preview = coords.slice(0, endIdx + 1);
        const src = map.getSource(DOPPEL_PREVIEW_SOURCE) as maplibregl.GeoJSONSource;
        if (src) {
          src.setData({
            type: 'FeatureCollection',
            features: [{
              type: 'Feature',
              properties: {},
              geometry: { type: 'LineString', coordinates: preview },
            }],
          });
          if (map.getLayer(DOPPEL_PREVIEW_LAYER)) {
            // Tint red-ish when the cursor is beyond the max range as a
            // "truncated here" signal — same UX affordance as the Move
            // card overflow preview.
            map.setPaintProperty(DOPPEL_PREVIEW_LAYER, 'line-color', overRange ? '#ff9944' : '#ffdd44');
          }
        }
        // Distance label at the end of the preview path — matches the
        // Move card's "123m" label that floats above the target node.
        const labelSrc = map.getSource(DOPPEL_LABEL_SOURCE) as maplibregl.GeoJSONSource;
        if (labelSrc && preview.length > 0) {
          const endCoord = preview[preview.length - 1];
          labelSrc.setData({
            type: 'FeatureCollection',
            features: [{
              type: 'Feature',
              properties: {
                label: `${Math.round(truncDist)}m`,
                over: overRange ? 'true' : 'false',
              },
              geometry: { type: 'Point', coordinates: endCoord },
            }],
          });
        }
        onDoppelgangerPreviewRef.current?.({ distance: truncDist, overRange });
      } else {
        const labelSrc = map.getSource(DOPPEL_LABEL_SOURCE) as maplibregl.GeoJSONSource;
        if (labelSrc) labelSrc.setData({ type: 'FeatureCollection', features: [] });
        onDoppelgangerPreviewRef.current?.(null);
      }
    };

    const onClick = (e: maplibregl.MapMouseEvent) => {
      doppelConfirmed = true;
      if (onDoppelgangerPlaceRef.current) onDoppelgangerPlaceRef.current(e.lngLat.lat, e.lngLat.lng);
    };
    map.on('mousemove', onMouseMove);
    map.on('click', onClick);
    return () => {
      map.off('mousemove', onMouseMove);
      map.off('click', onClick);
      // Always clear the preview — the committed decoy renders via its
      // own `doppelganger-path` source, so leaving preview data around
      // would leave a phantom yellow line that outlives the decoy. (This
      // diverges from Misdirect's pattern, where the preview IS the
      // committed display.)
      const src = map.getSource(DOPPEL_PREVIEW_SOURCE) as maplibregl.GeoJSONSource;
      if (src) src.setData({ type: 'FeatureCollection', features: [] });
      const labelSrc = map.getSource(DOPPEL_LABEL_SOURCE) as maplibregl.GeoJSONSource;
      if (labelSrc) labelSrc.setData({ type: 'FeatureCollection', features: [] });
      onDoppelgangerPreviewRef.current?.(null);
      canvas.style.cursor = CROSSHAIR_CURSOR;
      void doppelConfirmed; // kept for parity with misdirect; now unused
    };
  }, [doppelgangerMode, gameState?.player.position.lat, gameState?.player.position.lng]);

  // --- Overwatch mode: player position is fixed at one end; cursor aims
  // the other end. Preview walks up to 300m along the road network,
  // greedy-picking whichever adjacent edge points most toward the cursor.
  // Uses the same pre-created `overwatch-segment` source (red dashed) so
  // once placed, the committed segment keeps rendering in the same style
  // without needing a second layer.
  const onOverwatchPlaceRef = useRef(onOverwatchPlace);
  onOverwatchPlaceRef.current = onOverwatchPlace;
  const onOverwatchPreviewRef = useRef(onOverwatchPreview);
  onOverwatchPreviewRef.current = onOverwatchPreview;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !overwatchMode) return;
    if (!gameState) return;
    const canvas = map.getCanvasContainer();
    canvas.style.cursor = MOVE_CURSOR;

    const PREVIEW_SOURCE = 'overwatch-preview';
    const PREVIEW_LAYER = 'overwatch-preview-line';
    if (!map.getSource(PREVIEW_SOURCE)) {
      map.addSource(PREVIEW_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }
    if (!map.getLayer(PREVIEW_LAYER)) {
      map.addLayer({
        id: PREVIEW_LAYER,
        type: 'line',
        source: PREVIEW_SOURCE,
        paint: {
          'line-color': '#ff5050',
          'line-width': 4,
          'line-dasharray': [4, 3],
          'line-opacity': 0.75,
        },
      });
    }

    const SEGMENT_M = 300;
    const nodeMapLocal = new Map<string, NavNode>();
    for (const n of cityData.navigation.nodes) nodeMapLocal.set(n.id, n);
    const edgeMapLocal = new Map<string, NavEdge>();
    for (const e of cityData.navigation.edges) edgeMapLocal.set(e.id, e);
    const startNodeId = gameState.player.nearestNodeId;
    const startNode = nodeMapLocal.get(startNodeId);

    let confirmed = false;

    const computeSegment = (cursorLat: number, cursorLng: number): [number, number][] => {
      if (!startNode) return [];
      // Anchor preview at player's exact position, then into the nearest
      // road node, so the rendered segment runs from the player icon out
      // to the street (see GameScreen handleOverwatchPlace — same change
      // applied to the committed segment for consistency).
      const playerPos = gameState.player.position;
      const coords: [number, number][] = [[playerPos.lng, playerPos.lat], [startNode.lng, startNode.lat]];
      let currentNodeId: string = startNodeId;
      let remaining = SEGMENT_M;
      let targetRoadId: string | null = null;
      const visitedEdges = new Set<string>();
      const visitedNodes = new Set<string>();
      visitedNodes.add(currentNodeId);

      while (remaining > 0) {
        const curNode = nodeMapLocal.get(currentNodeId);
        if (!curNode) break;
        const cLat = cursorLat - curNode.lat;
        const cLng = cursorLng - curNode.lng;
        const cMag = Math.sqrt(cLat * cLat + cLng * cLng);
        if (cMag === 0) break;
        const cLatN = cLat / cMag;
        const cLngN = cLng / cMag;

        let candidates = curNode.edges
          .map(id => edgeMapLocal.get(id))
          .filter((e): e is NavEdge => !!e && !visitedEdges.has(e.id) && e.classification !== 'footpath');
        // Same-road restriction: stay on the same named street. If the road
        // ends at an intersection, stop here rather than bending onto the
        // cross-street. Mirrors wire tap's collectEdges helper.
        if (targetRoadId) {
          const sameRoad = candidates.filter(e => (e.roadId ?? e.id) === targetRoadId);
          if (sameRoad.length > 0) candidates = sameRoad;
          else break;
        }
        if (candidates.length === 0) break;

        let best: NavEdge | null = null;
        let bestDot = -Infinity;
        let bestOtherId: string | null = null;
        for (const e of candidates) {
          const otherId = e.from === currentNodeId ? e.to : e.from;
          if (visitedNodes.has(otherId)) continue;
          const other = nodeMapLocal.get(otherId);
          if (!other) continue;
          const eLat = other.lat - curNode.lat;
          const eLng = other.lng - curNode.lng;
          const eMag = Math.sqrt(eLat * eLat + eLng * eLng);
          if (eMag === 0) continue;
          const dot = (eLat / eMag) * cLatN + (eLng / eMag) * cLngN;
          if (dot > bestDot) { bestDot = dot; best = e; bestOtherId = otherId; }
        }
        if (!best || !bestOtherId || bestDot <= 0) break;

        visitedEdges.add(best.id);
        if (!targetRoadId) targetRoadId = best.roadId ?? best.id;
        const geom = best.geometry;
        const oriented = best.from === currentNodeId ? geom : [...geom].reverse();
        for (let i = 1; i < oriented.length; i++) coords.push(oriented[i]);
        remaining -= best.length;
        currentNodeId = bestOtherId;
        visitedNodes.add(currentNodeId);
      }
      return coords;
    };

    // Haversine-ish distance between two [lng, lat] points (meters).
    const segDist = (a: [number, number], b: [number, number]): number => {
      const dLat = (b[1] - a[1]) * 111000;
      const dLng = (b[0] - a[0]) * 111000 * Math.cos(((a[1] + b[1]) / 2) * Math.PI / 180);
      return Math.sqrt(dLat * dLat + dLng * dLng);
    };
    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      const coords = computeSegment(e.lngLat.lat, e.lngLat.lng);
      const src = map.getSource(PREVIEW_SOURCE) as maplibregl.GeoJSONSource;
      if (!src) return;
      if (coords.length >= 2) {
        src.setData({
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            properties: {},
            geometry: { type: 'LineString', coordinates: coords },
          }],
        });
        // Sum consecutive segment lengths for the live distance readout.
        let total = 0;
        for (let i = 1; i < coords.length; i++) total += segDist(coords[i - 1], coords[i]);
        onOverwatchPreviewRef.current?.({ distance: total });
      } else {
        src.setData({ type: 'FeatureCollection', features: [] });
        onOverwatchPreviewRef.current?.(null);
      }
    };

    const onClick = (e: maplibregl.MapMouseEvent) => {
      confirmed = true;
      if (onOverwatchPlaceRef.current) onOverwatchPlaceRef.current(e.lngLat.lat, e.lngLat.lng);
    };

    map.on('mousemove', onMouseMove);
    map.on('click', onClick);
    return () => {
      map.off('mousemove', onMouseMove);
      map.off('click', onClick);
      // Always clear the preview on exit — the committed segment lives in
      // the `overwatch-segment` source, so clearing the preview can't
      // accidentally wipe it.
      const src = map.getSource(PREVIEW_SOURCE) as maplibregl.GeoJSONSource | undefined;
      if (src) src.setData({ type: 'FeatureCollection', features: [] });
      onOverwatchPreviewRef.current?.(null);
      canvas.style.cursor = CROSSHAIR_CURSOR;
      void confirmed; // acknowledged — we clear the preview either way
    };
  }, [overwatchMode, gameState?.player.position.lat, gameState?.player.position.lng, gameState?.player.nearestNodeId, cityData.navigation.nodes, cityData.navigation.edges]);

  // --- Taxi select mode: click a road to pick destination, live preview while hovering ---
  const onTaxiSelectPlaceRef = useRef(onTaxiSelectPlace);
  onTaxiSelectPlaceRef.current = onTaxiSelectPlace;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !taxiSelectMode) return;
    const canvas = map.getCanvasContainer();
    canvas.style.cursor = MOVE_CURSOR;

    // Pre-filter to drivable edges (no footpaths) so the taxi only routes on real roads
    const drivableEdges = cityData.navigation.edges.filter(e => e.classification !== 'footpath');

    const TAXI_PREVIEW_SOURCE = 'taxi-preview-path';
    const TAXI_PREVIEW_LAYER = 'taxi-preview-line';
    if (!map.getSource(TAXI_PREVIEW_SOURCE)) {
      map.addSource(TAXI_PREVIEW_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }
    if (!map.getLayer(TAXI_PREVIEW_LAYER)) {
      map.addLayer({
        id: TAXI_PREVIEW_LAYER,
        type: 'line',
        source: TAXI_PREVIEW_SOURCE,
        paint: {
          'line-color': '#ffcc33',
          'line-width': 4,
          'line-opacity': 0.85,
        },
      });
    }

    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      if (!gameState) return;
      const playerPos = gameState.player.position;
      const path = findPathBetweenPoints(
        playerPos.lat, playerPos.lng,
        e.lngLat.lat, e.lngLat.lng,
        cityData.navigation.nodes,
        drivableEdges,
        100000, // effectively unlimited — taxi has no range cap
      );
      const src = map.getSource(TAXI_PREVIEW_SOURCE) as maplibregl.GeoJSONSource;
      if (!src) return;
      if (path && path.coordinates.length >= 2) {
        src.setData({
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            properties: {},
            geometry: { type: 'LineString', coordinates: path.coordinates },
          }],
        });
      } else {
        src.setData({ type: 'FeatureCollection', features: [] });
      }
    };

    const onClick = (e: maplibregl.MapMouseEvent) => {
      if (onTaxiSelectPlaceRef.current) onTaxiSelectPlaceRef.current(e.lngLat.lat, e.lngLat.lng);
    };
    map.on('mousemove', onMouseMove);
    map.on('click', onClick);
    return () => {
      map.off('mousemove', onMouseMove);
      map.off('click', onClick);
      // Clear preview on exit
      const src = map.getSource(TAXI_PREVIEW_SOURCE) as maplibregl.GeoJSONSource;
      if (src) src.setData({ type: 'FeatureCollection', features: [] });
      canvas.style.cursor = CROSSHAIR_CURSOR;
    };
  }, [taxiSelectMode, gameState?.player.position.lat, gameState?.player.position.lng, cityData.navigation.nodes, cityData.navigation.edges]);

  // --- Recon Plane mode: click-drag-release to place a rotating strip ---
  // On mousedown: capture the click point as the strip's center.
  // While dragging: the cursor position defines the strip's angle from center
  //   (bearing from center → cursor). The preview updates live.
  // On mouseup: fire onReconPlanePlace(center, angleDeg). The parent opens a
  //   confirm banner; the preview stays visible via reconPlanePending until
  //   the player confirms or cancels.
  const onReconPlanePlaceRef = useRef(onReconPlanePlace);
  onReconPlanePlaceRef.current = onReconPlanePlace;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !reconPlaneMode) return;
    const canvas = map.getCanvasContainer();
    canvas.style.cursor = MOVE_CURSOR;

    // Compute a strip length that spans the full map (plus overshoot).
    const b = cityData.bounds;
    const dLatM = (b.north - b.south) * 111000;
    const dLngM = (b.east - b.west) * 111000 * Math.cos(((b.north + b.south) / 2) * Math.PI / 180);
    const stripLength = 2 * Math.sqrt(dLatM * dLatM + dLngM * dLngM);
    // Must match the widthM the parent stamps into reconPlanePending
    // (GameScreen.tsx passes 300 when committing placement). Used to be
    // 200, which meant the drag preview showed a thinner band than the
    // strip that actually gets scanned on commit.
    const STRIP_WIDTH = 300;

    const PREVIEW_SRC = 'recon-plane-preview';
    const PREVIEW_FILL = 'recon-plane-preview-fill';
    const PREVIEW_LINE = 'recon-plane-preview-line';
    if (!map.getSource(PREVIEW_SRC)) {
      map.addSource(PREVIEW_SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    }
    if (!map.getLayer(PREVIEW_FILL)) {
      map.addLayer({
        id: PREVIEW_FILL,
        type: 'fill',
        source: PREVIEW_SRC,
        paint: { 'fill-color': '#aa88ff', 'fill-opacity': 0.15 },
      });
    }
    if (!map.getLayer(PREVIEW_LINE)) {
      map.addLayer({
        id: PREVIEW_LINE,
        type: 'line',
        source: PREVIEW_SRC,
        paint: { 'line-color': '#aa88ff', 'line-width': 2, 'line-dasharray': [4, 3], 'line-opacity': 0.9 },
      });
    }

    let dragging = false;
    let centerLng = 0;
    let centerLat = 0;

    const setPreview = (lng: number, lat: number, angleDeg: number) => {
      const coords = createStripPolygon(lng, lat, angleDeg, stripLength, STRIP_WIDTH);
      const src = map.getSource(PREVIEW_SRC) as maplibregl.GeoJSONSource;
      if (!src) return;
      src.setData({
        type: 'FeatureCollection',
        features: [{ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [coords] } }],
      });
    };

    const bearingFromCenter = (lng: number, lat: number): number => {
      const dLat = (lat - centerLat) * 111000;
      const dLng = (lng - centerLng) * 111000 * Math.cos((centerLat * Math.PI) / 180);
      // Bearing: 0 = north (positive dLat), 90 = east (positive dLng)
      const theta = Math.atan2(dLng, dLat);
      return ((theta * 180) / Math.PI + 360) % 360;
    };

    const onMouseDown = (e: maplibregl.MapMouseEvent) => {
      // Only left-click starts a placement drag. Right-click is passed through
      // to the window-level contextmenu handler in GameScreen, which cancels
      // the placement. Middle-click is ignored.
      if (e.originalEvent.button !== 0) return;
      e.preventDefault();
      dragging = true;
      centerLng = e.lngLat.lng;
      centerLat = e.lngLat.lat;
      // Default angle: horizontal (east-west)
      setPreview(centerLng, centerLat, 90);
    };
    const onMouseMoveDrag = (e: maplibregl.MapMouseEvent) => {
      if (!dragging) return;
      const angle = bearingFromCenter(e.lngLat.lng, e.lngLat.lat);
      setPreview(centerLng, centerLat, angle);
    };
    const onMouseUp = (e: maplibregl.MapMouseEvent) => {
      if (!dragging) return;
      if (e.originalEvent.button !== 0) return;
      dragging = false;
      const angle = bearingFromCenter(e.lngLat.lng, e.lngLat.lat);
      setPreview(centerLng, centerLat, angle);
      if (onReconPlanePlaceRef.current) {
        onReconPlanePlaceRef.current({ lat: centerLat, lng: centerLng }, angle);
      }
    };

    // Disable native map drag-pan during recon mode so mousedown doesn't pan the map
    map.dragPan.disable();
    map.on('mousedown', onMouseDown);
    map.on('mousemove', onMouseMoveDrag);
    map.on('mouseup', onMouseUp);

    return () => {
      map.off('mousedown', onMouseDown);
      map.off('mousemove', onMouseMoveDrag);
      map.off('mouseup', onMouseUp);
      map.dragPan.enable();
      // Clear preview
      const src = map.getSource(PREVIEW_SRC) as maplibregl.GeoJSONSource;
      if (src) src.setData({ type: 'FeatureCollection', features: [] });
      canvas.style.cursor = CROSSHAIR_CURSOR;
    };
  }, [reconPlaneMode, cityData.bounds]);

  // Recon Plane pending preview — when a placement has been made but not yet
  // confirmed, render it on the map using the same preview source so the
  // player can see what they're about to commit to. Cleared when the pending
  // is cleared or replaced by a fresh drag.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!reconPlanePending) return;
    if (!map.getSource('recon-plane-preview')) return;
    const src = map.getSource('recon-plane-preview') as maplibregl.GeoJSONSource;
    if (!src) return;
    const coords = createStripPolygon(
      reconPlanePending.center.lng,
      reconPlanePending.center.lat,
      reconPlanePending.angleDeg,
      reconPlanePending.lengthM,
      reconPlanePending.widthM,
    );
    src.setData({
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [coords] } }],
    });
  }, [reconPlanePending]);

  // --- Stakeout placement: two-click flow ---
  // Phase 1: no pending building → highlight commercial buildings, click picks
  //   one and calls onStakeoutBuildingSelect(buildingId, centroid).
  // Phase 2: pending building set → render a live cone preview following the
  //   cursor's bearing from the building centroid, click anywhere to commit
  //   via onStakeoutAngleCommit(lat, lng).
  const onStakeoutBuildingSelectRef = useRef(onStakeoutBuildingSelect);
  onStakeoutBuildingSelectRef.current = onStakeoutBuildingSelect;
  const onStakeoutAngleCommitRef = useRef(onStakeoutAngleCommit);
  onStakeoutAngleCommitRef.current = onStakeoutAngleCommit;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !stakeoutMode || !gameState) return;

    const canvas = map.getCanvasContainer();

    // Preview layers for the angle phase — a purple cone following the cursor.
    const PREVIEW_SRC = 'stakeout-preview-cone';
    const PREVIEW_FILL = 'stakeout-preview-cone-fill';
    const PREVIEW_LINE = 'stakeout-preview-cone-line';
    if (!map.getSource(PREVIEW_SRC)) {
      map.addSource(PREVIEW_SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    }
    if (!map.getLayer(PREVIEW_FILL)) {
      map.addLayer({
        id: PREVIEW_FILL,
        type: 'fill',
        source: PREVIEW_SRC,
        paint: { 'fill-color': '#aa88ff', 'fill-opacity': 0.15 },
      });
    }
    if (!map.getLayer(PREVIEW_LINE)) {
      map.addLayer({
        id: PREVIEW_LINE,
        type: 'line',
        source: PREVIEW_SRC,
        paint: { 'line-color': '#aa88ff', 'line-width': 2, 'line-dasharray': [3, 3], 'line-opacity': 0.9 },
      });
    }

    // Shared helper to clear the preview source
    const clearPreview = () => {
      const src = map.getSource(PREVIEW_SRC) as maplibregl.GeoJSONSource | undefined;
      if (src) src.setData({ type: 'FeatureCollection', features: [] });
    };

    // --- PHASE 1: building selection ---
    // Pre-compute commercial building IDs + highlight via the buildings source
    // feature state (same mechanic as Rooftop Access).
    const highlightedIds = new Set<number>();
    let setupDone = false;
    let hoveredId: number | undefined;
    let cancelled = false;

    const setup = () => {
      if (setupDone || cancelled) return;
      if (!map.getSource('buildings') || !map.getLayer('buildings-fill')) {
        setTimeout(setup, 100);
        return;
      }
      setupDone = true;

      const data = cityData.layers.buildings;
      if (!data || !data.features) return;

      const COMMERCIAL_TYPES = ['commercial', 'retail', 'office', 'industrial', 'warehouse', 'public', 'civic', 'government', 'church', 'cathedral', 'chapel', 'mosque', 'synagogue', 'temple', 'hospital', 'hotel', 'school', 'university', 'train_station', 'supermarket', 'mall', 'basilica'];
      const zonePoly = buildZoneLookup(cityData.layers.zones);
      for (let i = 0; i < data.features.length; i++) {
        const feature = data.features[i];
        const geom = feature.geometry;
        if (!geom) continue;
        const buildingType = (feature.properties as { type?: string } | undefined)?.type || 'yes';
        let isCommercial = COMMERCIAL_TYPES.includes(buildingType);
        const RESIDENTIAL_TYPES_SET = ['residential', 'house', 'apartments', 'detached', 'semidetached_house', 'terrace', 'bungalow', 'cabin', 'dormitory'];
        const isResidential = RESIDENTIAL_TYPES_SET.includes(buildingType);
        if (!isCommercial && !isResidential) {
          // Fall back to zone check for non-residential untagged buildings
          let checkLng: number, checkLat: number;
          if (geom.type === 'Polygon') {
            checkLng = (geom.coordinates[0] as [number, number][])[0][0];
            checkLat = (geom.coordinates[0] as [number, number][])[0][1];
          } else if (geom.type === 'MultiPolygon') {
            checkLng = (geom.coordinates[0][0] as [number, number][])[0][0];
            checkLat = (geom.coordinates[0][0] as [number, number][])[0][1];
          } else {
            continue;
          }
          const z = zoneAtPoint(checkLng, checkLat, zonePoly);
          if (z === 'commercial' || z === 'mixed-use' || z === 'civic' || z === 'transit') isCommercial = true;
        }
        if (!isCommercial) continue;
        highlightedIds.add(i);
        // Reuse the rooftopAvailable feature state — its cyan highlight styling
        // is perfect for a "pick a building" affordance and keeps us from
        // adding a new shader state key.
        map.setFeatureState({ source: 'buildings', id: i }, { rooftopAvailable: true });
      }
    };

    const computeCentroid = (feature: GeoJSON.Feature): { lat: number; lng: number } | null => {
      if (feature.geometry?.type !== 'Polygon') return null;
      const ring = (feature.geometry as GeoJSON.Polygon).coordinates[0];
      let sumLng = 0, sumLat = 0;
      const n = ring.length > 1 ? ring.length - 1 : ring.length;
      for (let i = 0; i < n; i++) {
        sumLng += ring[i][0];
        sumLat += ring[i][1];
      }
      return { lat: sumLat / n, lng: sumLng / n };
    };

    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      // PHASE 2: live cone preview following the cursor from the chosen building.
      if (stakeoutPendingBuilding) {
        const center = stakeoutPendingBuilding.position;
        const dLat = (e.lngLat.lat - center.lat) * 111000;
        const dLng = (e.lngLat.lng - center.lng) * 111000 * Math.cos((center.lat * Math.PI) / 180);
        const theta = Math.atan2(dLng, dLat);
        const bearingDegrees = ((theta * 180) / Math.PI + 360) % 360;
        const coords = createConePolygon(
          center.lng,
          center.lat,
          tuning.stakeoutConeRange,
          bearingDegrees,
          tuning.stakeoutConeAngle,
        );
        const src = map.getSource(PREVIEW_SRC) as maplibregl.GeoJSONSource | undefined;
        if (src) {
          src.setData({
            type: 'FeatureCollection',
            features: [{ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [coords] } }],
          });
        }
        canvas.style.cursor = 'crosshair';
        return;
      }

      // PHASE 1: building hover highlight
      if (!setupDone) return;
      const point = e.point;
      const bbox: [maplibregl.PointLike, maplibregl.PointLike] = [
        [point.x - 8, point.y - 8],
        [point.x + 8, point.y + 8],
      ];
      const buildingFeatures = map.getLayer('buildings-fill')
        ? map.queryRenderedFeatures(bbox, { layers: ['buildings-fill'] })
        : [];
      if (hoveredId !== undefined) {
        map.setFeatureState({ source: 'buildings', id: hoveredId }, { hideHover: false });
        hoveredId = undefined;
      }
      if (buildingFeatures.length > 0) {
        const feature = buildingFeatures[0];
        const rawId = feature.id;
        const id = typeof rawId === 'string' ? parseInt(rawId, 10) : rawId as number;
        if (!isNaN(id) && highlightedIds.has(id)) {
          hoveredId = id;
          map.setFeatureState({ source: 'buildings', id }, { hideHover: true });
          canvas.style.cursor = 'pointer';
          return;
        }
      }
      canvas.style.cursor = 'not-allowed';
    };

    const onClick = (e: maplibregl.MapMouseEvent) => {
      // PHASE 2: commit the angle via the click point's bearing.
      if (stakeoutPendingBuilding) {
        if (onStakeoutAngleCommitRef.current) {
          onStakeoutAngleCommitRef.current(e.lngLat.lat, e.lngLat.lng);
        }
        return;
      }

      // PHASE 1: pick a commercial building.
      if (!setupDone) return;
      const point = e.point;
      const bbox: [maplibregl.PointLike, maplibregl.PointLike] = [
        [point.x - 8, point.y - 8],
        [point.x + 8, point.y + 8],
      ];
      const buildingFeatures = map.getLayer('buildings-fill')
        ? map.queryRenderedFeatures(bbox, { layers: ['buildings-fill'] })
        : [];
      if (buildingFeatures.length > 0) {
        const feature = buildingFeatures[0];
        const rawId = feature.id;
        const id = typeof rawId === 'string' ? parseInt(rawId, 10) : rawId as number;
        if (!isNaN(id) && highlightedIds.has(id)) {
          // Use the actual feature from cityData for centroid math (map features
          // may be clipped by tile boundaries, giving bad centroids).
          const src = cityData.layers.buildings.features[id];
          const centroid = src ? computeCentroid(src) : computeCentroid(feature);
          if (centroid && onStakeoutBuildingSelectRef.current) {
            onStakeoutBuildingSelectRef.current(id, centroid);
          }
        }
      }
    };

    setup();
    map.on('mousemove', onMouseMove);
    map.on('click', onClick);

    return () => {
      cancelled = true;
      map.off('mousemove', onMouseMove);
      map.off('click', onClick);
      clearPreview();
      // Clear building highlights
      if (map.getSource('buildings')) {
        try {
          map.removeFeatureState({ source: 'buildings' });
        } catch {
          for (const id of highlightedIds) {
            map.setFeatureState({ source: 'buildings', id }, { rooftopAvailable: false, hideHover: false });
          }
        }
      }
      canvas.style.cursor = CROSSHAIR_CURSOR;
    };
  }, [stakeoutMode, stakeoutPendingBuilding, cityData]);

  // --- Surveillance Van placement: two-click flow, mirrors Move card ---
  // Click 1: snap to the nearest drivable node and pin the origin.
  // Mouse move (no click): live pathfind from origin → cursor along drivable
  //   edges. Length and over-budget state are reported via
  //   onSurveillanceVanPreview so the parent can show a live length banner.
  // Click 2: if in budget, commit via onSurveillanceVanRoutePlace(fullCoords).
  //
  // Over-budget routes render in red instead of being hidden; the commit is
  // rejected on click 2 and the player has to shorten the route.
  //
  // Origin lives in the effect's closure so GameScreen doesn't need to know
  // about the intermediate state — it only receives the final coord list.
  const onSurveillanceVanRoutePlaceRef = useRef(onSurveillanceVanRoutePlace);
  onSurveillanceVanRoutePlaceRef.current = onSurveillanceVanRoutePlace;
  const onSurveillanceVanPreviewRef = useRef(onSurveillanceVanPreview);
  onSurveillanceVanPreviewRef.current = onSurveillanceVanPreview;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !surveillanceVanMode || !gameState) return;
    const canvas = map.getCanvasContainer();
    canvas.style.cursor = MOVE_CURSOR;

    // Pre-filter drivable edges once per mode activation. Same filter as Taxi.
    const drivable = cityData.navigation.edges.filter(e => e.classification !== 'footpath');

    // Preview layers. Line color switches to red when over-budget via the
    // 'over' feature property.
    const PREVIEW_SRC = 'surveillance-van-preview-route';
    const PREVIEW_GLOW = 'surveillance-van-preview-route-glow';
    const PREVIEW_LINE = 'surveillance-van-preview-route-line';
    if (!map.getSource(PREVIEW_SRC)) {
      map.addSource(PREVIEW_SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    }
    if (!map.getLayer(PREVIEW_GLOW)) {
      map.addLayer({
        id: PREVIEW_GLOW,
        type: 'line',
        source: PREVIEW_SRC,
        paint: {
          'line-color': ['case', ['boolean', ['get', 'over'], false], '#ff3c3c', '#7a5ed6'],
          'line-width': 10,
          'line-opacity': 0.32,
          'line-blur': 3,
        },
      });
    }
    if (!map.getLayer(PREVIEW_LINE)) {
      map.addLayer({
        id: PREVIEW_LINE,
        type: 'line',
        source: PREVIEW_SRC,
        paint: {
          'line-color': ['case', ['boolean', ['get', 'over'], false], '#ff3c3c', '#7a5ed6'],
          'line-width': 4,
          'line-dasharray': [3, 2],
          'line-opacity': 1.0,
        },
      });
    }
    const setPreview = (coords: [number, number][], over: boolean) => {
      const src = map.getSource(PREVIEW_SRC) as maplibregl.GeoJSONSource | undefined;
      if (!src) return;
      if (coords.length < 2) {
        src.setData({ type: 'FeatureCollection', features: [] });
        return;
      }
      src.setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: { over },
          geometry: { type: 'LineString', coordinates: coords },
        }],
      });
    };
    const clearPreview = () => {
      setPreview([], false);
      onSurveillanceVanPreviewRef.current?.(null);
    };

    // --- Local origin state (effect-scoped) ---
    let origin: { lat: number; lng: number } | null = null;
    // Cache the most recent in-budget path so we can commit on the second
    // click even if the final mousemove landed on an out-of-budget cursor.
    let lastInBudgetCoords: [number, number][] | null = null;
    let lastInBudgetLength = 0;

    const budget = tuning.surveillanceVanRouteBudget;

    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      if (!origin) return;
      // Try the in-budget pathfind first. If it fails, re-try with a huge
      // budget so we can render the over-range path in red (Misdirect style).
      let path = findPathBetweenPoints(
        origin.lat, origin.lng,
        e.lngLat.lat, e.lngLat.lng,
        cityData.navigation.nodes,
        drivable,
        budget,
      );
      let over = false;
      if (!path) {
        path = findPathBetweenPoints(
          origin.lat, origin.lng,
          e.lngLat.lat, e.lngLat.lng,
          cityData.navigation.nodes,
          drivable,
          budget * 10,
        );
        over = true;
      }
      if (!path || path.coordinates.length < 2) {
        setPreview([], false);
        onSurveillanceVanPreviewRef.current?.({ distance: 0, overBudget: false });
        return;
      }
      const coords = path.coordinates as [number, number][];
      setPreview(coords, over);
      onSurveillanceVanPreviewRef.current?.({ distance: path.totalDistance, overBudget: over });
      if (!over) {
        lastInBudgetCoords = coords;
        lastInBudgetLength = path.totalDistance;
      }
    };

    // Max distance (meters) the click can be from a drivable road to still
    // count as a valid origin. Generous enough to forgive fat-fingering on
    // narrow streets, tight enough to reject clicks in buildings/parks.
    const MAX_SNAP_DIST_M = 60;

    const onClick = (e: maplibregl.MapMouseEvent) => {
      if (e.originalEvent.button !== 0) return;
      if (!origin) {
        // PHASE 1: snap the click onto the nearest drivable edge (not node).
        // This uses the same on-edge snapping as the player Move card, so a
        // click in the middle of a long straight road segment lands exactly
        // where the player clicked rather than jumping to the far-off
        // intersection at either end.
        const snap = snapPointToEdges(e.lngLat.lat, e.lngLat.lng, drivable);
        if (!snap || snap.distanceM > MAX_SNAP_DIST_M) return;
        origin = { lat: snap.lat, lng: snap.lng };
        // Seed the preview at the origin itself — mouse move will replace it.
        setPreview([[snap.lng, snap.lat]], false);
        onSurveillanceVanPreviewRef.current?.({ distance: 0, overBudget: false });
        return;
      }
      // PHASE 2: commit if the current preview is in-budget.
      // Re-pathfind fresh so the committed coord list always matches what the
      // player sees at the moment of click.
      const path = findPathBetweenPoints(
        origin.lat, origin.lng,
        e.lngLat.lat, e.lngLat.lng,
        cityData.navigation.nodes,
        drivable,
        budget,
      );
      if (path && path.coordinates.length >= 2 && path.totalDistance >= 20) {
        if (onSurveillanceVanRoutePlaceRef.current) {
          onSurveillanceVanRoutePlaceRef.current(path.coordinates as [number, number][]);
        }
        return;
      }
      // Click landed on an over-budget endpoint — if we have a cached in-budget
      // path, do nothing (let the player keep hunting). If we don't even have
      // that yet, also do nothing. (Misdirect-style: no scolding modal.)
      // Suppress unused-var lint — cached length is only for the banner.
      void lastInBudgetCoords;
      void lastInBudgetLength;
    };

    map.on('mousemove', onMouseMove);
    map.on('click', onClick);
    return () => {
      map.off('mousemove', onMouseMove);
      map.off('click', onClick);
      clearPreview();
      canvas.style.cursor = CROSSHAIR_CURSOR;
    };
  }, [surveillanceVanMode, cityData.navigation.nodes, cityData.navigation.edges]);

  // Persistent Surveillance Van render — routes, cones, and marker points.
  // Sources/layers are pre-created in addDataLayers, so this just calls setData.
  //
  // Marker animation: on each state change, any van whose animPath differs
  // from what we animated last time gets a RAF-driven interpolation along
  // that polyline, with the cone following the marker. Multiple vans animate
  // concurrently on a single shared RAF loop.
  const vanAnimRafRef = useRef<number | null>(null);
  const vanAnimStateRef = useRef<Map<string, {
    path: [number, number][];
    cumDist: number[];
    total: number;
    startTime: number;
    duration: number;
    visionRangeM: number;
    agentInCone: boolean;
    finalPos: { lat: number; lng: number };
  }>>(new Map());
  const lastVanAnimPathRef = useRef<Map<string, [number, number][] | null>>(new Map());

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;
    const routeSrc = map.getSource('surveillance-van-routes') as maplibregl.GeoJSONSource | undefined;
    const coneSrc = map.getSource('surveillance-van-cones') as maplibregl.GeoJSONSource | undefined;
    const markerSrc = map.getSource('surveillance-van-markers') as maplibregl.GeoJSONSource | undefined;
    if (!routeSrc || !coneSrc || !markerSrc) return;

    // Routes never animate — just set directly.
    const routeFeatures: GeoJSON.Feature[] = gameState.surveillanceVans.map(v => ({
      type: 'Feature' as const,
      properties: { id: v.id },
      geometry: { type: 'LineString' as const, coordinates: v.routeCoords },
    }));
    routeSrc.setData({ type: 'FeatureCollection', features: routeFeatures });

    // Figure out which vans have a NEW animPath to play. A van is "animating"
    // if its current animPath is a different reference than the one we last
    // animated (or last rendered static) for that van.
    const now = performance.now();
    const nextAnimState = new Map<string, typeof vanAnimStateRef.current extends Map<string, infer V> ? V : never>();
    for (const v of gameState.surveillanceVans) {
      const lastPath = lastVanAnimPathRef.current.get(v.id) ?? null;
      // Already-animating van that hasn't changed: keep existing anim state.
      const existing = vanAnimStateRef.current.get(v.id);
      if (v.animPath && v.animPath === lastPath && existing) {
        nextAnimState.set(v.id, existing);
        continue;
      }
      if (v.animPath && v.animPath.length >= 2) {
        // Compute cumulative distances for the path (meters).
        const coords = v.animPath;
        const cumDist: number[] = [0];
        for (let i = 1; i < coords.length; i++) {
          const [lng1, lat1] = coords[i - 1];
          const [lng2, lat2] = coords[i];
          const dLat = (lat2 - lat1) * 111000;
          const dLng = (lng2 - lng1) * 111000 * Math.cos((lat1 * Math.PI) / 180);
          cumDist.push(cumDist[i - 1] + Math.sqrt(dLat * dLat + dLng * dLng));
        }
        const total = cumDist[cumDist.length - 1];
        if (total > 1) {
          // ~1.5ms per meter capped at 1400ms. 200m step ≈ 800ms.
          const duration = Math.min(1400, 400 + total * 1.5);
          nextAnimState.set(v.id, {
            path: coords,
            cumDist,
            total,
            startTime: now,
            duration,
            visionRangeM: v.visionRangeM,
            agentInCone: v.agentInCone,
            finalPos: v.position,
          });
        }
      }
      lastVanAnimPathRef.current.set(v.id, v.animPath ?? null);
    }
    // Drop animations for vans that no longer exist.
    for (const id of Array.from(vanAnimStateRef.current.keys())) {
      if (!gameState.surveillanceVans.find(v => v.id === id)) {
        vanAnimStateRef.current.delete(id);
        lastVanAnimPathRef.current.delete(id);
      }
    }
    vanAnimStateRef.current = nextAnimState;

    // Helper: render a snapshot of all vans at their current animated (or
    // static) positions.
    const renderFrame = () => {
      const timeNow = performance.now();
      const animState = vanAnimStateRef.current;
      let anyStillAnimating = false;
      const markerFeatures: GeoJSON.Feature[] = [];
      const coneFeatures: GeoJSON.Feature[] = [];
      for (const v of gameState.surveillanceVans) {
        const anim = animState.get(v.id);
        let vanPos: { lat: number; lng: number } = v.position;
        if (anim) {
          const t = Math.min(1, (timeNow - anim.startTime) / anim.duration);
          if (t < 1) anyStillAnimating = true;
          // Ease-in-out cubic
          const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
          const target = eased * anim.total;
          let i = 1;
          while (i < anim.cumDist.length && anim.cumDist[i] < target) i++;
          if (i >= anim.cumDist.length) i = anim.cumDist.length - 1;
          const segStart = anim.cumDist[i - 1];
          const segLen = anim.cumDist[i] - segStart;
          const segT = segLen > 0 ? (target - segStart) / segLen : 0;
          const [lng1, lat1] = anim.path[i - 1];
          const [lng2, lat2] = anim.path[i];
          vanPos = { lat: lat1 + (lat2 - lat1) * segT, lng: lng1 + (lng2 - lng1) * segT };
          if (t >= 1) vanPos = anim.finalPos;
        }
        markerFeatures.push({
          type: 'Feature' as const,
          properties: { id: v.id },
          geometry: { type: 'Point' as const, coordinates: [vanPos.lng, vanPos.lat] },
        });
        const coneCoords = createCirclePolygon(vanPos.lng, vanPos.lat, v.visionRangeM, 36);
        coneFeatures.push({
          type: 'Feature' as const,
          properties: { id: v.id, active: v.agentInCone },
          geometry: { type: 'Polygon' as const, coordinates: [coneCoords] },
        });
      }
      markerSrc.setData({ type: 'FeatureCollection', features: markerFeatures });
      coneSrc.setData({ type: 'FeatureCollection', features: coneFeatures });
      if (anyStillAnimating) {
        vanAnimRafRef.current = requestAnimationFrame(renderFrame);
      } else {
        vanAnimRafRef.current = null;
        // Clear anim state so next update starts fresh comparisons
        vanAnimStateRef.current = new Map();
      }
    };

    // Cancel any in-flight animation and start a new frame loop.
    if (vanAnimRafRef.current != null) {
      cancelAnimationFrame(vanAnimRafRef.current);
      vanAnimRafRef.current = null;
    }
    renderFrame();

    // Defensively lift the van layers above any street layers that may have
    // been added later by other effects. moveLayer with no beforeId puts the
    // layer at the very top of the stack.
    try {
      if (map.getLayer('surveillance-van-routes-glow')) map.moveLayer('surveillance-van-routes-glow');
      if (map.getLayer('surveillance-van-routes-line')) map.moveLayer('surveillance-van-routes-line');
      if (map.getLayer('surveillance-van-cones-fill')) map.moveLayer('surveillance-van-cones-fill');
      if (map.getLayer('surveillance-van-cones-outline')) map.moveLayer('surveillance-van-cones-outline');
      if (map.getLayer('surveillance-van-marker-halo')) map.moveLayer('surveillance-van-marker-halo');
      if (map.getLayer('surveillance-van-marker-icon')) map.moveLayer('surveillance-van-marker-icon');
    } catch { /* ignore */ }
  }, [gameState?.surveillanceVans, mapLayersReady]);

  // Persistent Recon Plane strips — render every past strip as a colored
  // rectangle (red for hit, gray for miss) with age-based fade matching Sweep.
  // Source + layers are pre-created in addDataLayers() so this effect just
  // has to call setData — no isStyleLoaded dance required.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;
    const src = map.getSource('recon-plane-strips') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;

    const activeStrips = gameState.reconStrips.filter(s => gameState.turn - s.turn <= 3);
    const features: GeoJSON.Feature[] = activeStrips.map(s => {
      const age = gameState.turn - s.turn;
      const fillBase = age === 0 ? 0.20 : age === 1 ? 0.10 : age === 2 ? 0.05 : 0.02;
      const lineBase = age === 0 ? 0.9 : age === 1 ? 0.45 : age === 2 ? 0.22 : 0.1;
      const color = s.hit ? '#ff4444' : '#777788';
      const coords = createStripPolygon(s.center.lng, s.center.lat, s.angleDeg, s.lengthM, s.widthM);
      return {
        type: 'Feature' as const,
        properties: { id: s.id, color, fillOpacity: fillBase, lineOpacity: lineBase, age },
        geometry: { type: 'Polygon' as const, coordinates: [coords] },
      };
    });
    src.setData({ type: 'FeatureCollection', features });
  }, [gameState?.reconStrips, gameState?.turn, mapLayersReady]);

  // Persistent stakeout cones + marker dots. Sources/layers are pre-created in
  // addDataLayers, so this effect just rebuilds features from gameState.stakeouts.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;
    const coneSrc = map.getSource('stakeout-cones') as maplibregl.GeoJSONSource | undefined;
    const markerSrc = map.getSource('stakeout-markers') as maplibregl.GeoJSONSource | undefined;
    if (!coneSrc || !markerSrc) return;

    const coneFeatures: GeoJSON.Feature[] = gameState.stakeouts.map(s => {
      const coords = createConePolygon(s.position.lng, s.position.lat, s.coneRangeM, s.facingDeg, s.coneAngleDeg);
      return {
        type: 'Feature' as const,
        properties: { id: s.id, active: s.agentInCone },
        geometry: { type: 'Polygon' as const, coordinates: [coords] },
      };
    });
    coneSrc.setData({ type: 'FeatureCollection', features: coneFeatures });

    const markerFeatures: GeoJSON.Feature[] = gameState.stakeouts.map(s => ({
      type: 'Feature' as const,
      properties: { id: s.id, active: s.agentInCone },
      geometry: { type: 'Point' as const, coordinates: [s.position.lng, s.position.lat] },
    }));
    markerSrc.setData({ type: 'FeatureCollection', features: markerFeatures });
  }, [gameState?.stakeouts, mapLayersReady]);

  // Persistent stakeout building highlights — paint each deployed stakeout's
  // building purple using feature state. Separate effect so the picker's
  // feature-state cleanup doesn't wipe these out.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;
    // Wait for the buildings source (may take a few ms on mount)
    if (!map.getSource('buildings')) return;
    const painted: number[] = [];
    for (const s of gameState.stakeouts) {
      try {
        map.setFeatureState({ source: 'buildings', id: s.buildingFeatureId }, { stakeoutActive: true });
        painted.push(s.buildingFeatureId);
      } catch { /* feature state may fail if id not loaded yet — ignore */ }
    }
    return () => {
      for (const id of painted) {
        try {
          map.setFeatureState({ source: 'buildings', id }, { stakeoutActive: false });
        } catch { /* ignore */ }
      }
    };
  }, [gameState?.stakeouts, mapLayersReady]);

  // Signal pulse overlay — transient ring chart next to the player marker.
  // On each new pulse (identified by id), add a maplibregl.Marker with an
  // HTML/SVG circular progress ring + label. The element has a CSS animation
  // that fades in, holds for ~1.2s, then fades out. Auto-removed from the
  // map on animation end via setTimeout. Multiple pulses in quick succession
  // cancel the previous one so the reading is always current.
  const signalPulseMarkerRef = useRef<maplibregl.Marker | null>(null);
  const signalPulseTimeoutRef = useRef<number | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Clean up any in-flight pulse first — cancels animation, drops marker.
    if (signalPulseTimeoutRef.current != null) {
      window.clearTimeout(signalPulseTimeoutRef.current);
      signalPulseTimeoutRef.current = null;
    }
    if (signalPulseMarkerRef.current) {
      signalPulseMarkerRef.current.remove();
      signalPulseMarkerRef.current = null;
    }
    if (!signalPulse) return;

    const strength = Math.max(0, Math.min(1, signalPulse.strength));
    const pct = Math.round(strength * 100);
    // Dark-signal case: the final strength is below the game's minimum
    // signal threshold, which is exactly when generateMovementSignal()
    // returns null and the enemy receives zero blip. This is the only
    // case where "SIGNAL DARK" is correct — any non-dark strength means
    // the enemy did get something, even if it's weak.
    const isDark = strength < tuning.minSignalThreshold;
    // Color ramp: cyan (dark) → green → yellow → red as signal gets louder.
    const color = isDark
      ? '#66ddee'
      : strength < 0.33 ? '#44cc88'
      : strength < 0.66 ? '#f0c040'
      : '#ff5050';
    const centerLabel = isDark ? 'DARK' : `${pct}%`;
    const bottomLabel = isDark ? 'SIGNAL DARK' : `SIGNAL ${pct}%`;
    const centerFontSize = isDark ? 10 : 13;

    const size = 54;
    const radius = 22;
    const strokeWidth = 4;
    const circumference = 2 * Math.PI * radius;
    // In the dark case, show a very thin ring outline (no fill) so the
    // ring reads as "zero signal" visually without being invisible.
    const dashOffset = circumference * (1 - Math.max(strength, 0));

    // Build the marker element. IMPORTANT: maplibregl.Marker applies its own
    // `transform: translate(...)` to the root element we pass in to position
    // it on the map. If we put our own transform or keyframes animation on
    // that root, it clobbers maplibregl's positioning and the marker snaps
    // to (0, 0). So we leave the root untouched and put the fade/scale
    // animation on an INNER wrapper. The marker's offset option is used for
    // the on-screen displacement from the player point.
    const el = document.createElement('div');
    el.className = 'signal-pulse-overlay';
    el.style.pointerEvents = 'none';
    const inner = document.createElement('div');
    inner.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
      opacity: 0;
      transform-origin: center bottom;
      animation: signalPulseFade 2000ms ease-out forwards;
    `;
    // In the dark case, render a dashed empty ring so the ring reads as
    // "zero / silenced" without being invisible.
    const ringStrokeDasharray = isDark
      ? `2 4`
      : `${circumference}`;
    const ringStrokeOpacity = isDark ? 0.5 : 1;
    inner.innerHTML = `
      <div style="position: relative; width: ${size}px; height: ${size}px;">
        <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" style="display: block;">
          <circle cx="${size / 2}" cy="${size / 2}" r="${radius}" fill="rgba(8, 12, 20, 0.75)" stroke="rgba(255,255,255,0.15)" stroke-width="1" />
          <circle
            cx="${size / 2}"
            cy="${size / 2}"
            r="${radius}"
            fill="none"
            stroke="${color}"
            stroke-width="${strokeWidth}"
            stroke-linecap="round"
            stroke-dasharray="${ringStrokeDasharray}"
            stroke-dashoffset="${dashOffset}"
            stroke-opacity="${ringStrokeOpacity}"
            transform="rotate(-90 ${size / 2} ${size / 2})"
          />
          <text
            x="${size / 2}"
            y="${size / 2 + (isDark ? 4 : 5)}"
            text-anchor="middle"
            font-family="ui-monospace, monospace"
            font-size="${centerFontSize}"
            font-weight="700"
            fill="${color}"
          >${centerLabel}</text>
        </svg>
      </div>
      <div style="
        font-family: ui-monospace, monospace;
        font-size: 10px;
        letter-spacing: 1px;
        color: ${color};
        background: rgba(8, 12, 20, 0.85);
        border: 1px solid ${color}55;
        border-radius: 3px;
        padding: 2px 6px;
        white-space: nowrap;
        text-shadow: 0 1px 2px rgba(0,0,0,0.8);
      ">${bottomLabel}</div>
      ${signalPulse.exposureGain && signalPulse.exposureGain > 0 ? `
        <div style="
          font-family: ui-monospace, monospace;
          font-size: 9px;
          letter-spacing: 0.5px;
          color: ${signalPulse.exposureGain < 20 ? '#44cc88' : signalPulse.exposureGain < 60 ? '#ccaa44' : '#cc4444'};
          background: rgba(8, 12, 20, 0.85);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 3px;
          padding: 1px 5px;
          white-space: nowrap;
          text-shadow: 0 1px 2px rgba(0,0,0,0.8);
        ">+${Math.round(signalPulse.exposureGain)} EXP</div>
      ` : ''}
    `;
    el.appendChild(inner);

    // Inject the keyframes once on first use. Animation targets only opacity
    // + scale (no translate), so it doesn't fight maplibregl's positioning.
    if (!document.getElementById('signal-pulse-keyframes')) {
      const style = document.createElement('style');
      style.id = 'signal-pulse-keyframes';
      style.textContent = `
        @keyframes signalPulseFade {
          0%   { opacity: 0; transform: scale(0.7); }
          12%  { opacity: 1; transform: scale(1.1); }
          22%  { opacity: 1; transform: scale(1.0); }
          75%  { opacity: 1; transform: scale(1.0); }
          100% { opacity: 0; transform: scale(0.95); }
        }
      `;
      document.head.appendChild(style);
    }

    // offset=[40, -48] places the ring up-and-right of the player marker so
    // it doesn't cover the dot, with 'center' anchor so the offset is simply
    // applied from the point.
    const marker = new maplibregl.Marker({ element: el, anchor: 'center', offset: [40, -48] })
      .setLngLat([signalPulse.lng, signalPulse.lat])
      .addTo(map);
    signalPulseMarkerRef.current = marker;
    signalPulseTimeoutRef.current = window.setTimeout(() => {
      marker.remove();
      if (signalPulseMarkerRef.current === marker) {
        signalPulseMarkerRef.current = null;
      }
      signalPulseTimeoutRef.current = null;
    }, 2000);

    return () => {
      // Effect cleanup when pulse changes or component unmounts — the next
      // effect run handles the replacement (see top of effect body).
    };
  }, [signalPulse]);

  // --- Persistent misdirect path: render gameState.player.misdirectPath as
  //     a yellow dashed line. Source + layer are pre-created in
  //     addDataLayers (same pattern as Doppelgänger's path, which worked);
  //     this effect just writes the current coords to the source. ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const src = map.getSource('misdirect-path') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    const misdirectPath = gameState?.player.misdirectPath;
    if (misdirectPath && misdirectPath.length >= 2) {
      src.setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: misdirectPath },
        }],
      });
    } else {
      src.setData({ type: 'FeatureCollection', features: [] });
      // Also clear the preview source when the persistent path clears
      // (end of turn) so the next placement starts from a clean slate.
      const previewSrc = map.getSource('misdirect-preview-path') as maplibregl.GeoJSONSource | undefined;
      if (previewSrc) previewSrc.setData({ type: 'FeatureCollection', features: [] });
    }
  }, [gameState?.player.misdirectPath]);

  // --- Persistent Stage Exit path: render gameState.player.stageExitPath
  //     as a yellow line along the faked transit ride. Source + glow/line
  //     layers are pre-created in addDataLayers (same pattern as Misdirect
  //     and Doppelgänger); this effect just writes the current coords. ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const src = map.getSource('stage-exit-path') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    const stagePath = gameState?.player.stageExitPath;
    if (stagePath && stagePath.length >= 2) {
      src.setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: stagePath },
        }],
      });
    } else {
      src.setData({ type: 'FeatureCollection', features: [] });
    }
  }, [gameState?.player.stageExitPath]);

  // --- Persistent jammer zone: a translucent blue disk at the placement
  //     point while the jammer is active (jammerTurnsLeft > 0). Source +
  //     fill/outline layers are pre-created in addDataLayers; this effect
  //     just writes the circle polygon. Cleared when the timer reaches 0.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const src = map.getSource('jammer-zone') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    const pos = gameState?.player.jammerPosition;
    const radius = gameState?.player.jammerRadius ?? 0;
    const turnsLeft = gameState?.player.jammerTurnsLeft ?? 0;
    if (!pos || radius <= 0 || turnsLeft <= 0) {
      src.setData({ type: 'FeatureCollection', features: [] });
      return;
    }
    // Build a 64-segment circle polygon around the placement point.
    // Same lat/lng approximation the spotter/van zones use — fine at city
    // scale and produces a visually-round disk.
    const segments = 64;
    const coords: [number, number][] = [];
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * 2 * Math.PI;
      const dLat = (radius * Math.cos(angle)) / 111000;
      const dLng = (radius * Math.sin(angle)) / (111000 * Math.cos((pos.lat * Math.PI) / 180));
      coords.push([pos.lng + dLng, pos.lat + dLat]);
    }
    src.setData({
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        properties: { turnsLeft },
        geometry: { type: 'Polygon', coordinates: [coords] },
      }],
    });
  }, [
    gameState?.player.jammerPosition?.lat,
    gameState?.player.jammerPosition?.lng,
    gameState?.player.jammerRadius,
    gameState?.player.jammerTurnsLeft,
  ]);

  // --- Traffic Analysis highlight: render every edge in the full named
  // road the agent is on, as a glowing purple line. Source/layers are
  // pre-created in addDataLayers, so this effect just calls setData. ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;
    const src = map.getSource('traffic-analysis-road') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    const edges = gameState.player.trafficAnalysisEdges;
    if (!edges || edges.length === 0) {
      src.setData({ type: 'FeatureCollection', features: [] });
      return;
    }
    const features: GeoJSON.Feature[] = edges.map((coords, i) => ({
      type: 'Feature' as const,
      properties: { idx: i },
      geometry: { type: 'LineString' as const, coordinates: coords },
    }));
    src.setData({ type: 'FeatureCollection', features });
  }, [gameState?.player.trafficAnalysisEdges]);

  // --- Persistent taxi path: render remaining taxi route while in a taxi ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const TAXI_ROUTE_SOURCE = 'taxi-route';
    const TAXI_ROUTE_LAYER = 'taxi-route-line';

    const update = () => {
      if (!map.getSource(TAXI_ROUTE_SOURCE)) {
        map.addSource(TAXI_ROUTE_SOURCE, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
      }
      if (!map.getLayer(TAXI_ROUTE_LAYER)) {
        map.addLayer({
          id: TAXI_ROUTE_LAYER,
          type: 'line',
          source: TAXI_ROUTE_SOURCE,
          paint: {
            'line-color': '#ffcc33',
            'line-width': 3,
            'line-opacity': 0.75,
          },
        });
      }

      const src = map.getSource(TAXI_ROUTE_SOURCE) as maplibregl.GeoJSONSource;
      if (!src) return;

      const path = gameState?.player.taxiRemainingPath;
      const inTaxi = gameState?.player.isInTaxi ?? false;
      if (inTaxi && path && path.coordinates.length >= 2) {
        src.setData({
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            properties: {},
            geometry: { type: 'LineString', coordinates: path.coordinates },
          }],
        });
      } else {
        src.setData({ type: 'FeatureCollection', features: [] });
      }
    };

    if (map.isStyleLoaded()) {
      update();
    } else {
      map.once('load', update);
    }
  }, [gameState?.player.isInTaxi, gameState?.player.taxiRemainingPath]);

  // --- Wire Tap mode: click a street to place a tap ---
  const onWireTapPlaceRef = useRef(onWireTapPlace);
  onWireTapPlaceRef.current = onWireTapPlace;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !wireTapMode) return;
    const canvas = map.getCanvasContainer();
    canvas.style.cursor = 'crosshair';

    // Add preview source/layer for wire tap placement
    const PREVIEW_SOURCE = 'wiretap-preview';
    const PREVIEW_LAYER = 'wiretap-preview-line';
    if (!map.getSource(PREVIEW_SOURCE)) {
      map.addSource(PREVIEW_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }
    if (!map.getLayer(PREVIEW_LAYER)) {
      map.addLayer({
        id: PREVIEW_LAYER,
        type: 'line',
        source: PREVIEW_SOURCE,
        paint: {
          'line-color': '#ffdd44',
          'line-width': 4,
          'line-opacity': 0.6,
          'line-dasharray': [3, 2],
        },
      });
    }

    const WIRE_TAP_RANGE = 150;
    const navNodes = cityData.navigation.nodes;
    const nodeMapLocal = new Map(navNodes.map(n => [n.id, n]));

    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      const { lat, lng } = e.lngLat;
      const edges = cityData.navigation.edges;
      const src = map.getSource(PREVIEW_SOURCE) as maplibregl.GeoJSONSource;
      if (!src) return;

      // Find nearest edge to cursor
      let bestEdge: typeof edges[0] | null = null;
      let bestDist = Infinity;
      for (const edge of edges) {
        if (!edge.geometry || edge.geometry.length < 2) continue;
        for (let i = 0; i < edge.geometry.length - 1; i++) {
          const [lng1, lat1] = edge.geometry[i];
          const [lng2, lat2] = edge.geometry[i + 1];
          const midLat = (lat1 + lat2) / 2;
          const midLng = (lng1 + lng2) / 2;
          const dLat2 = (midLat - lat) * 111000;
          const dLng2 = (midLng - lng) * 111000 * Math.cos((lat * Math.PI) / 180);
          const d = Math.sqrt(dLat2 * dLat2 + dLng2 * dLng2);
          if (d < bestDist && d < 80) { bestDist = d; bestEdge = edge; }
        }
      }
      if (!bestEdge) {
        src.setData({ type: 'FeatureCollection', features: [] });
        return;
      }

      // Walk along connected edges 150m each direction, staying on the
      // SAME named road (shared roadId) whenever possible. This mirrors
      // handleWireTapPlace exactly so the hover preview shows the real
      // segment that will be placed — not a different "straight-line
      // through intersections" shape that could leap onto another street.
      const targetRoadId = bestEdge.roadId;
      const collectEdges = (startNodeId: string, maxDist: number): typeof edges[0][] => {
        const result: typeof edges[0][] = [];
        let currentNodeId = startNodeId;
        let remaining = maxDist;
        const visitedEdges = new Set<string>();
        const visitedNodes = new Set<string>();
        while (remaining > 0) {
          visitedNodes.add(currentNodeId);
          let candidates = edges.filter(ed =>
            !visitedEdges.has(ed.id) && ed.classification !== 'footpath' &&
            (ed.from === currentNodeId || ed.to === currentNodeId)
          );
          // Same-road filter: prefer edges sharing the starting edge's
          // roadId. If none exist, stop (named-road chain has ended).
          if (targetRoadId) {
            const sameRoad = candidates.filter(ed => ed.roadId === targetRoadId);
            if (sameRoad.length > 0) candidates = sameRoad;
            else break;
          }
          if (candidates.length === 0) break;
          let next = candidates[0];
          if (candidates.length > 1 && result.length > 0) {
            // Y-fork disambiguation: pick the most-aligned continuation
            // so we follow the visual "through-line" of the road.
            const prevNodeId = result[result.length - 1].from === currentNodeId
              ? result[result.length - 1].to
              : result[result.length - 1].from;
            const prevN = nodeMapLocal.get(prevNodeId);
            const curN = nodeMapLocal.get(currentNodeId);
            if (prevN && curN) {
              const dirLat = curN.lat - prevN.lat;
              const dirLng = curN.lng - prevN.lng;
              let bestDot = -Infinity;
              for (const c of candidates) {
                const otherNodeId = c.from === currentNodeId ? c.to : c.from;
                if (visitedNodes.has(otherNodeId)) continue;
                const otherN = nodeMapLocal.get(otherNodeId);
                if (!otherN) continue;
                const dot = dirLat * (otherN.lat - curN.lat) + dirLng * (otherN.lng - curN.lng);
                if (dot > bestDot) { bestDot = dot; next = c; }
              }
            }
          }
          const nextNodeId = next.from === currentNodeId ? next.to : next.from;
          if (visitedNodes.has(nextNodeId)) break;
          visitedEdges.add(next.id);
          result.push(next);
          remaining -= next.length;
          currentNodeId = nextNodeId;
        }
        return result;
      };

      const edgesForward = collectEdges(bestEdge.to, WIRE_TAP_RANGE - bestEdge.length / 2);
      const edgesBackward = collectEdges(bestEdge.from, WIRE_TAP_RANGE - bestEdge.length / 2);
      const allEdges = [bestEdge, ...edgesForward, ...edgesBackward];
      const seen = new Set<string>();
      const unique = allEdges.filter(ed => { if (seen.has(ed.id)) return false; seen.add(ed.id); return true; });

      const features: GeoJSON.Feature[] = unique
        .filter(ed => ed.geometry && ed.geometry.length >= 2)
        .map(ed => ({
          type: 'Feature' as const,
          properties: {},
          geometry: { type: 'LineString' as const, coordinates: ed.geometry },
        }));
      src.setData({ type: 'FeatureCollection', features });
    };

    const onClick = (e: maplibregl.MapMouseEvent) => {
      if (onWireTapPlaceRef.current) onWireTapPlaceRef.current(e.lngLat.lat, e.lngLat.lng);
    };
    map.on('mousemove', onMouseMove);
    map.on('click', onClick);
    return () => {
      map.off('mousemove', onMouseMove);
      map.off('click', onClick);
      // Clear the hover preview — placed taps live in their own source
      // (`wiretap-placed`) so this is safe and won't touch them.
      const previewSrc = map.getSource(PREVIEW_SOURCE) as maplibregl.GeoJSONSource | undefined;
      if (previewSrc) previewSrc.setData({ type: 'FeatureCollection', features: [] });
      canvas.style.cursor = CROSSHAIR_CURSOR;
    };
  }, [wireTapMode, cityData.navigation.nodes, cityData.navigation.edges]);

  // --- Teleport mode (test only): click anywhere to snap player to nearest road node ---
  const onTeleportRef = useRef(onTeleport);
  onTeleportRef.current = onTeleport;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !teleportMode) return;

    const canvas = map.getCanvasContainer();
    const prevCursor = canvas.style.cursor;
    canvas.style.cursor = MOVE_CURSOR;

    const onClick = (e: maplibregl.MapMouseEvent) => {
      if (onTeleportRef.current) {
        onTeleportRef.current(e.lngLat.lat, e.lngLat.lng);
      }
    };
    map.on('click', onClick);
    return () => {
      map.off('click', onClick);
      canvas.style.cursor = prevCursor;
    };
  }, [teleportMode]);

  // --- Hide Inside / Rooftop Access mode: highlight buildings within range, click to select ---
  const hideModeRef = useRef(false);
  hideModeRef.current = !!hideMode || !!rooftopMode;
  const onHideSelectRef = useRef(onHideSelect);
  onHideSelectRef.current = onHideSelect;

  useEffect(() => {
    const map = mapRef.current;
    const buildingSelectActive = hideMode || rooftopMode;
    if (!map || !buildingSelectActive || !gameState) return;

    const playerPos = gameState.player.position;
    const HIDE_RANGE = tuning.hideRange;
    const modeLabel = rooftopMode ? 'rooftopMode' : 'hideMode';

    // We track highlighted building IDs in a Set for fast lookup
    const highlightedIds = new Set<number>();
    let setupDone = false;
    let hoveredId: number | undefined;
    let cancelled = false;

    // For rooftop mode, import zone lookup to filter commercial buildings
    const zonesData = rooftopMode ? cityData.layers.zones : null;

    const setup = () => {
      if (setupDone || cancelled) return;

      // Wait for the buildings source and layer to be ready
      if (!map.getSource('buildings') || !map.getLayer('buildings-fill')) {
        console.log(`[${modeLabel}] Waiting for buildings layer...`);
        setTimeout(setup, 100);
        return;
      }

      setupDone = true;

      const data = cityData.layers.buildings;
      if (!data || !data.features) {
        console.warn(`[${modeLabel}] No building data in cityData`);
        return;
      }

      console.log(`[${modeLabel}] Scanning ${data.features.length} buildings for ones within ${HIDE_RANGE}m...`);
      const latRad = (playerPos.lat * Math.PI) / 180;

      // Pre-compute zone polygons for rooftop mode filtering
      const zonePoly = rooftopMode && zonesData ? buildZoneLookup(zonesData) : null;

      for (let i = 0; i < data.features.length; i++) {
        const feature = data.features[i];
        const geom = feature.geometry;
        if (!geom) continue;

        // Check distance to the NEAREST vertex of the polygon. Using a single
        // corner (previous behavior) skipped large commercial buildings whose
        // first vertex happened to be >50m away even though the rest was
        // right next to the player. This is O(n) per building but n is
        // small (typical building has ~10 vertices).
        let ring: [number, number][] | null = null;
        if (geom.type === 'Polygon') {
          ring = geom.coordinates[0] as [number, number][];
        } else if (geom.type === 'MultiPolygon') {
          ring = geom.coordinates[0][0] as [number, number][];
        }
        if (!ring || ring.length === 0) continue;

        let dist = Infinity;
        let checkLng = ring[0][0], checkLat = ring[0][1];
        for (const [vLng, vLat] of ring) {
          const dLat = (vLat - playerPos.lat) * 111000;
          const dLng = (vLng - playerPos.lng) * 111000 * Math.cos(latRad);
          const d = Math.sqrt(dLat * dLat + dLng * dLng);
          if (d < dist) { dist = d; checkLng = vLng; checkLat = vLat; }
        }

        if (dist <= HIDE_RANGE) {
          const buildingType = (feature.properties as any)?.type || 'yes';
          const COMMERCIAL_TYPES = ['commercial', 'retail', 'office', 'industrial', 'warehouse', 'public', 'civic', 'government', 'church', 'cathedral', 'chapel', 'mosque', 'synagogue', 'temple', 'hospital', 'hotel', 'school', 'university', 'train_station', 'supermarket', 'mall', 'basilica', 'service', 'parking', 'transportation', 'stadium', 'sports_centre', 'museum', 'library', 'theatre', 'cinema', 'fire_station', 'police', 'post_office', 'bank', 'marketplace'];
          let isCommercial = COMMERCIAL_TYPES.includes(buildingType);

          // Zone fallback: if the building isn't explicitly residential but
          // sits in a commercial/mixed-use/civic/transit zone, treat it as
          // commercial. Covers 'yes' tags AND the many cases where OSM
          // mistags a shop/office as a generic building.
          const RESIDENTIAL_TYPES = ['residential', 'house', 'apartments', 'detached', 'semidetached_house', 'terrace', 'bungalow', 'cabin', 'dormitory'];
          const isResidential = RESIDENTIAL_TYPES.includes(buildingType);
          if (!isCommercial && !isResidential && zonePoly) {
            const zone = zoneAtPoint(checkLng, checkLat, zonePoly);
            if (zone === 'commercial' || zone === 'mixed-use' || zone === 'civic' || zone === 'transit') isCommercial = true;
          }

          if (rooftopMode) {
            // Rooftop: only commercial buildings — unless Free Runner is active,
            // in which case any building is climbable.
            const freeRunner = !!gameState?.player.freeRunnerActive;
            if (!isCommercial && !freeRunner) continue;
            highlightedIds.add(i);
            map.setFeatureState({ source: 'buildings', id: i }, { rooftopAvailable: true });
          } else {
            // Hide Inside: green for commercial (free), yellow for residential with lockpick, skip residential without
            highlightedIds.add(i);
            if (isCommercial) {
              map.setFeatureState({ source: 'buildings', id: i }, { hideAvailable: true });
            } else {
              // Residential — check if player has lockpick
              const hasLockpick = gameState?.player.items.some(it => it.name === 'Lock Pick Set');
              if (hasLockpick) {
                map.setFeatureState({ source: 'buildings', id: i }, { hideLocked: true });
              } else {
                // No lockpick — don't highlight (can't enter)
                highlightedIds.delete(i);
              }
            }
          }
        }
      }

      console.log(`[${modeLabel}] Found ${highlightedIds.size} buildings within ${HIDE_RANGE}m`);

      // Zoom in if needed so buildings are visible
      if (map.getZoom() < 15) {
        map.flyTo({ center: [playerPos.lng, playerPos.lat], zoom: 16, duration: 500 });
      }
    };

    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      if (!setupDone) return;

      const point = e.point;
      const bbox: [maplibregl.PointLike, maplibregl.PointLike] = [
        [point.x - 8, point.y - 8],
        [point.x + 8, point.y + 8],
      ];

      const buildingFeatures = map.getLayer('buildings-fill')
        ? map.queryRenderedFeatures(bbox, { layers: ['buildings-fill'] })
        : [];

      // Clear previous hover
      if (hoveredId !== undefined) {
        map.setFeatureState({ source: 'buildings', id: hoveredId }, { hideHover: false });
        hoveredId = undefined;
      }

      if (buildingFeatures.length > 0) {
        const feature = buildingFeatures[0];
        // Ensure numeric ID — queryRenderedFeatures can return string or number
        const rawId = feature.id;
        const id = typeof rawId === 'string' ? parseInt(rawId, 10) : rawId as number;
        if (!isNaN(id) && highlightedIds.has(id)) {
          hoveredId = id;
          map.setFeatureState({ source: 'buildings', id }, { hideHover: true });
          map.getCanvasContainer().style.cursor = 'pointer';

          // Show building info tooltip with levels
          const levels = feature.properties?.levels;
          const height = feature.properties?.height;
          const buildingType = feature.properties?.type;
          let tooltip = '';
          if (levels != null && levels > 0) {
            tooltip = `${levels} floor${levels !== 1 ? 's' : ''}`;
          } else if (height != null && height > 0) {
            const estFloors = Math.max(1, Math.round(height / 3.5));
            tooltip = `~${estFloors} floor${estFloors !== 1 ? 's' : ''} (${height}m)`;
          } else {
            tooltip = 'Unknown height';
          }
          if (buildingType && buildingType !== 'yes') {
            tooltip += ` · ${buildingType}`;
          }

          // Remove existing tooltip popup if any
          if (!(map as any)._buildingTooltip) {
            (map as any)._buildingTooltip = new maplibregl.Popup({
              closeButton: false,
              closeOnClick: false,
              className: 'building-tooltip',
              offset: [0, -10],
            });
          }
          (map as any)._buildingTooltip
            .setLngLat(e.lngLat)
            .setHTML(`<div style="font-family:monospace;font-size:11px;color:#000;background:rgba(255,255,255,0.85);letter-spacing:1px;padding:2px 6px;border-radius:3px">${tooltip}</div>`)
            .addTo(map);
        } else {
          map.getCanvasContainer().style.cursor = 'not-allowed';
          if ((map as any)._buildingTooltip) (map as any)._buildingTooltip.remove();
        }
      } else {
        map.getCanvasContainer().style.cursor = 'default';
        if ((map as any)._buildingTooltip) (map as any)._buildingTooltip.remove();
      }
    };

    const onClick = (e: maplibregl.MapMouseEvent) => {
      if (!setupDone) return;

      const point = e.point;
      const bbox: [maplibregl.PointLike, maplibregl.PointLike] = [
        [point.x - 8, point.y - 8],
        [point.x + 8, point.y + 8],
      ];

      const buildingFeatures = map.getLayer('buildings-fill')
        ? map.queryRenderedFeatures(bbox, { layers: ['buildings-fill'] })
        : [];

      if (buildingFeatures.length > 0) {
        const feature = buildingFeatures[0];
        const rawId = feature.id;
        const id = typeof rawId === 'string' ? parseInt(rawId, 10) : rawId as number;
        if (!isNaN(id) && highlightedIds.has(id) && onHideSelectRef.current) {
          console.log(`[${modeLabel}] Selected building ${id}`);
          // Compute building centroid from polygon geometry
          let buildingInfo: { centroid: { lat: number; lng: number }; levels: number | null } | undefined;
          if (feature.geometry.type === 'Polygon') {
            const ring = (feature.geometry as GeoJSON.Polygon).coordinates[0];
            let sumLng = 0, sumLat = 0;
            // exclude closing point (last == first)
            const n = ring.length > 1 ? ring.length - 1 : ring.length;
            for (let i = 0; i < n; i++) {
              sumLng += ring[i][0];
              sumLat += ring[i][1];
            }
            buildingInfo = {
              centroid: { lat: sumLat / n, lng: sumLng / n },
              levels: feature.properties?.levels != null ? Number(feature.properties.levels) : null,
            };
          }
          onHideSelectRef.current(id, buildingInfo);
        }
      }
    };

    // Start setup immediately — will retry if source not ready yet
    setup();

    map.on('mousemove', onMouseMove);
    map.on('click', onClick);

    return () => {
      cancelled = true;
      map.off('mousemove', onMouseMove);
      map.off('click', onClick);

      // Clear all highlight states in bulk — removeFeatureState on the whole source
      // is O(1) instead of O(n) individual setFeatureState calls
      if (map.getSource('buildings')) {
        try {
          map.removeFeatureState({ source: 'buildings' });
        } catch {
          // Fallback for older MapLibre versions
          for (const id of highlightedIds) {
            map.setFeatureState({ source: 'buildings', id }, { hideAvailable: false, hideLocked: false, rooftopAvailable: false, hideHover: false });
          }
          if (hoveredId !== undefined) {
            map.setFeatureState({ source: 'buildings', id: hoveredId }, { hideHover: false });
          }
        }
      }

      // Remove building tooltip
      if ((map as any)._buildingTooltip) {
        (map as any)._buildingTooltip.remove();
        delete (map as any)._buildingTooltip;
      }

      // Reset cursor
      if (!movementModeRef.current && !sweepModeRef.current) {
        map.getCanvasContainer().style.cursor = CROSSHAIR_CURSOR;
      }
    };
  }, [!!hideMode, !!rooftopMode, gameState?.player.position.lat, gameState?.player.position.lng, cityData]);

  // Highlight the building the player is hiding in (blue) and clear hide-mode green
  // Highlight the building the player is inside (Hide Inside or Safehouse)
  const highlightedBuildingRef = useRef<number | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    let buildingId = gameState?.player.hidingBuildingId ?? null;

    // For safehouses: query the map at the safehouse's building centroid
    // (not the player's snapped street position) to find the building polygon.
    if (gameState?.player.isInSafehouse && gameState?.player.safehousePosition && map.getLayer('buildings-fill')) {
      const shPos = gameState.player.safehousePosition;
      const point = map.project([shPos.lng, shPos.lat]);
      const bbox: [maplibregl.PointLike, maplibregl.PointLike] = [
        [point.x - 30, point.y - 30],
        [point.x + 30, point.y + 30],
      ];
      const features = map.queryRenderedFeatures(bbox, { layers: ['buildings-fill'] });
      if (features.length > 0 && features[0].id != null) {
        buildingId = typeof features[0].id === 'string' ? parseInt(features[0].id, 10) : features[0].id as number;
      }
    }

    // Clear previous highlight
    const prevId = highlightedBuildingRef.current;
    if (prevId !== null && prevId !== buildingId && map.getSource('buildings')) {
      map.setFeatureState({ source: 'buildings', id: prevId }, {
        playerInside: false,
        hideAvailable: false,
        hideHover: false,
      });
    }

    // Apply new highlight
    if (buildingId !== null && !isNaN(buildingId) && map.getSource('buildings')) {
      map.setFeatureState({ source: 'buildings', id: buildingId }, {
        playerInside: true,
        hideAvailable: false,
        hideHover: false,
      });
      highlightedBuildingRef.current = buildingId;
    } else {
      highlightedBuildingRef.current = null;
    }

    return () => {
      const id = highlightedBuildingRef.current;
      if (id !== null && map.getSource('buildings')) {
        map.setFeatureState({ source: 'buildings', id }, {
          playerInside: false,
          hideAvailable: false,
          hideHover: false,
        });
        highlightedBuildingRef.current = null;
      }
    };
  }, [gameState?.player.hidingBuildingId, gameState?.player.isInSafehouse, gameState?.player.isInBuilding, gameState?.player.safehousePosition]);

  // Toggle player marker between blue dot, car icon, rooftop icon, or hidden
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const hiding = gameState?.player.isInBuilding ?? false;
    const inVehicle = gameState?.player.isInVehicle ?? false;
    const inTaxi = gameState?.player.isInTaxi ?? false;
    const isElevated = gameState?.player.isElevated ?? false;
    const inTransit = gameState?.player.isInTransit ?? false;
    const transitLineId = gameState?.player.transitLineId ?? null;
    // Determine which transit layer should be visible (if any)
    let activeTransitLayer: string | null = null;
    if (inTransit && transitLineId) {
      const line = cityData.layers.transit.lines.find(l => l.id === transitLineId);
      if (line) activeTransitLayer = `player-${line.type}`;
    }
    const transitLayers = ['player-subway', 'player-tram', 'player-bus', 'player-ferry'];

    // Hide all transit layers by default
    for (const lid of transitLayers) {
      if (map.getLayer(lid)) map.setLayoutProperty(lid, 'visibility', 'none');
    }

    // Always start by hiding the taxi layer — each branch can re-enable if needed
    const hideTaxi = () => {
      if (map.getLayer('player-taxi')) map.setLayoutProperty('player-taxi', 'visibility', 'none');
    };

    if (hiding || isElevated) {
      // Hide player dot when in a building OR on a rooftop
      if (map.getLayer('player-dot')) map.setLayoutProperty('player-dot', 'visibility', 'none');
      if (map.getLayer('player-glow')) map.setLayoutProperty('player-glow', 'visibility', 'none');
      if (map.getLayer('player-car')) map.setLayoutProperty('player-car', 'visibility', 'none');
      hideTaxi();
    } else if (inTransit && activeTransitLayer) {
      // Show transit icon, hide circles + car + taxi
      if (map.getLayer('player-dot')) map.setLayoutProperty('player-dot', 'visibility', 'none');
      if (map.getLayer('player-glow')) map.setLayoutProperty('player-glow', 'visibility', 'none');
      if (map.getLayer('player-car')) map.setLayoutProperty('player-car', 'visibility', 'none');
      hideTaxi();
      if (map.getLayer(activeTransitLayer)) map.setLayoutProperty(activeTransitLayer, 'visibility', 'visible');
    } else if (inTaxi) {
      // Show taxi icon, hide circles + car
      if (map.getLayer('player-dot')) map.setLayoutProperty('player-dot', 'visibility', 'none');
      if (map.getLayer('player-glow')) map.setLayoutProperty('player-glow', 'visibility', 'none');
      if (map.getLayer('player-car')) map.setLayoutProperty('player-car', 'visibility', 'none');
      if (map.getLayer('player-taxi')) map.setLayoutProperty('player-taxi', 'visibility', 'visible');
    } else if (inVehicle) {
      // Show car icon, hide circles
      if (map.getLayer('player-dot')) map.setLayoutProperty('player-dot', 'visibility', 'none');
      if (map.getLayer('player-glow')) map.setLayoutProperty('player-glow', 'visibility', 'none');
      if (map.getLayer('player-car')) map.setLayoutProperty('player-car', 'visibility', 'visible');
      hideTaxi();
    } else {
      // Normal — show blue dot, hide car + taxi
      if (map.getLayer('player-dot')) map.setLayoutProperty('player-dot', 'visibility', 'visible');
      if (map.getLayer('player-glow')) map.setLayoutProperty('player-glow', 'visibility', 'visible');
      if (map.getLayer('player-car')) map.setLayoutProperty('player-car', 'visibility', 'none');
      hideTaxi();
    }

    // Rooftop marker: show cyan marker on building when on rooftop
    const rooftopPos = gameState?.player.rooftopPosition;
    if (isElevated && rooftopPos) {
      const rooftopSource = map.getSource('rooftop-marker') as maplibregl.GeoJSONSource;
      if (rooftopSource) {
        rooftopSource.setData({
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            properties: {},
            geometry: { type: 'Point', coordinates: [rooftopPos.lng, rooftopPos.lat] },
          }],
        });
      }
      if (map.getLayer('rooftop-dot')) map.setLayoutProperty('rooftop-dot', 'visibility', 'visible');
      if (map.getLayer('rooftop-glow')) map.setLayoutProperty('rooftop-glow', 'visibility', 'visible');
    } else {
      if (map.getLayer('rooftop-dot')) map.setLayoutProperty('rooftop-dot', 'visibility', 'none');
      if (map.getLayer('rooftop-glow')) map.setLayoutProperty('rooftop-glow', 'visibility', 'none');
    }
  }, [gameState?.player.isInBuilding, gameState?.player.isInVehicle, gameState?.player.isInTaxi, gameState?.player.isElevated, gameState?.player.isInTransit, gameState?.player.transitLineId, gameState?.player.rooftopPosition?.lat, gameState?.player.rooftopPosition?.lng, cityData.layers.transit.lines]);

  // Sync player marker source to gameState position (fixes desync after mid-path detection truncation).
  // While on transit, interpolate along the line's geometry between stops so the icon tracks the route.
  const prevTransitStopIndexRef = useRef<number | null>(null);
  const prevTransitLineIdRef = useRef<string | null>(null);
  const transitAnimRafRef = useRef<number | null>(null);
  // Taxi animation: remember last render's remaining-path coords and in-taxi state so
  // we can compute the slice the taxi just consumed and animate the marker along it.
  const prevTaxiCoordsRef = useRef<[number, number][] | null>(null);
  const prevIsInTaxiRef = useRef<boolean>(false);
  const taxiAnimRafRef = useRef<number | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;
    const source = map.getSource('player-marker') as maplibregl.GeoJSONSource;
    if (!source) return;

    const setMarker = (lng: number, lat: number) => {
      source.setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {},
          geometry: { type: 'Point', coordinates: [lng, lat] },
        }],
      });
    };

    // Update the vision cone and its handle to track the marker during transit animation.
    // Uses the player's current facing/cone params from gameState.
    const setConeAt = (lng: number, lat: number) => {
      if (!gameState) return;
      const p = gameState.player;
      // Vision cone is hidden while hiding or elevated — skip syncing in those cases
      if (p.isInBuilding || p.isElevated) return;
      const coneSource = map.getSource('vision-cone') as maplibregl.GeoJSONSource | undefined;
      const handleSource = map.getSource('vision-handle') as maplibregl.GeoJSONSource | undefined;
      if (!coneSource || !handleSource) return;

      // Match the cone parameters from the main cone effect
      const hasBinoculars = p.items.some(i => i.id.startsWith('binoculars'));
      const hasSniperRifle = p.items.some(i => i.id.startsWith('sniper'));
      const isSniperMode = p.isElevated && hasSniperRifle;
      let effectiveRange = tuning.visionConeRange;
      if (isSniperMode) effectiveRange = tuning.sniperRifleRange;
      else if (p.isElevated) effectiveRange *= hasBinoculars ? 4 : tuning.rooftopRangeMultiplier;
      else if (hasBinoculars) effectiveRange *= 2;
      let effectiveConeWidth = p.visionConeWidth;
      if (p.visionConeWidth < 360 && p.isElevated) {
        if (hasSniperRifle) effectiveConeWidth = hasBinoculars ? tuning.sniperConeAngleWithBinoculars : tuning.sniperConeAngle;
        else effectiveConeWidth = tuning.rooftopConeAngle;
      }

      const coords = createConePolygon(lng, lat, effectiveRange, p.facing, effectiveConeWidth);
      coneSource.setData({
        type: 'FeatureCollection',
        features: [{ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [coords] } }],
      });

      const facingRad = (p.facing * Math.PI) / 180;
      const latRad = (lat * Math.PI) / 180;
      const handleDLat = (effectiveRange * Math.cos(facingRad)) / 111000;
      const handleDLng = (effectiveRange * Math.sin(facingRad)) / (111000 * Math.cos(latRad));
      handleSource.setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: { facing: p.facing },
          geometry: { type: 'Point', coordinates: [lng + handleDLng, lat + handleDLat] },
        }],
      });
    };

    // Cancel any in-flight transit animation
    if (transitAnimRafRef.current != null) {
      cancelAnimationFrame(transitAnimRafRef.current);
      transitAnimRafRef.current = null;
    }
    // Cancel any in-flight taxi animation
    if (taxiAnimRafRef.current != null) {
      cancelAnimationFrame(taxiAnimRafRef.current);
      taxiAnimRafRef.current = null;
    }

    const player = gameState.player;
    const newPos = { lat: player.position.lat, lng: player.position.lng };
    const prevIdx = prevTransitStopIndexRef.current;
    const prevLineId = prevTransitLineIdRef.current;
    const curIdx = player.transitStopIndex;
    const curLineId = player.transitLineId;
    prevTransitStopIndexRef.current = player.isInTransit ? curIdx : null;
    prevTransitLineIdRef.current = player.isInTransit ? curLineId : null;

    // Only animate if: still on same transit line, stop index actually moved, and we have a prior
    if (
      player.isInTransit &&
      curLineId &&
      prevLineId === curLineId &&
      prevIdx !== null &&
      prevIdx !== curIdx
    ) {
      const line = cityData.layers.transit.lines.find(l => l.id === curLineId);
      if (line && line.geometry && line.geometry.length > 0 && prevIdx >= 0 && prevIdx < line.stops.length) {
        // Concatenate all geometry ways into one long polyline
        const full: [number, number][] = [];
        for (const way of line.geometry) {
          for (const pt of way) {
            const last = full[full.length - 1];
            if (!last || last[0] !== pt[0] || last[1] !== pt[1]) full.push(pt);
          }
        }
        // Cumulative distances along the polyline (in meters)
        const distAlong: number[] = [0];
        for (let i = 1; i < full.length; i++) {
          const dx = (full[i][0] - full[i - 1][0]) * 111000 * Math.cos((full[i][1] * Math.PI) / 180);
          const dy = (full[i][1] - full[i - 1][1]) * 111000;
          distAlong.push(distAlong[i - 1] + Math.sqrt(dx * dx + dy * dy));
        }
        // For a stop, find the polyline vertex closest to it and return its distance-along
        const projectStop = (stop: { lat: number; lng: number }): number => {
          let best = 0;
          let bestD = Infinity;
          for (let i = 0; i < full.length; i++) {
            const dx = (full[i][0] - stop.lng) * 111000 * Math.cos((stop.lat * Math.PI) / 180);
            const dy = (full[i][1] - stop.lat) * 111000;
            const d = dx * dx + dy * dy;
            if (d < bestD) { bestD = d; best = i; }
          }
          return distAlong[best];
        };
        const prevStop = line.stops[prevIdx];
        const curStop = line.stops[curIdx];
        const startAlong = projectStop(prevStop);
        const endAlong = projectStop(curStop);
        const forward = endAlong >= startAlong;
        const lo = Math.min(startAlong, endAlong);
        const hi = Math.max(startAlong, endAlong);
        // Build the sub-polyline between the two stops
        const subCoords: [number, number][] = [];
        for (let i = 0; i < full.length; i++) {
          if (distAlong[i] >= lo && distAlong[i] <= hi) subCoords.push(full[i]);
        }
        if (subCoords.length >= 2) {
          const coords = forward ? subCoords : [...subCoords].reverse();
          // Build cumulative distances for the sub-polyline
          const cumDist: number[] = [0];
          for (let i = 1; i < coords.length; i++) {
            const dx = (coords[i][0] - coords[i - 1][0]) * 111000 * Math.cos((coords[i][1] * Math.PI) / 180);
            const dy = (coords[i][1] - coords[i - 1][1]) * 111000;
            cumDist.push(cumDist[i - 1] + Math.sqrt(dx * dx + dy * dy));
          }
          const total = cumDist[cumDist.length - 1];
          if (total > 0) {
            const startTime = performance.now();
            const duration = 700; // ms per stop
            const step = (now: number) => {
              const t = Math.min(1, (now - startTime) / duration);
              // Ease-in-out cubic
              const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
              const target = eased * total;
              // Find segment containing target distance
              let i = 1;
              while (i < cumDist.length && cumDist[i] < target) i++;
              const segStart = cumDist[i - 1];
              const segLen = cumDist[i] - segStart;
              const segT = segLen > 0 ? (target - segStart) / segLen : 0;
              const lng = coords[i - 1][0] + (coords[i][0] - coords[i - 1][0]) * segT;
              const lat = coords[i - 1][1] + (coords[i][1] - coords[i - 1][1]) * segT;
              setMarker(lng, lat);
              setConeAt(lng, lat);
              if (t < 1) {
                transitAnimRafRef.current = requestAnimationFrame(step);
              } else {
                transitAnimRafRef.current = null;
                // Snap to exact final position to stay in sync with gameState
                setMarker(newPos.lng, newPos.lat);
                setConeAt(newPos.lng, newPos.lat);
              }
            };
            // Prime the marker + cone to the start position so the first paint
            // shows them in sync at the previous stop (before the RAF kicks in)
            const startLng = coords[0][0];
            const startLat = coords[0][1];
            setMarker(startLng, startLat);
            setConeAt(startLng, startLat);
            transitAnimRafRef.current = requestAnimationFrame(step);
            return;
          }
        }
      }
    }

    // Taxi animation: if we were in a taxi last render, compute the slice the
    // taxi just consumed (from the previous remaining-path coords up to the
    // new player position) and animate the marker along it.
    const prevTaxiCoords = prevTaxiCoordsRef.current;
    const wasInTaxi = prevIsInTaxiRef.current;
    // Update refs for next render
    prevTaxiCoordsRef.current = player.taxiRemainingPath?.coordinates ?? null;
    prevIsInTaxiRef.current = player.isInTaxi;

    if (wasInTaxi && prevTaxiCoords && prevTaxiCoords.length >= 2) {
      // Find the coord in prevTaxiCoords closest to the current player position —
      // that's where the taxi advanced to. Use squared-meter distance.
      const curLng = newPos.lng;
      const curLat = newPos.lat;
      let bestI = 0;
      let bestD = Infinity;
      for (let i = 0; i < prevTaxiCoords.length; i++) {
        const dLng = (prevTaxiCoords[i][0] - curLng) * 111000 * Math.cos((curLat * Math.PI) / 180);
        const dLat = (prevTaxiCoords[i][1] - curLat) * 111000;
        const d = dLng * dLng + dLat * dLat;
        if (d < bestD) { bestD = d; bestI = i; }
      }

      // Only animate if the taxi actually advanced (bestI > 0). If bestI === 0
      // the player is still at the start of the prev path (e.g. manual exit),
      // so fall through to the default snap.
      if (bestI > 0) {
        // Sub-path: prev coords up through bestI, with the final coord snapped
        // to the exact current player position so the animation ends in sync.
        const sub: [number, number][] = prevTaxiCoords.slice(0, bestI + 1).map(c => [c[0], c[1]]);
        sub[sub.length - 1] = [curLng, curLat];

        // Cumulative distances along sub-path (meters)
        const cumDist: number[] = [0];
        for (let i = 1; i < sub.length; i++) {
          const dLng = (sub[i][0] - sub[i - 1][0]) * 111000 * Math.cos((sub[i][1] * Math.PI) / 180);
          const dLat = (sub[i][1] - sub[i - 1][1]) * 111000;
          cumDist.push(cumDist[i - 1] + Math.sqrt(dLng * dLng + dLat * dLat));
        }
        const total = cumDist[cumDist.length - 1];

        if (total > 10) {
          // ~1ms per meter, clamped — 600m ride ≈ 1.3s
          const duration = Math.min(2500, 700 + total * 1.0);
          const startTime = performance.now();
          const step = (now: number) => {
            const t = Math.min(1, (now - startTime) / duration);
            // Ease-in-out cubic
            const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
            const target = eased * total;
            let i = 1;
            while (i < cumDist.length && cumDist[i] < target) i++;
            if (i >= cumDist.length) i = cumDist.length - 1;
            const segStart = cumDist[i - 1];
            const segLen = cumDist[i] - segStart;
            const segT = segLen > 0 ? (target - segStart) / segLen : 0;
            const lng = sub[i - 1][0] + (sub[i][0] - sub[i - 1][0]) * segT;
            const lat = sub[i - 1][1] + (sub[i][1] - sub[i - 1][1]) * segT;
            setMarker(lng, lat);
            setConeAt(lng, lat);
            if (t < 1) {
              taxiAnimRafRef.current = requestAnimationFrame(step);
            } else {
              taxiAnimRafRef.current = null;
              // Snap to exact final position to stay in sync with gameState
              setMarker(newPos.lng, newPos.lat);
              setConeAt(newPos.lng, newPos.lat);
            }
          };
          // Prime to the start of the sub-path so the first paint is in sync
          setMarker(sub[0][0], sub[0][1]);
          setConeAt(sub[0][0], sub[0][1]);
          taxiAnimRafRef.current = requestAnimationFrame(step);
          return;
        }
      }
    }

    // Default: snap immediately
    setMarker(newPos.lng, newPos.lat);
    setConeAt(newPos.lng, newPos.lat);
  }, [gameState?.player.position.lat, gameState?.player.position.lng, gameState?.player.isInTransit, gameState?.player.transitLineId, gameState?.player.transitStopIndex, gameState?.player.isInTaxi, gameState?.player.taxiRemainingPath, cityData.layers.transit.lines]);

  // Recenter: fly to player position when the trigger counter changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;
    if (recenterTrigger === 0) return; // ignore initial render
    const target = recenterTarget ?? gameState.player.position;

    // Placement-card recenters carry a requested zoom (recenterZoom !== null).
    // The UX goal of that recenter is ONLY to snap the view closer to the
    // player when they're zoomed out too far to comfortably place a path.
    // If the player is already zoomed in tighter than the placement zoom,
    // they can obviously see themselves fine — skip the entire flyTo so
    // we don't yank their framing (even just recentering is disorienting
    // when the player is already focused on a spot they chose themselves).
    //
    // Non-placement recenters (recenterZoom === null — e.g. the manual
    // recenter button, alert banner flyTos) always fire regardless of
    // current zoom.
    if (typeof recenterZoom === 'number' && map.getZoom() >= recenterZoom) {
      return;
    }

    const opts: maplibregl.FlyToOptions = {
      center: [target.lng, target.lat],
      duration: 600,
      essential: true,
    };
    if (typeof recenterZoom === 'number') {
      // Player is zoomed out past the placement zoom — zoom in to it.
      opts.zoom = recenterZoom;
    }
    map.flyTo(opts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recenterTrigger]);

  // Passphrase markers — update source when collected status changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;
    const source = map.getSource('game-passphrases') as maplibregl.GeoJSONSource | undefined;
    if (!source) return;
    const features: GeoJSON.Feature[] = gameState.passphrases
      .filter(p => !p.collected)
      .map(p => ({
        type: 'Feature' as const,
        properties: { id: p.id, word: p.word },
        geometry: { type: 'Point' as const, coordinates: [p.lng, p.lat] },
      }));
    source.setData({ type: 'FeatureCollection', features });
  }, [gameState?.passphrases, mapLayersReady]);

  // Wire Tap lines — source + layer are pre-created in addDataLayers.
  // This effect just rebuilds the features from gameState.wireTaps. The
  // placement-mode hover preview uses a separate source (`wiretap-preview`)
  // so placed taps stay visible while you're hovering to place a new one.
  // Paint is set once at layer creation:
  //   - solid line, width 5, opacity 0.85
  //   - yellow (#ffdd44) while active
  //   - red   (#ff4444) once triggered — persists 3 turns via the
  //     wireTapLifespan filter in the turn-end reducer
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;
    const src = map.getSource('wiretap-placed') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;

    const features: GeoJSON.Feature[] = [];
    for (const tap of gameState.wireTaps) {
      const groups = tap.edgeCoordGroups;
      if (groups && groups.length > 0) {
        for (const coords of groups) {
          if (coords.length >= 2) {
            features.push({
              type: 'Feature',
              properties: { placed: true, triggered: tap.triggered },
              geometry: { type: 'LineString', coordinates: coords },
            });
          }
        }
      } else if (tap.coordinates.length >= 2) {
        features.push({
          type: 'Feature',
          properties: { placed: true, triggered: tap.triggered },
          geometry: { type: 'LineString', coordinates: tap.coordinates },
        });
      }
    }

    src.setData({ type: 'FeatureCollection', features });
  }, [gameState?.wireTaps, mapLayersReady]);

  // Overwatch segment — red dashed line drawn along the 300m stretch of
  // road the player is watching. Source + layer pre-created in addDataLayers.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const src = map.getSource('overwatch-segment') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    if (overwatchSegment && overwatchSegment.length >= 2) {
      src.setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: overwatchSegment },
        }],
      });
    } else {
      src.setData({ type: 'FeatureCollection', features: [] });
    }
  }, [overwatchSegment, mapLayersReady]);

  // Doppelgänger — blue dot at the decoy's current position plus a faint
  // path line showing the full route it will walk. Sources/layers are
  // pre-created in addDataLayers.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const markerSrc = map.getSource('doppelganger-marker') as maplibregl.GeoJSONSource | undefined;
    const pathSrc = map.getSource('doppelganger-path') as maplibregl.GeoJSONSource | undefined;
    if (!markerSrc || !pathSrc) return;
    if (doppelganger) {
      markerSrc.setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {},
          geometry: { type: 'Point', coordinates: [doppelganger.currentPos.lng, doppelganger.currentPos.lat] },
        }],
      });
      if (doppelganger.pathCoords && doppelganger.pathCoords.length >= 2) {
        pathSrc.setData({
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            properties: {},
            geometry: { type: 'LineString', coordinates: doppelganger.pathCoords },
          }],
        });
      } else {
        pathSrc.setData({ type: 'FeatureCollection', features: [] });
      }
    } else {
      markerSrc.setData({ type: 'FeatureCollection', features: [] });
      pathSrc.setData({ type: 'FeatureCollection', features: [] });
    }
  }, [doppelganger, mapLayersReady]);

  // Acoustic Ping rings — distance circles centered on ping positions
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState || !map.isStyleLoaded()) return;
    let source = map.getSource('acoustic-pings') as maplibregl.GeoJSONSource | undefined;
    if (!source) {
      map.addSource('acoustic-pings', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'acoustic-pings-ring',
        type: 'line',
        source: 'acoustic-pings',
        paint: {
          'line-color': '#66ccff',
          'line-width': 2,
          'line-opacity': ['get', 'opacity'],
          'line-dasharray': [4, 4],
        },
      });
      source = map.getSource('acoustic-pings') as maplibregl.GeoJSONSource;
    }
    const features: GeoJSON.Feature[] = gameState.acousticPings.map(ping => {
      const age = gameState.turn - ping.turn;
      const opacity = Math.max(0.15, 1 - age * 0.25);
      const coords = createCirclePolygon(ping.position.lng, ping.position.lat, ping.distanceM);
      return {
        type: 'Feature' as const,
        properties: { id: ping.id, opacity, distance: ping.distanceM },
        geometry: { type: 'LineString' as const, coordinates: coords },
      };
    });
    source.setData({ type: 'FeatureCollection', features });
  }, [gameState?.acousticPings, gameState?.turn, mapLayersReady]);

  // Bearing lines — static snapshots of the direction from the player to
  // the agent at cast time. Each read renders as a line from its origin
  // extending beyond the map bounds in the recorded direction. Older reads
  // fade. Multiple reads intersect visually, enabling triangulation.
  // Source + layers are pre-created in addDataLayers() so this effect just
  // has to call setData.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;
    const source = map.getSource('bearing-lines') as maplibregl.GeoJSONSource | undefined;
    if (!source) return;

    // Compute a length large enough to cross the map from any point.
    // Use 2x the lat/lng diagonal converted to meters — overshoot is fine
    // because maplibre will clip at the viewport edges.
    const b = cityData.bounds;
    const dLatM = (b.north - b.south) * 111000;
    const dLngM = (b.east - b.west) * 111000 * Math.cos(((b.north + b.south) / 2) * Math.PI / 180);
    const rayLength = 2 * Math.sqrt(dLatM * dLatM + dLngM * dLngM);

    const activeBearings = gameState.bearings.filter(br => gameState.turn - br.turn <= 3);
    const features: GeoJSON.Feature[] = activeBearings.map(br => {
      const age = gameState.turn - br.turn;
      // Fade over 3 turns: 1.0 → 0.45 → 0.22 → 0.1
      const opacity = age === 0 ? 1.0 : age === 1 ? 0.45 : age === 2 ? 0.22 : 0.1;
      // Convert bearing to local meters-offset using destination = origin + length * (sinθ, cosθ)
      // where θ is in radians with 0 = north, 90 = east.
      const theta = (br.bearingDeg * Math.PI) / 180;
      const dNorthM = Math.cos(theta) * rayLength;
      const dEastM = Math.sin(theta) * rayLength;
      const latOff = dNorthM / 111000;
      const lngOff = dEastM / (111000 * Math.cos((br.origin.lat * Math.PI) / 180));
      const endLat = br.origin.lat + latOff;
      const endLng = br.origin.lng + lngOff;
      return {
        type: 'Feature' as const,
        properties: { id: br.id, opacity, age },
        geometry: {
          type: 'LineString' as const,
          coordinates: [[br.origin.lng, br.origin.lat], [endLng, endLat]],
        },
      };
    });
    source.setData({ type: 'FeatureCollection', features });
  }, [gameState?.bearings, gameState?.turn, cityData.bounds, mapLayersReady]);

  // Jammer zone — green dashed circle at jammer position
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;
    // Create source/layers on first use (only when style is loaded)
    if (!map.getSource('jammer-zone')) {
      if (!map.isStyleLoaded()) return; // wait for style — effect will re-run on next state change
      map.addSource('jammer-zone', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'jammer-zone-fill',
        type: 'fill',
        source: 'jammer-zone',
        paint: { 'fill-color': '#44cc88', 'fill-opacity': 0.06 },
      });
      map.addLayer({
        id: 'jammer-zone-line',
        type: 'line',
        source: 'jammer-zone',
        paint: { 'line-color': '#44cc88', 'line-width': 1.5, 'line-dasharray': [4, 4], 'line-opacity': 0.5 },
      });
    }
    const src = map.getSource('jammer-zone') as maplibregl.GeoJSONSource;
    if (!src) return;
    if (gameState.player.jammerTurnsLeft > 0 && gameState.player.jammerPosition) {
      const { lat, lng } = gameState.player.jammerPosition;
      const r = gameState.player.jammerRadius;
      const coords: [number, number][] = [];
      for (let i = 0; i <= 64; i++) {
        const angle = (i / 64) * 2 * Math.PI;
        coords.push([lng + (r * Math.sin(angle)) / (111000 * Math.cos(lat * Math.PI / 180)), lat + (r * Math.cos(angle)) / 111000]);
      }
      src.setData({ type: 'FeatureCollection', features: [{ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [coords] } }] });
    } else {
      src.setData({ type: 'FeatureCollection', features: [] });
    }
  }, [gameState?.player.jammerTurnsLeft, gameState?.player.jammerPosition?.lat, gameState?.player.jammerPosition?.lng, mapLayersReady]);

  // Trace Route trail — fading path showing agent's recent movement
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState || !map.isStyleLoaded()) return;
    let source = map.getSource('trace-route') as maplibregl.GeoJSONSource | undefined;
    if (!source) {
      map.addSource('trace-route', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'trace-route-line',
        type: 'line',
        source: 'trace-route',
        paint: {
          'line-color': '#ff8844',
          'line-width': 3,
          'line-opacity': 0.7,
          'line-dasharray': [2, 2],
        },
      });
      map.addLayer({
        id: 'trace-route-dots',
        type: 'circle',
        source: 'trace-route',
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': 5,
          'circle-color': '#ff8844',
          'circle-opacity': ['get', 'opacity'],
          'circle-stroke-width': 1,
          'circle-stroke-color': '#fff',
        },
      });
      source = map.getSource('trace-route') as maplibregl.GeoJSONSource;
    }
    const trail = gameState.traceRouteTrail;
    if (!trail || trail.positions.length === 0) {
      source.setData({ type: 'FeatureCollection', features: [] });
      return;
    }
    const features: GeoJSON.Feature[] = [];
    // Draw the trail as a line — stays on the map indefinitely
    if (trail.positions.length >= 2) {
      features.push({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: trail.positions.map(p => [p.lng, p.lat]),
        },
      });
    }
    // Endpoint marker at the end of the trail
    const lastPos = trail.positions[trail.positions.length - 1];
    features.push({
      type: 'Feature',
      properties: { opacity: 0.8 },
      geometry: { type: 'Point', coordinates: [lastPos.lng, lastPos.lat] },
    });
    source.setData({ type: 'FeatureCollection', features });
  }, [gameState?.traceRouteTrail, gameState?.turn, mapLayersReady]);

  // District Scan — highlight the scanned neighborhood
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState || !map.isStyleLoaded()) return;
    let source = map.getSource('district-scan') as maplibregl.GeoJSONSource | undefined;
    if (!source) {
      map.addSource('district-scan', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'district-scan-fill',
        type: 'fill',
        source: 'district-scan',
        paint: {
          'fill-color': '#aa88ff',
          'fill-opacity': 0.15,
        },
      });
      map.addLayer({
        id: 'district-scan-outline',
        type: 'line',
        source: 'district-scan',
        paint: {
          'line-color': '#aa88ff',
          'line-width': 2,
          'line-opacity': 0.6,
          'line-dasharray': [4, 2],
        },
      });
      map.addLayer({
        id: 'district-scan-label',
        type: 'symbol',
        source: 'district-scan',
        layout: {
          'text-field': ['get', 'name'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 14,
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': '#aa88ff',
          'text-halo-color': 'rgba(0,0,0,0.8)',
          'text-halo-width': 2,
        },
      });
      source = map.getSource('district-scan') as maplibregl.GeoJSONSource;
    }
    const scanName = gameState.districtScanName;
    const scanTurn = gameState.districtScanTurn;
    if (!scanName || scanTurn == null || gameState.turn - scanTurn > 2) {
      source.setData({ type: 'FeatureCollection', features: [] });
      return;
    }
    // Find the neighborhood matching the scan name (polygon or point)
    const features: GeoJSON.Feature[] = [];
    for (const feature of cityData.layers.neighborhoods.features) {
      const name = (feature.properties as Record<string, unknown>)?.name;
      if (name === scanName) {
        if (feature.geometry.type === 'Point') {
          // For point neighborhoods, create a ~500m circle polygon to highlight the area
          const [lng, lat] = feature.geometry.coordinates as [number, number];
          const circleCoords = createCirclePolygon(lng, lat, 500);
          features.push({
            type: 'Feature',
            properties: { name: scanName },
            geometry: { type: 'Polygon', coordinates: [circleCoords] },
          });
        } else {
          features.push({
            ...feature,
            properties: { ...feature.properties, name: scanName },
          });
        }
        break;
      }
    }
    source.setData({ type: 'FeatureCollection', features });
  }, [gameState?.districtScanName, gameState?.districtScanTurn, gameState?.turn, cityData.layers.neighborhoods, mapLayersReady]);

  // Update safehouse markers when visited status changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;
    const source = map.getSource('game-safehouses') as maplibregl.GeoJSONSource | undefined;
    if (!source) return;
    source.setData({
      type: 'FeatureCollection',
      features: gameState.safehouses.filter(sh => !sh.visited).map(sh => ({
        type: 'Feature' as const,
        properties: { id: sh.id },
        geometry: { type: 'Point' as const, coordinates: [sh.lng, sh.lat] },
      })),
    });
  }, [gameState?.safehouses, mapLayersReady]);

  // Transit route highlight — draw full line + stops when player is boarded
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const source = map.getSource('transit-route-highlight') as maplibregl.GeoJSONSource | undefined;
    if (!source) return;
    const player = gameState?.player;
    if (!player || !player.isInTransit || !player.transitLineId) {
      source.setData({ type: 'FeatureCollection', features: [] });
      return;
    }
    const line = cityData.layers.transit.lines.find(l => l.id === player.transitLineId);
    if (!line) {
      source.setData({ type: 'FeatureCollection', features: [] });
      return;
    }
    const features: GeoJSON.Feature[] = [];
    // Line segments
    for (const seg of line.geometry) {
      features.push({
        type: 'Feature',
        properties: { color: line.color },
        geometry: { type: 'LineString', coordinates: seg },
      });
    }
    // Stops
    for (const stop of line.stops) {
      features.push({
        type: 'Feature',
        properties: { color: line.color, name: stop.name },
        geometry: { type: 'Point', coordinates: [stop.lng, stop.lat] },
      });
    }
    source.setData({ type: 'FeatureCollection', features });
  }, [gameState?.player.isInTransit, gameState?.player.transitLineId, cityData.layers.transit.lines]);

  // Vision cone — rendered as a green arc on the map with a draggable handle
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;

    const player = gameState.player;
    const hiding = player.isInBuilding;

    // Vision range: sniper rifle on rooftop = 500m, rooftop gives base 2x, binoculars give 2x (or 4x on rooftop)
    const hasBinoculars = player.items.some(i => i.id.startsWith('binoculars'));
    const hasSniperRifle = player.items.some(i => i.id.startsWith('sniper'));
    const isSniperMode = player.isElevated && hasSniperRifle;
    let effectiveRange = tuning.visionConeRange;
    if (isSniperMode) {
      effectiveRange = tuning.sniperRifleRange;
    } else if (player.isElevated) {
      effectiveRange *= hasBinoculars ? 4 : tuning.rooftopRangeMultiplier;
    } else if (hasBinoculars) {
      effectiveRange *= 2;
    }

    // Cone width:
    //  - Instinct (360°) overrides everything
    //  - Sniper on rooftop: tight cone (90°, or 110° with binoculars)
    //  - Rooftop: wider cone (120°)
    //  - Otherwise: player's current cone width
    let effectiveConeWidth = player.visionConeWidth;
    if (player.visionConeWidth < 360 && player.isElevated) {
      if (hasSniperRifle) {
        effectiveConeWidth = hasBinoculars ? tuning.sniperConeAngleWithBinoculars : tuning.sniperConeAngle;
      } else {
        effectiveConeWidth = tuning.rooftopConeAngle;
      }
    }

    // Update cone geometry
    const updateCone = () => {
      const coneSource = map.getSource('vision-cone') as maplibregl.GeoJSONSource;
      const handleSource = map.getSource('vision-handle') as maplibregl.GeoJSONSource;
      if (!coneSource || !handleSource) return;

      if (hiding) {
        coneSource.setData({ type: 'FeatureCollection', features: [] });
        handleSource.setData({ type: 'FeatureCollection', features: [] });
        return;
      }

      // Use building centroid as cone source when on rooftop
      const coneLng = player.isElevated && player.rooftopPosition ? player.rooftopPosition.lng : player.position.lng;
      const coneLat = player.isElevated && player.rooftopPosition ? player.rooftopPosition.lat : player.position.lat;

      const coords = createConePolygon(
        coneLng, coneLat,
        effectiveRange,
        player.facing,
        effectiveConeWidth,
      );

      coneSource.setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {},
          geometry: { type: 'Polygon', coordinates: [coords] },
        }],
      });

      // Sniper mode: red cone to indicate shoot mode
      const coneColor = isSniperMode ? '#ff4444' : '#44cc66';
      if (map.getLayer('vision-cone-fill')) {
        map.setPaintProperty('vision-cone-fill', 'fill-color', coneColor);
      }
      if (map.getLayer('vision-cone-outline')) {
        map.setPaintProperty('vision-cone-outline', 'line-color', coneColor);
      }

      // Handle sits at the tip of the center line (use rooftop position if elevated)
      const facingRad = (player.facing * Math.PI) / 180;
      const handleBaseLat = coneLat;
      const handleBaseLng = coneLng;
      const latRad = (handleBaseLat * Math.PI) / 180;
      const handleDLat = (effectiveRange * Math.cos(facingRad)) / 111000;
      const handleDLng = (effectiveRange * Math.sin(facingRad)) / (111000 * Math.cos(latRad));

      handleSource.setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: { facing: player.facing },
          geometry: {
            type: 'Point',
            coordinates: [handleBaseLng + handleDLng, handleBaseLat + handleDLat],
          },
        }],
      });
    };

    let cancelled = false;

    if (map.getSource('vision-cone')) {
      updateCone();
    } else {
      // Sources/layers not added yet — wait for them
      const check = () => {
        if (cancelled || !mapRef.current) return;
        if (map.getSource('vision-cone')) updateCone();
        else setTimeout(check, 100);
      };
      check();
    }

    // Also hide/show cone layers based on hiding state
    if (map.getLayer('vision-cone-fill')) {
      map.setLayoutProperty('vision-cone-fill', 'visibility', hiding ? 'none' : 'visible');
      map.setLayoutProperty('vision-cone-outline', 'visibility', hiding ? 'none' : 'visible');
      map.setLayoutProperty('vision-handle-layer', 'visibility', hiding ? 'none' : 'visible');
    }

    return () => { cancelled = true; };
  }, [
    gameState?.player.position.lat, gameState?.player.position.lng,
    gameState?.player.facing, gameState?.player.visionConeWidth,
    gameState?.player.isInBuilding, gameState?.player.isElevated,
    gameState?.player.rooftopPosition?.lat, gameState?.player.rooftopPosition?.lng,
  ]);

  // Agent marker — only visible when player detects the agent via vision cone
  // Ensure source + layers exist, then update data reactively
  const agentMarkerInitRef = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (agentMarkerInitRef.current) return; // already initialized
    const init = () => {
      if (map.getSource('agent-marker')) { agentMarkerInitRef.current = true; return; }
      map.addSource('agent-marker', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'agent-marker-glow',
        type: 'circle',
        source: 'agent-marker',
        paint: {
          'circle-radius': 12,
          // Live agent = red. Dead (extraction-op kill) = grey corpse marker.
          'circle-color': ['case', ['==', ['get', 'dead'], true], '#666666', theme.colors.agentMarker],
          'circle-opacity': ['case', ['==', ['get', 'dead'], true], 0.18, 0.3],
          'circle-blur': 0.8,
        },
      });
      map.addLayer({
        id: 'agent-marker-dot',
        type: 'circle',
        source: 'agent-marker',
        paint: {
          'circle-radius': 6,
          'circle-color': ['case', ['==', ['get', 'dead'], true], '#666666', theme.colors.agentMarker],
          'circle-opacity': ['case', ['==', ['get', 'dead'], true], 0.55, 0.9],
          'circle-stroke-width': 2,
          'circle-stroke-color': ['case', ['==', ['get', 'dead'], true], '#444444', '#ffffff'],
        },
      });
      agentMarkerInitRef.current = true;
    };
    if (map.isStyleLoaded()) init();
    else map.once('load', init);
  }, []);

  // Update agent marker data reactively when visibility or position changes.
  // Uses `dotVisible` (falls back to `agentVisible` when no explicit override)
  // so cone-only reveals like Cold Opening don't render the exact-position
  // marker while still letting the cone give a directional hint.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;
    const source = map.getSource('agent-marker') as maplibregl.GeoJSONSource | undefined;
    if (!source) return;
    try { map.moveLayer('agent-marker-glow'); map.moveLayer('agent-marker-dot'); } catch { /* */ }
    if (dotVisible && gameState.agent) {
      // Use rooftop position when agent is elevated (sniper overwatch)
      const displayPos = gameState.agent.isElevated && gameState.agent.rooftopPosition
        ? gameState.agent.rooftopPosition : gameState.agent.position;
      source.setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          // `dead` drives the data-driven paint expressions on the marker
          // layers — live agent renders red, dead corpse renders grey.
          properties: { dead: !!gameState.agent.isDead },
          geometry: { type: 'Point', coordinates: [displayPos.lng, displayPos.lat] },
        }],
      });
    } else {
      source.setData({ type: 'FeatureCollection', features: [] });
    }
  }, [dotVisible, gameState?.agent?.position.lat, gameState?.agent?.position.lng, gameState?.agent?.rooftopPosition?.lat, gameState?.agent?.rooftopPosition?.lng, gameState?.agent?.isElevated, gameState?.agent?.isDead, mapLayersReady]);

  // --- Agent movement animation — animate agent dot along path when visible ---
  // Gated on dotVisible (not agentVisible) because cone-only reveals should
  // not move the dot — there IS no dot rendered in that case.
  const agentAnimDoneRef = useRef(onAgentAnimationDone);
  agentAnimDoneRef.current = onAgentAnimationDone;
  const lastAnimPathRef = useRef<[number, number][] | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !agentAnimationPath || agentAnimationPath.length < 2 || !dotVisible) {
      if (agentAnimationPath && agentAnimDoneRef.current) agentAnimDoneRef.current();
      return;
    }
    // Don't re-animate the same path (prevents retriggering on state updates during animation)
    if (agentAnimationPath === lastAnimPathRef.current) return;
    lastAnimPathRef.current = agentAnimationPath;

    const source = map.getSource('agent-marker') as maplibregl.GeoJSONSource | undefined;
    if (!source) {
      if (agentAnimDoneRef.current) agentAnimDoneRef.current();
      return;
    }

    const coords = agentAnimationPath;
    // Calculate total distance for timing
    let totalDist = 0;
    for (let i = 0; i < coords.length - 1; i++) {
      const [lng1, lat1] = coords[i];
      const [lng2, lat2] = coords[i + 1];
      const dLat = (lat2 - lat1) * 111000;
      const dLng = (lng2 - lng1) * 111000 * Math.cos(lat1 * Math.PI / 180);
      totalDist += Math.sqrt(dLat * dLat + dLng * dLng);
    }
    // Duration: ~400ms for 300m, min 200ms, max 800ms
    const duration = Math.max(200, Math.min(800, totalDist * 1.3));

    const startTime = performance.now();
    let rafId: number;

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);

      // Find position along path at fraction t
      const targetDist = t * totalDist;
      let accumulated = 0;
      let posLng = coords[0][0];
      let posLat = coords[0][1];
      for (let i = 0; i < coords.length - 1; i++) {
        const [lng1, lat1] = coords[i];
        const [lng2, lat2] = coords[i + 1];
        const dLat = (lat2 - lat1) * 111000;
        const dLng = (lng2 - lng1) * 111000 * Math.cos(lat1 * Math.PI / 180);
        const segDist = Math.sqrt(dLat * dLat + dLng * dLng);
        if (accumulated + segDist >= targetDist) {
          const segT = segDist > 0 ? (targetDist - accumulated) / segDist : 0;
          posLng = lng1 + (lng2 - lng1) * segT;
          posLat = lat1 + (lat2 - lat1) * segT;
          break;
        }
        accumulated += segDist;
        posLng = coords[i + 1][0];
        posLat = coords[i + 1][1];
      }

      source.setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {},
          geometry: { type: 'Point', coordinates: [posLng, posLat] },
        }],
      });

      if (t < 1) {
        rafId = requestAnimationFrame(animate);
      } else {
        lastAnimPathRef.current = null;
        if (agentAnimDoneRef.current) agentAnimDoneRef.current();
      }
    };

    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [agentAnimationPath, dotVisible]);

  // --- Agent belief marker (debug — shown when AI test panel is open) ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;

    const updateBeliefMarker = () => {
      const belief = gameState.agent?.belief;
      const shouldShow = showAgentBelief && belief && belief.confidence > 0.01;

      // --- Belief radius circle (fill) ---
      let radiusSource = map.getSource('agent-belief-radius') as maplibregl.GeoJSONSource;
      if (!radiusSource) {
        map.addSource('agent-belief-radius', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
        map.addLayer({
          id: 'agent-belief-radius-fill',
          type: 'fill',
          source: 'agent-belief-radius',
          paint: {
            'fill-color': '#ff6600',
            'fill-opacity': 0.08,
          },
        });
        map.addLayer({
          id: 'agent-belief-radius-outline',
          type: 'line',
          source: 'agent-belief-radius',
          paint: {
            'line-color': '#ff6600',
            'line-width': 1.5,
            'line-opacity': 0.4,
            'line-dasharray': [4, 4],
          },
        });
        radiusSource = map.getSource('agent-belief-radius') as maplibregl.GeoJSONSource;
      }

      // --- Belief center point ---
      let pointSource = map.getSource('agent-belief-point') as maplibregl.GeoJSONSource;
      if (!pointSource) {
        map.addSource('agent-belief-point', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
        map.addLayer({
          id: 'agent-belief-point-glow',
          type: 'circle',
          source: 'agent-belief-point',
          paint: {
            'circle-radius': 10,
            'circle-color': '#ff6600',
            'circle-opacity': 0.25,
            'circle-blur': 0.6,
          },
        });
        map.addLayer({
          id: 'agent-belief-point-dot',
          type: 'circle',
          source: 'agent-belief-point',
          paint: {
            'circle-radius': 5,
            'circle-color': '#ff6600',
            'circle-opacity': 0.9,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffcc00',
          },
        });
        map.addLayer({
          id: 'agent-belief-point-label',
          type: 'symbol',
          source: 'agent-belief-point',
          layout: {
            'text-field': ['get', 'label'],
            'text-size': 10,
            'text-offset': [0, -1.6],
            'text-font': ['Noto Sans Bold'],
          },
          paint: {
            'text-color': '#ff6600',
            'text-halo-color': '#000000',
            'text-halo-width': 1.5,
          },
        });
        pointSource = map.getSource('agent-belief-point') as maplibregl.GeoJSONSource;
      }

      if (shouldShow && belief) {
        const confPct = Math.round(belief.confidence * 100);
        const radiusMeters = Math.min(belief.radius, 2000); // cap visual radius

        // Update radius circle
        const circleCoords = createCirclePolygon(belief.position.lng, belief.position.lat, radiusMeters);
        radiusSource.setData({
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Polygon',
              coordinates: [circleCoords],
            },
          }],
        });

        // Update center point
        pointSource.setData({
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            properties: { label: `BELIEF ${confPct}%` },
            geometry: {
              type: 'Point',
              coordinates: [belief.position.lng, belief.position.lat],
            },
          }],
        });
      } else {
        radiusSource.setData({ type: 'FeatureCollection', features: [] });
        pointSource.setData({ type: 'FeatureCollection', features: [] });
      }
    };

    if (map.isStyleLoaded()) {
      updateBeliefMarker();
    } else {
      map.once('styledata', updateBeliefMarker);
    }
  }, [showAgentBelief, gameState?.agent?.belief?.position.lat, gameState?.agent?.belief?.position.lng, gameState?.agent?.belief?.confidence, gameState?.agent?.belief?.radius]);

  // --- Agent vision cone (shown when player has visual on the agent) ---
  // Source + layers are pre-created in addDataLayers so this effect just
  // updates the source data whenever visibility or position changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;
    const source = map.getSource('agent-vision-cone') as maplibregl.GeoJSONSource | undefined;
    if (!source) return;

    if (agentVisible && gameState.agent) {
      // Use rooftop position and elevation-aware cone parameters
      const agent = gameState.agent;
      const displayPos = agent.isElevated && agent.rooftopPosition
        ? agent.rooftopPosition : agent.position;
      const hasSniper = agent.equipment.some(i => i.id.startsWith('sniper'));
      let coneRange = tuning.visionConeRange;
      let coneAngle = tuning.visionConeAngle;
      if (agent.isElevated) {
        if (hasSniper) { coneRange = tuning.sniperRifleRange; coneAngle = tuning.sniperConeAngle; }
        else { coneRange *= tuning.rooftopRangeMultiplier; coneAngle = tuning.rooftopConeAngle; }
      }
      const coneCoords = createConePolygon(
        displayPos.lng, displayPos.lat,
        coneRange,
        agentFacing,
        coneAngle,
      );
      source.setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {},
          geometry: { type: 'Polygon', coordinates: [coneCoords] },
        }],
      });
    } else {
      source.setData({ type: 'FeatureCollection', features: [] });
    }
  }, [agentVisible, agentFacing, gameState?.agent?.position.lat, gameState?.agent?.position.lng, gameState?.agent?.isElevated, gameState?.agent?.rooftopPosition?.lat, gameState?.agent?.rooftopPosition?.lng, mapLayersReady]);

  // --- Informant tip markers ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;

    const updateInformantMarkers = () => {
      let source = map.getSource('informant-tips') as maplibregl.GeoJSONSource;
      if (!source) {
        map.addSource('informant-tips', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
        // "I" icon marker with decay opacity
        map.addLayer({
          id: 'informant-tips-circle',
          type: 'circle',
          source: 'informant-tips',
          paint: {
            'circle-radius': 10,
            'circle-color': 'rgba(170, 136, 255, 0.3)',
            'circle-stroke-width': 1.5,
            'circle-stroke-color': '#aa88ff',
            'circle-opacity': ['get', 'opacity'],
            'circle-stroke-opacity': ['get', 'opacity'],
          },
        });
        map.addLayer({
          id: 'informant-tips-label',
          type: 'symbol',
          source: 'informant-tips',
          layout: {
            'text-field': 'I',
            'text-size': 13,
            'text-font': ['Noto Sans Bold'],
          },
          paint: {
            'text-color': '#aa88ff',
            'text-halo-color': '#000000',
            'text-halo-width': 1,
            'text-opacity': ['get', 'opacity'],
          },
        });
        map.addLayer({
          id: 'informant-tips-name',
          type: 'symbol',
          source: 'informant-tips',
          layout: {
            'text-field': ['get', 'poiName'],
            'text-size': 10,
            'text-offset': [0, 1.5],
            'text-font': ['Noto Sans Bold'],
          },
          paint: {
            'text-color': '#aa88ff',
            'text-halo-color': '#000000',
            'text-halo-width': 1,
            'text-opacity': ['*', ['get', 'opacity'], 0.8],
          },
        });
        source = map.getSource('informant-tips') as maplibregl.GeoJSONSource;
      }

      // Ensure informant layers render above roads and street labels
      try {
        map.moveLayer('informant-tips-circle');
        map.moveLayer('informant-tips-label');
        map.moveLayer('informant-tips-name');
      } catch { /* layers might not exist yet */ }

      const currentTurn = gameState?.turn ?? 0;
      const features = informantTips.map(tip => {
        const age = currentTurn - tip.turnCreated;
        const maxAge = tuning.informantTipDuration;
        // Decay opacity: starts at 1.0, fades toward 0.2 as it ages
        const decayOpacity = Math.max(0.2, 1.0 - (age / maxAge) * 0.8);
        return {
          type: 'Feature' as const,
          properties: {
            poiName: tip.poiName,
            streetName: tip.streetName,
            opacity: decayOpacity,
          },
          geometry: {
            type: 'Point' as const,
            coordinates: [tip.poiPosition.lng, tip.poiPosition.lat],
          },
        };
      });

      source.setData({ type: 'FeatureCollection', features });
    };

    if (map.isStyleLoaded()) {
      updateInformantMarkers();
    } else {
      map.once('styledata', updateInformantMarkers);
    }

    // Hover popup for informant markers
    let tipPopup: maplibregl.Popup | null = null;
    const onTipEnter = (e: maplibregl.MapLayerMouseEvent) => {
      if (!e.features || e.features.length === 0) return;
      map.getCanvasContainer().style.cursor = 'pointer';
      const f = e.features[0];
      const coords = (f.geometry as GeoJSON.Point).coordinates;
      const props = f.properties!;
      const html = `<div style="font-family:monospace;font-size:11px;max-width:200px;">
        <div style="font-weight:700;color:#aa88ff;margin-bottom:4px;">INFORMANT TIP</div>
        <div style="color:#ddd;font-size:10px;">POI: ${props.poiName || 'Unknown'}</div>
        <div style="color:#aaa;font-size:10px;">Street: ${props.streetName || 'Unknown'}</div>
      </div>`;
      tipPopup = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 12, className: 'item-popup' })
        .setLngLat(coords as [number, number])
        .setHTML(html)
        .addTo(map);
    };
    const onTipLeave = () => {
      map.getCanvasContainer().style.cursor = '';
      if (tipPopup) { tipPopup.remove(); tipPopup = null; }
    };

    let tipCancelled = false;
    const setupTipHover = () => {
      if (tipCancelled) return;
      if (!map || !map.getLayer('informant-tips-circle')) {
        setTimeout(setupTipHover, 200);
        return;
      }
      map.on('mouseenter', 'informant-tips-circle', onTipEnter);
      map.on('mouseleave', 'informant-tips-circle', onTipLeave);
    };
    setupTipHover();

    return () => {
      tipCancelled = true;
      try {
        map.off('mouseenter', 'informant-tips-circle', onTipEnter);
        map.off('mouseleave', 'informant-tips-circle', onTipLeave);
      } catch { /* ignore if map destroyed */ }
      if (tipPopup) tipPopup.remove();
    };
  }, [informantTips]);

  // --- Spotter zone circles ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;

    const updateSpotters = () => {
      // Spotter icons (center points)
      let iconSource = map.getSource('spotter-icons') as maplibregl.GeoJSONSource;
      if (!iconSource) {
        map.addSource('spotter-icons', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
        // Load tactics icon for spotter marker using canvas (avoids SVG encoding issues)
        if (!map.hasImage('spotter-tactics-icon')) {
          const size = 32;
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d')!;
          // Draw the tactics icon path in gold
          ctx.fillStyle = '#f0c040';
          ctx.save();
          ctx.scale(size / 26, size / 26);
          const p = new Path2D('M 13.9375 0.03125 C 11.136719 0.503906 10.835938 3.074219 10.65625 3.59375 C 9.90625 4.035156 8.878906 4.9375 8.125 5.6875 C 6.238281 7.574219 5.539063 10.664063 6.6875 13.6875 C 7.1875 15 8.03125 19 8.03125 19 L 6 19 C 4.894531 19 4 19.671875 4 20.5 C 4 21.328125 4.894531 22 6 22 L 6.25 22 L 5.9375 22.28125 C 4.132813 23.914063 3.234375 26 5 26 L 21 26 C 22.765625 26 21.871094 23.914063 20.0625 22.28125 L 19.75 22 L 20 22 C 21.105469 22 22 21.328125 22 20.5 C 22 19.671875 21.105469 19 20 19 L 19 19 C 19 15.292969 13.84375 11.128906 13.84375 10.59375 C 13.84375 10.171875 14.0625 10.703125 14.6875 10.65625 C 15.304688 10.605469 15.957031 9.6875 16.53125 9.6875 C 17.765625 9.6875 17.574219 10.78125 18.625 10.78125 C 19.335938 10.78125 19.652344 10 19.9375 9.65625 C 20.292969 9.222656 21.304688 9.398438 20.75 8.1875 C 20.457031 7.542969 19.3125 5.390625 17.15625 4.5 C 16.175781 4.09375 17.078125 2.96875 13.96875 2.96875 C 13.699219 2.554688 13.734375 1.046875 13.9375 0.03125 Z');
          ctx.fill(p);
          ctx.restore();
          map.addImage('spotter-tactics-icon', canvas as any);
        }
        map.addLayer({
          id: 'spotter-icon-circle',
          type: 'symbol',
          source: 'spotter-icons',
          layout: {
            'icon-image': 'spotter-tactics-icon',
            'icon-size': 0.8,
            'icon-allow-overlap': true,
          },
        });
        map.addLayer({
          id: 'spotter-icon-label',
          type: 'symbol',
          source: 'spotter-icons',
          layout: {
            'text-field': 'SPOTTER',
            'text-size': 10,
            'text-offset': [0, 2.5],
            'text-font': ['Noto Sans Bold'],
            'text-allow-overlap': true,
          },
          paint: {
            'text-color': '#f0c040',
            'text-halo-color': '#000000',
            'text-halo-width': 1,
          },
        });
        iconSource = map.getSource('spotter-icons') as maplibregl.GeoJSONSource;
      }

      // Spotter radius circles (dashed rings)
      let ringSource = map.getSource('spotter-rings') as maplibregl.GeoJSONSource;
      if (!ringSource) {
        map.addSource('spotter-rings', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
        map.addLayer({
          id: 'spotter-ring-fill',
          type: 'fill',
          source: 'spotter-rings',
          paint: {
            'fill-color': 'rgba(240, 192, 64, 0.12)',
          },
        });
        map.addLayer({
          id: 'spotter-ring-line',
          type: 'line',
          source: 'spotter-rings',
          paint: {
            'line-color': '#f0c040',
            'line-width': 1.5,
            'line-dasharray': [4, 4],
            'line-opacity': 0.7,
          },
        });
        ringSource = map.getSource('spotter-rings') as maplibregl.GeoJSONSource;
      }

      // Ensure spotter layers render above buildings, roads, and street labels
      for (const lid of ['spotter-ring-fill', 'spotter-ring-line', 'spotter-icon-circle', 'spotter-icon-label']) {
        if (map.getLayer(lid)) {
          try { map.moveLayer(lid); } catch { /* */ }
        }
      }

      // Generate circle polygons for each spotter
      const iconFeatures = spotters.map(s => ({
        type: 'Feature' as const,
        properties: { turnsRemaining: s.turnsRemaining },
        geometry: {
          type: 'Point' as const,
          coordinates: [s.position.lng, s.position.lat],
        },
      }));

      const ringFeatures = spotters.map(s => {
        // Generate a circle polygon (64 segments)
        const lat = s.position.lat;
        const lng = s.position.lng;
        const segments = 64;
        const coords: [number, number][] = [];
        for (let i = 0; i <= segments; i++) {
          const angle = (i / segments) * 2 * Math.PI;
          const dLat = (s.radius * Math.cos(angle)) / 111000;
          const dLng = (s.radius * Math.sin(angle)) / (111000 * Math.cos((lat * Math.PI) / 180));
          coords.push([lng + dLng, lat + dLat]);
        }
        return {
          type: 'Feature' as const,
          properties: {},
          geometry: {
            type: 'Polygon' as const,
            coordinates: [coords],
          },
        };
      });

      iconSource.setData({ type: 'FeatureCollection', features: iconFeatures });
      ringSource.setData({ type: 'FeatureCollection', features: ringFeatures });
    };

    // Ensure sources/layers exist before updating data
    if (!map.getSource('spotter-icons') || !map.getSource('spotter-rings')) {
      if (map.isStyleLoaded()) {
        updateSpotters();
      } else {
        map.once('load', updateSpotters);
      }
    } else {
      // Sources exist — just update data
      const iconSource = map.getSource('spotter-icons') as maplibregl.GeoJSONSource;
      const ringSource = map.getSource('spotter-rings') as maplibregl.GeoJSONSource;
      const iconFeatures = spotters.map(s => ({
        type: 'Feature' as const,
        properties: { turnsRemaining: s.turnsRemaining },
        geometry: { type: 'Point' as const, coordinates: [s.position.lng, s.position.lat] },
      }));
      const ringFeatures = spotters.map(s => {
        const lat = s.position.lat;
        const lng = s.position.lng;
        const segments = 64;
        const coords: [number, number][] = [];
        for (let i = 0; i <= segments; i++) {
          const angle = (i / segments) * 2 * Math.PI;
          const dLat = (s.radius * Math.cos(angle)) / 111000;
          const dLng = (s.radius * Math.sin(angle)) / (111000 * Math.cos((lat * Math.PI) / 180));
          coords.push([lng + dLng, lat + dLat]);
        }
        return {
          type: 'Feature' as const,
          properties: {},
          geometry: { type: 'Polygon' as const, coordinates: [coords] },
        };
      });
      iconSource.setData({ type: 'FeatureCollection', features: iconFeatures });
      ringSource.setData({ type: 'FeatureCollection', features: ringFeatures });
    }
  }, [spotters, mapLayersReady]);

  // --- Spotter agent reveal — yellow outline ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Update agent marker stroke to show yellow outline when spotter-revealed
    if (map.getLayer('agent-marker-dot')) {
      try {
        map.setPaintProperty('agent-marker-dot', 'circle-stroke-color',
          spotterReveal ? '#f0c040' : '#ffffff'
        );
        map.setPaintProperty('agent-marker-dot', 'circle-stroke-width',
          spotterReveal ? 3 : 2
        );
      } catch { /* layer may not exist yet */ }
    }
  }, [spotterReveal]);

  // --- Item hover tooltip ---

  // Update game-items source when items change (collected etc)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;
    const source = map.getSource('game-items') as maplibregl.GeoJSONSource | undefined;
    if (!source) return;

    const features: GeoJSON.Feature[] = gameState.placedItems
      .filter(pi => !pi.collected)
      .map(pi => {
        const table = pi.lootTable ?? [];
        const totalWeight = table.reduce((s, e) => s + e.weight, 0);
        // Encode loot rows as "id:category:name:pct" joined by "|" so the
        // hover popup can render the item icon next to the name. Category
        // is needed so the icon gets its tint without a second ITEMS lookup
        // inside the tooltip builder.
        const lootInfo = table.map(e => {
          const item = ITEMS[e.item];
          const pct = totalWeight > 0 ? Math.round((e.weight / totalWeight) * 100) : 0;
          return `${item?.id ?? e.item}::${item?.category ?? 'tool'}::${item?.name ?? e.item}::${pct}%`;
        }).join('|');

        return {
          type: 'Feature' as const,
          properties: {
            id: pi.id,
            revealed: pi.revealed,
            category: pi.poi.category,
            categoryLabel: (pi.poi.category as string).charAt(0).toUpperCase() + (pi.poi.category as string).slice(1),
            poiName: pi.poi.name,
            pickupType: pi.pickupType ?? 'mixed',
            lootInfo,
          },
          geometry: {
            type: 'Point' as const,
            coordinates: [pi.poi.lng, pi.poi.lat],
          },
        };
      });

    source.setData({ type: 'FeatureCollection', features });
  }, [gameState?.placedItems, mapLayersReady]);

  // Hover tooltip for item pickups (no click — pickup is via Scavenge card)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;

    // Item tooltip is now handled inside addProximityHover via the HOVER_RADIUS bbox
    // so the outer cursor ring triggers it (not just pixel-precise mouseenter).
    // Passphrase + safehouse hover also handled there for consistent popup management.
  }, []);

  // Debrief paths — shown on round end (player = blue, agent = red)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    let source = map.getSource('debrief-paths') as maplibregl.GeoJSONSource | undefined;
    if (!source) {
      if (!map.isStyleLoaded()) return;
      map.addSource('debrief-paths', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'debrief-path-agent',
        type: 'line',
        source: 'debrief-paths',
        filter: ['==', ['get', 'side'], 'agent'],
        paint: {
          'line-color': '#ff4444',
          'line-width': ['interpolate', ['linear'], ['zoom'], 11, 2, 17, 5],
          'line-opacity': 0.7,
          'line-dasharray': [3, 2],
        },
      });
      map.addLayer({
        id: 'debrief-path-player',
        type: 'line',
        source: 'debrief-paths',
        filter: ['==', ['get', 'side'], 'player'],
        paint: {
          'line-color': '#44aaff',
          'line-width': ['interpolate', ['linear'], ['zoom'], 11, 2, 17, 5],
          'line-opacity': 0.8,
        },
      });
      // Dots at each turn position
      map.addSource('debrief-dots', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'debrief-dots-layer',
        type: 'circle',
        source: 'debrief-dots',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 3, 17, 6],
          'circle-color': ['match', ['get', 'side'], 'player', '#44aaff', '#ff4444'],
          'circle-opacity': 0.9,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#fff',
        },
      });
      source = map.getSource('debrief-paths') as maplibregl.GeoJSONSource;
    }

    if (!debriefPaths) {
      source.setData({ type: 'FeatureCollection', features: [] });
      const dotSrc = map.getSource('debrief-dots') as maplibregl.GeoJSONSource | undefined;
      if (dotSrc) dotSrc.setData({ type: 'FeatureCollection', features: [] });
      return;
    }

    const features: GeoJSON.Feature[] = [];
    if (debriefPaths.playerPath.length >= 2) {
      features.push({
        type: 'Feature',
        properties: { side: 'player' },
        geometry: { type: 'LineString', coordinates: debriefPaths.playerPath },
      });
    }
    if (debriefPaths.agentPath.length >= 2) {
      features.push({
        type: 'Feature',
        properties: { side: 'agent' },
        geometry: { type: 'LineString', coordinates: debriefPaths.agentPath },
      });
    }
    source.setData({ type: 'FeatureCollection', features });

    // Dots at each position
    const dotFeatures: GeoJSON.Feature[] = [];
    for (const [lng, lat] of debriefPaths.playerPath) {
      dotFeatures.push({
        type: 'Feature',
        properties: { side: 'player' },
        geometry: { type: 'Point', coordinates: [lng, lat] },
      });
    }
    for (const [lng, lat] of debriefPaths.agentPath) {
      dotFeatures.push({
        type: 'Feature',
        properties: { side: 'agent' },
        geometry: { type: 'Point', coordinates: [lng, lat] },
      });
    }
    const dotSrc = map.getSource('debrief-dots') as maplibregl.GeoJSONSource | undefined;
    if (dotSrc) dotSrc.setData({ type: 'FeatureCollection', features: dotFeatures });
  }, [debriefPaths]);

  // Passphrase hover is now handled inside addProximityHover alongside items/safehouses

  // Vision cone handle drag interaction
  const isDraggingConeRef = useRef(false);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    let cancelled = false;

    const onMouseDown = (e: maplibregl.MapMouseEvent) => {
      const m = mapRef.current;
      if (!m) return;
      if (!m.getLayer('vision-handle-layer')) return;
      const features = m.queryRenderedFeatures(e.point, { layers: ['vision-handle-layer'] });
      if (features.length === 0) return;

      isDraggingConeRef.current = true;
      m.getCanvasContainer().style.cursor = 'grabbing';
      m.dragPan.disable();
      e.preventDefault();
    };

    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      if (!isDraggingConeRef.current) return;

      const gs = gameStateRef.current;
      if (!gs) return;

      const playerPos = gs.player.position;
      const lngLat = e.lngLat;

      // Calculate angle from player to cursor (in degrees, 0 = north, clockwise)
      const dLng = lngLat.lng - playerPos.lng;
      const dLat = lngLat.lat - playerPos.lat;
      const angleRad = Math.atan2(dLng, dLat);
      const angleDeg = ((angleRad * 180) / Math.PI + 360) % 360;

      onFacingChangeRef.current?.(angleDeg);
    };

    const onMouseUp = () => {
      if (!isDraggingConeRef.current) return;
      isDraggingConeRef.current = false;
      const m = mapRef.current;
      if (m) {
        m.dragPan.enable();
        if (movementModeRef.current) {
          m.getCanvasContainer().style.cursor = MOVE_CURSOR;
        } else if (!spaceHeldRef.current) {
          m.getCanvasContainer().style.cursor = CROSSHAIR_CURSOR;
        }
      }
    };

    // Layer-specific hover for grab cursor (only when not dragging)
    // Track whether cursor is over the eyeball handle using screen-space distance
    let isOverHandle = false;
    const HANDLE_HOVER_PX = 20;
    const checkHandleHover = (e: maplibregl.MapMouseEvent) => {
      if (isDraggingConeRef.current || !mapRef.current) return;
      const m = mapRef.current;
      const gs = gameStateRef.current;
      if (!gs) return;
      // Get the vision handle position (end of cone) projected to screen
      const player = gs.player;
      const facing = player.facing;
      const range = tuning.visionConeRange;
      const handleLat = player.position.lat + (range * Math.cos(facing * Math.PI / 180)) / 111000;
      const handleLng = player.position.lng + (range * Math.sin(facing * Math.PI / 180)) / (111000 * Math.cos(player.position.lat * Math.PI / 180));
      const handleScreen = m.project([handleLng, handleLat]);
      const dx = e.point.x - handleScreen.x;
      const dy = e.point.y - handleScreen.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const nowOver = dist < HANDLE_HOVER_PX;
      if (nowOver && !isOverHandle) {
        isOverHandle = true;
        m.getCanvas().style.cursor = 'move';
      } else if (!nowOver && isOverHandle) {
        isOverHandle = false;
        if (movementModeRef.current) {
          m.getCanvas().style.cursor = MOVE_CURSOR;
        } else if (!spaceHeldRef.current) {
          m.getCanvas().style.cursor = CROSSHAIR_CURSOR;
        }
      }
    };
    const onHandleEnter = () => {};
    const onHandleLeave = () => {};

    const setup = () => {
      if (cancelled) return;
      const m = mapRef.current;
      if (!m) return;
      if (!m.getLayer('vision-handle-layer')) {
        setTimeout(setup, 100);
        return;
      }
      m.on('mousedown', onMouseDown);
      m.on('mousemove', onMouseMove);
      m.on('mousemove', checkHandleHover);
      m.on('mouseup', onMouseUp);
      m.on('mouseenter', 'vision-handle-layer', onHandleEnter);
      m.on('mouseleave', 'vision-handle-layer', onHandleLeave);
    };
    setup();

    return () => {
      cancelled = true;
      const m = mapRef.current;
      if (m) {
        m.off('mousedown', onMouseDown);
        m.off('mousemove', onMouseMove);
        m.off('mousemove', checkHandleHover);
        m.off('mouseup', onMouseUp);
        if (m.getLayer('vision-handle-layer')) {
          m.off('mouseenter', 'vision-handle-layer', onHandleEnter);
          m.off('mouseleave', 'vision-handle-layer', onHandleLeave);
        }
        if (isDraggingConeRef.current) {
          isDraggingConeRef.current = false;
          m.dragPan.enable();
        }
      }
    };
  }, []); // Only once — uses refs for current values

  // Toggle zone fill overlay visibility (Q key)
  useEffect(() => {
    let cancelled = false;
    function apply() {
      const map = mapRef.current;
      if (!map) return;
      if (!map.getLayer('zones-fill')) {
        setTimeout(() => { if (!cancelled) apply(); }, 200);
        return;
      }
      if (showZones) {
        map.setPaintProperty('zones-fill', 'fill-opacity', ['interpolate', ['linear'], ['zoom'], 10, 0.12, 14, 0.2, 17, 0.25]);
        // Zones visible — hover highlight is white so it stands out against colored fills
        if (map.getLayer('streets-highlight')) {
          map.setPaintProperty('streets-highlight', 'line-color', '#ffffff');
        }
      } else {
        map.setPaintProperty('zones-fill', 'fill-opacity', 0);
        if (map.getLayer('zones-hover')) map.setLayoutProperty('zones-hover', 'visibility', 'visible');
        // Zones hidden — hover highlight uses zone color so player still gets zone info
        if (map.getLayer('streets-highlight')) {
          map.setPaintProperty('streets-highlight', 'line-color', [
            'match',
            ['get', 'zone'],
            'commercial', '#ff4466',
            'transit', '#ff8844',
            'mixed-use', '#cc66ff',
            'civic', '#4488ff',
            'park', '#22cc55',
            'residential', '#44bbcc',
            'industrial', '#ccaa33',
            '#ffffff',
          ]);
        }
      }
    }
    apply();
    return () => { cancelled = true; };
  }, [showZones]);

  // Toggle road classification overlay visibility (R key)
  useEffect(() => {
    let cancelled = false;
    const roadOverlayLayers = ['roads-overlay-major', 'roads-overlay-minor', 'roads-overlay-residential', 'roads-overlay-footpath'];
    function apply() {
      const map = mapRef.current;
      if (!map) return;
      if (!map.getLayer('roads-overlay-major')) {
        setTimeout(() => { if (!cancelled) apply(); }, 200);
        return;
      }
      for (const layerId of roadOverlayLayers) {
        if (map.getLayer(layerId)) {
          map.setPaintProperty(
            layerId,
            'line-opacity',
            showRoads
              ? ['interpolate', ['linear'], ['zoom'], 12, 0.4, 16, 0.6]
              : 0,
          );
        }
      }
    }
    apply();
    return () => { cancelled = true; };
  }, [showRoads]);

  // Update transit visibility when visibleTransitTypes changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    function applyTransitVisibility() {
      const m = mapRef.current;
      if (!m) return;
      const types = Array.from(visibleTransitTypes);
      const hasActiveTransit = types.length > 0;

      const allTransitLayers = ['transit-lines-layer', 'transit-lines-highlight', 'transit-stops-layer', 'transit-stops-hover', 'transit-stops-labels'];
      if (hasActiveTransit) {
        const filter: maplibregl.FilterSpecification = ['in', ['get', 'transitType'], ['literal', types]];
        for (const layerId of allTransitLayers) {
          if (m.getLayer(layerId)) {
            if (layerId !== 'transit-stops-hover') {
              m.setFilter(layerId, filter);
            }
            m.setLayoutProperty(layerId, 'visibility', 'visible');
          }
        }
      } else {
        for (const layerId of allTransitLayers) {
          if (m.getLayer(layerId)) {
            m.setLayoutProperty(layerId, 'visibility', 'none');
          }
        }
      }
    }

    if (map.isStyleLoaded()) {
      applyTransitVisibility();
    } else {
      map.once('load', applyTransitVisibility);
    }
  }, [visibleTransitTypes]);

  // Update signal circles on the map when signals change or visibility toggles
  const signalCount = gameState?.signals?.length ?? 0;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gameState) return;

    const update = () => {
      const source = map.getSource('signal-circles') as maplibregl.GeoJSONSource;
      if (!source) return;

      // Filter signals based on visibility toggles + awareness system
      const playerExposure = gameState.player.exposure;
      const awarenessMultiplier = getAwarenessMultiplier(playerExposure);
      // Radio Scanner is now a held-inventory passive rather than a 3-turn
      // consumable — simply having the item keeps the effect active. See
      // items.ts radioScanner for the player-facing description.
      //
      // startsWith (not equality) because items picked up during a round are
      // cloned with a unique suffix ("radio-scanner-abc123") and loadout
      // items include "-loadout" ("radio-scanner-loadout"). Strict equality
      // matched neither, which is why Radio Scanner was silently broken.
      const scannerActive = gameState.player.items.some(i => i.id.startsWith('radio-scanner'));

      // --- Perception bucket passives ---
      // Echo Chamber: always on, 1.25x agent signal strength + bypass blind
      // Blown Entrance: turns 1-3 only, 1.75x + bypass blind
      const echoChamber = !!gameState.player.echoChamberActive;
      const blownEntrance = !!gameState.player.blownEntranceActive && gameState.turn <= 3;
      const perceptionBypassBlind = echoChamber || blownEntrance;
      let perceptionBoost = 1;
      if (echoChamber) perceptionBoost *= 1.25;
      if (blownEntrance) perceptionBoost *= 1.75;

      const visible = gameState.signals.filter(s => {
        // Only show signals within the configured age window
        if (s.revealed && s.age > signalMaxAge) return false;

        // Sweep signals are always visible to the player who placed them
        // (Intel cards bypass awareness gating)
        if (s.type === 'sweep' && s.source === 'player') return true;

        if (s.source === 'player') {
          // Own passive signals only visible with Counter-Surveillance active
          // Only show the most recent turn's signal (not the whole history)
          if (!showOwnSignals) return false;
          return s.turnGenerated >= gameState.turn - 1;
        }
        if (s.source === 'agent') {
          if (!showEnemySignals || !s.revealed) return false;
          // Agent sweep signals are only visible when counter-surveillance is active
          if (s.type === 'sweep' && !showOwnSignals) return false;
          // Active intel (non-passive signals like sweep results) always visible
          if (s.type !== 'passive') return true;
          // Passive agent signals (movement/idle) are HIDDEN BY DEFAULT.
          // Only visible with an active bypass: Radio Scanner, Echo Chamber,
          // Blown Entrance, or Counter-Surveillance (for the prior turn's
          // signals only). Without one of these, the player is blind to
          // the agent's ambient signal — they must use intel cards to find them.
          if (scannerActive || perceptionBypassBlind) return true;
          if (counterSurveillanceActive && s.turnGenerated === gameState.turn - 1) return true;
          return false;
        }
        return s.revealed; // decoys always show if revealed
      });

      // Apply awareness dampening / scanner boost / perception passives to agent display strength
      const features: GeoJSON.Feature[] = visible
        .map(s => {
          if (s.source === 'agent') {
            let strength = s.strength;
            // Radio Scanner: amplify agent signal display strength by 25%
            if (scannerActive) {
              strength = strength * 1.25;
            } else if (awarenessMultiplier < 1 && !perceptionBypassBlind) {
              // Perception passives bypass awareness dampening entirely
              strength = strength * awarenessMultiplier;
            }
            // Perception passives stack multiplicatively on top
            if (perceptionBoost !== 1) strength = strength * perceptionBoost;
            strength = Math.min(1, strength);
            if (strength !== s.strength) {
              return signalToCircleFeature({ ...s, strength }, gameState.turn);
            }
          }
          return signalToCircleFeature(s, gameState.turn);
        });

      source.setData({
        type: 'FeatureCollection',
        features,
      });

      // Update sweep labels
      const labelSource = map.getSource('sweep-labels') as maplibregl.GeoJSONSource;
      if (labelSource) {
        const sweepLabels: GeoJSON.Feature[] = visible
          .filter(s => s.type === 'sweep' && s.sweepResult)
          .map(s => {
            const isSuccess = s.sweepResult === 'success';
            // Age-based opacity for labels too
            let opacity = 1;
            if (s.age === 1) opacity = 0.4;
            else if (s.age === 2) opacity = 0.18;
            else if (s.age >= 3) opacity = 0.08;

            return {
              type: 'Feature' as const,
              properties: {
                label: isSuccess ? '⚠ CONTACT' : 'NO MATCH',
                color: isSuccess ? '#ff3344' : '#666677',
                opacity,
              },
              geometry: {
                type: 'Point' as const,
                coordinates: [s.position.lng, s.position.lat],
              },
            };
          });

        // Add decoy/burner phone labels
        const decoyLabels: GeoJSON.Feature[] = visible
          .filter(s => s.source === 'decoy')
          .map(s => {
            const isBurner = s.neighborhoodName === 'burner-phone';
            let opacity = 1;
            if (s.age === 1) opacity = 0.5;
            else if (s.age === 2) opacity = 0.25;
            else if (s.age >= 3) opacity = 0.1;

            return {
              type: 'Feature' as const,
              properties: {
                label: isBurner ? '📱 BURNER PHONE' : '⚙ DECOY',
                color: isBurner ? '#44aaff' : theme.colors.accentOrange,
                opacity,
              },
              geometry: {
                type: 'Point' as const,
                coordinates: [s.position.lng, s.position.lat],
              },
            };
          });

        // Add Recon Plane strip labels — same "⚠ CONTACT" / "NO MATCH" pattern
        // as Sweep, positioned at the strip center, fading over 3 turns.
        const reconLabels: GeoJSON.Feature[] = (gameState?.reconStrips ?? [])
          .filter(s => (gameState ? gameState.turn - s.turn : 99) <= 3)
          .map(s => {
            const age = gameState ? gameState.turn - s.turn : 0;
            let opacity = 1;
            if (age === 1) opacity = 0.5;
            else if (age === 2) opacity = 0.25;
            else if (age >= 3) opacity = 0.1;
            return {
              type: 'Feature' as const,
              properties: {
                label: s.hit ? '⚠ CONTACT' : 'NO MATCH',
                color: s.hit ? '#ff3344' : '#888899',
                opacity,
              },
              geometry: {
                type: 'Point' as const,
                coordinates: [s.center.lng, s.center.lat],
              },
            };
          });

        labelSource.setData({
          type: 'FeatureCollection',
          features: [...sweepLabels, ...decoyLabels, ...reconLabels],
        });
      }
    };

    // Always try to update — the source is created during addDataLayers on map load.
    // isStyleLoaded() can return false even after the style is fully loaded (MapLibre quirk
    // after dynamic layer changes), so we just check if the source exists directly.
    if (map.getSource('signal-circles')) {
      update();
    } else if (map.isStyleLoaded()) {
      update();
    } else {
      map.once('load', update);
    }
    // Including `items` in deps so picking up / dropping a Radio Scanner
    // mid-turn re-runs the filter immediately (otherwise the reveal wouldn't
    // kick in until the next turn-end naturally changed signals/turn).
  }, [signalCount, gameState?.signals, gameState?.turn, gameState?.player?.exposure, gameState?.player?.items, gameState?.reconStrips, showEnemySignals, showOwnSignals, counterSurveillanceActive, signalMaxAge]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    />
  );
}

// --- Movement path layers ---

function addMovementLayers(map: maplibregl.Map) {
  // Path preview line (dashed)
  map.addSource('movement-path', {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  });
  map.addLayer({
    id: 'movement-path-line',
    type: 'line',
    source: 'movement-path',
    paint: {
      'line-color': theme.colors.playerMarker,
      'line-width': 3,
      'line-dasharray': [2, 3],
      'line-opacity': 0.8,
    },
  });

  // Distance label
  map.addSource('movement-label', {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  });
  map.addLayer({
    id: 'movement-label-layer',
    type: 'symbol',
    source: 'movement-label',
    layout: {
      'text-field': ['get', 'label'],
      'text-font': ['Noto Sans Regular'],
      'text-size': 14,
      'text-offset': [0, -1.5],
      'text-anchor': 'bottom',
      'text-allow-overlap': true,
    },
    paint: {
      'text-color': ['match', ['get', 'over'], 'true', theme.colors.accentRed, theme.colors.playerMarker],
      'text-halo-color': theme.colors.background,
      'text-halo-width': 2,
    },
  });

  // --- Alt movement route layers ---
  map.addSource('alt-routes', {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  });
  map.addLayer({
    id: 'alt-routes-line',
    type: 'line',
    source: 'alt-routes',
    paint: {
      'line-color': '#44aaff',
      'line-width': 3,
      'line-dasharray': [4, 3],
      'line-opacity': 0.5,
    },
  });

  map.addSource('alt-routes-hover', {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  });
  map.addLayer({
    id: 'alt-routes-hover-glow',
    type: 'line',
    source: 'alt-routes-hover',
    paint: {
      'line-color': '#44aaff',
      'line-width': 8,
      'line-opacity': 0.3,
      'line-blur': 4,
    },
  });
  map.addLayer({
    id: 'alt-routes-hover-line',
    type: 'line',
    source: 'alt-routes-hover',
    paint: {
      'line-color': '#44aaff',
      'line-width': 5,
      'line-opacity': 0.9,
    },
  });

  // Alt routes endpoint markers (small circles at the end of each route)
  map.addSource('alt-routes-endpoints', {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  });
  map.addLayer({
    id: 'alt-routes-endpoints-circle',
    type: 'circle',
    source: 'alt-routes-endpoints',
    paint: {
      'circle-radius': 5,
      'circle-color': '#44aaff',
      'circle-opacity': 0.7,
      'circle-stroke-width': 1,
      'circle-stroke-color': '#ffffff',
      'circle-stroke-opacity': 0.5,
    },
  });

  // Alt movement snap cursor — blue dot that follows the mouse snapped to nearest road
  map.addSource('alt-snap-cursor', {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  });
  map.addLayer({
    id: 'alt-snap-cursor-dot',
    type: 'circle',
    source: 'alt-snap-cursor',
    paint: {
      'circle-radius': 6,
      'circle-color': '#44aaff',
      'circle-opacity': 0.8,
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff',
      'circle-stroke-opacity': 0.6,
    },
  });
}

/**
 * Update the movement path preview.
 * @param path - The path to show (clamped to max range)
 * @param maxRange - The card's max movement range
 * @param overDistance - If set, the cursor is beyond range — show this distance in red
 */
function updateMovementPath(map: maplibregl.Map, path: PathResult | null, _maxRange?: number, overDistance?: number) {
  const pathSource = map.getSource('movement-path') as maplibregl.GeoJSONSource;
  const labelSource = map.getSource('movement-label') as maplibregl.GeoJSONSource;
  if (!pathSource || !labelSource) return;

  if (!path || path.coordinates.length < 2) {
    pathSource.setData({ type: 'FeatureCollection', features: [] });
    labelSource.setData({ type: 'FeatureCollection', features: [] });
    return;
  }

  pathSource.setData({
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: path.coordinates,
      },
    }],
  });

  const isOver = overDistance !== undefined;
  const displayDist = isOver ? overDistance : path.totalDistance;

  // Place label at end of path
  const endCoord = path.coordinates[path.coordinates.length - 1];
  labelSource.setData({
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: {
        label: `${Math.round(displayDist!)}m`,
        over: isOver ? 'true' : 'false',
      },
      geometry: {
        type: 'Point',
        coordinates: endCoord,
      },
    }],
  });
}

/**
 * Clear alt movement route layers.
 */
function clearAltRoutes(map: maplibregl.Map) {
  const altSrc = map.getSource('alt-routes') as maplibregl.GeoJSONSource;
  const hoverSrc = map.getSource('alt-routes-hover') as maplibregl.GeoJSONSource;
  const endpointSrc = map.getSource('alt-routes-endpoints') as maplibregl.GeoJSONSource;
  if (altSrc) altSrc.setData({ type: 'FeatureCollection', features: [] });
  if (hoverSrc) hoverSrc.setData({ type: 'FeatureCollection', features: [] });
  if (endpointSrc) endpointSrc.setData({ type: 'FeatureCollection', features: [] });
}

function animatePlayerMovement(
  map: maplibregl.Map,
  path: PathResult,
  animatingRef: React.RefObject<boolean>,
  onComplete: () => void,
) {
  const playerSource = map.getSource('player-marker') as maplibregl.GeoJSONSource;
  if (!playerSource || path.coordinates.length < 2) {
    onComplete();
    return;
  }

  (animatingRef as React.MutableRefObject<boolean>).current = true;

  // Calculate cumulative distances along the path for even speed
  const coords = path.coordinates;
  const cumDist: number[] = [0];
  for (let i = 1; i < coords.length; i++) {
    const dx = coords[i][0] - coords[i - 1][0];
    const dy = coords[i][1] - coords[i - 1][1];
    cumDist.push(cumDist[i - 1] + Math.sqrt(dx * dx + dy * dy));
  }
  const totalDist = cumDist[cumDist.length - 1];

  // Animation: ~500ms + 1ms per meter (so 300m = ~800ms)
  const duration = Math.min(2000, 500 + path.totalDistance);
  const startTime = performance.now();

  function animate(now: number) {
    const elapsed = now - startTime;
    const t = Math.min(1, elapsed / duration);
    // Ease out cubic for smooth deceleration
    const eased = 1 - Math.pow(1 - t, 3);

    // Find position along path at eased progress
    const targetDist = eased * totalDist;
    let segIdx = 0;
    for (let i = 1; i < cumDist.length; i++) {
      if (cumDist[i] >= targetDist) {
        segIdx = i - 1;
        break;
      }
      segIdx = i - 1;
    }

    const segLen = cumDist[segIdx + 1] - cumDist[segIdx];
    const segT = segLen > 0 ? (targetDist - cumDist[segIdx]) / segLen : 0;
    const lng = coords[segIdx][0] + (coords[segIdx + 1][0] - coords[segIdx][0]) * segT;
    const lat = coords[segIdx][1] + (coords[segIdx + 1][1] - coords[segIdx][1]) * segT;

    playerSource.setData({
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        properties: {},
        geometry: { type: 'Point', coordinates: [lng, lat] },
      }],
    });

    if (t < 1) {
      requestAnimationFrame(animate);
    } else {
      // Always clear the path line after animation (stale path from completed move)
      updateMovementPath(map, null);
      (animatingRef as React.MutableRefObject<boolean>).current = false;
      onComplete();
    }
  }

  requestAnimationFrame(animate);
}

// --- Geometry helpers ---

// --- Signal rendering helpers ---

/**
 * Generate a GeoJSON circle polygon from a center point and radius.
 * MapLibre doesn't have native circle-from-meters, so we approximate
 * with a 48-point polygon.
 */
function createCirclePolygon(
  centerLng: number,
  centerLat: number,
  radiusMeters: number,
  numPoints: number = 24,
): [number, number][] {
  const coords: [number, number][] = [];
  const latRad = (centerLat * Math.PI) / 180;

  for (let i = 0; i <= numPoints; i++) {
    const angle = (i / numPoints) * 2 * Math.PI;
    const dLat = (radiusMeters * Math.cos(angle)) / 111000;
    const dLng = (radiusMeters * Math.sin(angle)) / (111000 * Math.cos(latRad));
    coords.push([centerLng + dLng, centerLat + dLat]);
  }

  return coords;
}

/**
 * Generate a rotated rectangular strip polygon in lat/lng.
 * - centerLng/centerLat: midpoint of the strip
 * - angleDeg: bearing of the long axis (0 = north, 90 = east, CW)
 * - lengthM: strip length (along the axis)
 * - widthM: strip width (perpendicular)
 * Returns 5 coords (closed polygon).
 */
function createStripPolygon(
  centerLng: number,
  centerLat: number,
  angleDeg: number,
  lengthM: number,
  widthM: number,
): [number, number][] {
  const theta = (angleDeg * Math.PI) / 180;
  // Length axis (along angle): (sin θ, cos θ) in (east, north) meters
  const aE = Math.sin(theta);
  const aN = Math.cos(theta);
  // Width axis (perpendicular, CCW): (-cos θ, sin θ)
  const bE = -Math.cos(theta);
  const bN = Math.sin(theta);

  const halfL = lengthM / 2;
  const halfW = widthM / 2;
  const latRad = (centerLat * Math.PI) / 180;
  const mToLat = 1 / 111000;
  const mToLng = 1 / (111000 * Math.cos(latRad));

  const corners: Array<[number, number]> = [
    [ halfL * aE + halfW * bE,  halfL * aN + halfW * bN],
    [ halfL * aE - halfW * bE,  halfL * aN - halfW * bN],
    [-halfL * aE - halfW * bE, -halfL * aN - halfW * bN],
    [-halfL * aE + halfW * bE, -halfL * aN + halfW * bN],
  ];
  const coords: [number, number][] = corners.map(([e, n]) => [
    centerLng + e * mToLng,
    centerLat + n * mToLat,
  ]);
  coords.push(coords[0]); // close polygon
  return coords;
}

/**
 * Generate a cone (pie-slice) polygon for the vision cone.
 * facingDeg: 0 = north, 90 = east, clockwise.
 * widthDeg: total arc width in degrees (e.g. 90 = ±45° from facing).
 */
function createConePolygon(
  centerLng: number,
  centerLat: number,
  radiusMeters: number,
  facingDeg: number,
  widthDeg: number,
  numArcPoints: number = 32,
): [number, number][] {
  const coords: [number, number][] = [];
  const latRad = (centerLat * Math.PI) / 180;

  // Start at the player position (tip of the cone)
  coords.push([centerLng, centerLat]);

  // Arc from (facing - halfWidth) to (facing + halfWidth)
  const halfWidth = widthDeg / 2;
  const startAngle = facingDeg - halfWidth;
  const endAngle = facingDeg + halfWidth;

  for (let i = 0; i <= numArcPoints; i++) {
    const angleDeg = startAngle + (i / numArcPoints) * (endAngle - startAngle);
    const angleRad = (angleDeg * Math.PI) / 180;
    // angleDeg: 0 = north (positive lat), 90 = east (positive lng)
    const dLat = (radiusMeters * Math.cos(angleRad)) / 111000;
    const dLng = (radiusMeters * Math.sin(angleRad)) / (111000 * Math.cos(latRad));
    coords.push([centerLng + dLng, centerLat + dLat]);
  }

  // Close back to center
  coords.push([centerLng, centerLat]);

  return coords;
}

/**
 * Convert a Signal object to a GeoJSON Feature with styling properties.
 * Age determines opacity and outline style.
 */
function signalToCircleFeature(signal: Signal, _currentTurn?: number): GeoJSON.Feature {
  // Color depends on signal source/type
  let baseColor: string;
  if (signal.type === 'sweep') {
    baseColor = signal.sweepResult === 'success' ? '#ff3344' : '#666677';
  } else if (signal.source === 'decoy') {
    baseColor = theme.colors.accentOrange; // orange — player's own decoy
  } else {
    const isPlayer = signal.source === 'player';
    baseColor = isPlayer ? theme.colors.playerMarker : theme.colors.agentMarker;
  }

  // Age-based opacity: 3 turns visible, fading out each turn
  let fillOpacity: number;
  let outlineWidth: number;
  let outlineOpacity: number;

  // Sweep signals start brighter
  const isSweep = signal.type === 'sweep';
  const baseFill = isSweep ? 0.18 : 0.14;
  const baseOutline = isSweep ? 0.9 : 0.8;

  if (signal.age <= 0) {
    fillOpacity = baseFill;
    outlineWidth = 2;
    outlineOpacity = baseOutline;
  } else if (signal.age === 1) {
    fillOpacity = baseFill * 0.35;
    outlineWidth = 1.2;
    outlineOpacity = baseOutline * 0.4;
  } else if (signal.age === 2) {
    fillOpacity = baseFill * 0.15;
    outlineWidth = 0.8;
    outlineOpacity = baseOutline * 0.18;
  } else {
    fillOpacity = baseFill * 0.06;
    outlineWidth = 0.5;
    outlineOpacity = baseOutline * 0.08;
  }

  const circleCoords = createCirclePolygon(
    signal.position.lng,
    signal.position.lat,
    signal.radius,
  );

  return {
    type: 'Feature',
    properties: {
      color: baseColor,
      opacity: fillOpacity,
      outlineWidth,
      outlineOpacity,
      age: signal.age,
      source: signal.source,
      neighborhoodName: signal.neighborhoodName || '',
    },
    geometry: {
      type: 'Polygon',
      coordinates: [circleCoords],
    },
  };
}


function addDataLayers(map: maplibregl.Map, cityData: CityData, gameState?: GameState) {
  // --- SOURCES ---

  map.addSource('water', { type: 'geojson', data: cityData.layers.water });
  map.addSource('zones', { type: 'geojson', data: cityData.layers.zones });
  map.addSource('buildings', { type: 'geojson', data: cityData.layers.buildings, generateId: true });
  map.addSource('streets', { type: 'geojson', data: cityData.layers.streets });
  map.addSource('neighborhoods', { type: 'geojson', data: cityData.layers.neighborhoods });

  // Build maps from stop ID → transit types and line info
  const stopTransitTypes = new Map<string, Set<string>>();
  const stopLineInfo = new Map<string, { lineId: string; lineName: string }>();
  for (const line of cityData.layers.transit.lines) {
    for (const stop of line.stops) {
      if (!stopTransitTypes.has(stop.id)) stopTransitTypes.set(stop.id, new Set());
      stopTransitTypes.get(stop.id)!.add(line.type);
      // Store first line association for each stop
      if (!stopLineInfo.has(stop.id)) {
        stopLineInfo.set(stop.id, { lineId: line.id, lineName: line.name });
      }
    }
  }

  // Transit lines as GeoJSON — include lineId so all segments of a line can highlight together
  const transitLineFeatures: GeoJSON.Feature[] = [];
  for (const line of cityData.layers.transit.lines) {
    for (const segment of line.geometry) {
      transitLineFeatures.push({
        type: 'Feature',
        properties: { color: line.color, name: line.name, type: line.type, transitType: line.type, lineId: line.id },
        geometry: { type: 'LineString', coordinates: segment },
      });
    }
  }
  map.addSource('transit-lines', {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: transitLineFeatures },
  });

  // Determine fallback transit type — use the most common type in this city's data
  const typeCounts = new Map<string, number>();
  for (const line of cityData.layers.transit.lines) {
    typeCounts.set(line.type, (typeCounts.get(line.type) || 0) + 1);
  }
  const fallbackTransitType = [...typeCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || 'subway';

  // Transit stops as GeoJSON
  const transitStopFeatures: GeoJSON.Feature[] = cityData.layers.transit.stops.map(stop => {
    const types = stopTransitTypes.get(stop.id);
    const transitType = types ? Array.from(types)[0] : fallbackTransitType;
    const lineInfo = stopLineInfo.get(stop.id);
    return {
      type: 'Feature',
      properties: {
        name: stop.name, id: stop.id, transitType,
        lineId: lineInfo?.lineId || '',
        lineName: lineInfo?.lineName || '',
      },
      geometry: { type: 'Point', coordinates: [stop.lng, stop.lat] },
    };
  });
  map.addSource('transit-stops', {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: transitStopFeatures },
    generateId: true,
  });

  // POIs as GeoJSON
  const poiFeatures: GeoJSON.Feature[] = cityData.pois.map(poi => ({
    type: 'Feature',
    properties: { name: poi.name, category: poi.category, id: poi.id },
    geometry: { type: 'Point', coordinates: [poi.lng, poi.lat] },
  }));
  map.addSource('pois', {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: poiFeatures },
  });

  // --- LAYERS (back to front) ---

  // Water fill
  map.addLayer({
    id: 'water-fill',
    type: 'fill',
    source: 'water',
    filter: ['==', '$type', 'Polygon'],
    paint: {
      'fill-color': theme.colors.waterFill,
      'fill-opacity': 0.9,
    },
  });

  // Water lines (rivers, streams)
  map.addLayer({
    id: 'water-line',
    type: 'line',
    source: 'water',
    filter: ['==', '$type', 'LineString'],
    paint: {
      'line-color': theme.colors.waterStroke,
      'line-width': 3,
      'line-opacity': 0.8,
    },
  });

  // Zone overlays — color-coded by zone type
  map.addLayer({
    id: 'zones-fill',
    type: 'fill',
    source: 'zones',
    paint: {
      'fill-color': [
        'match',
        ['get', 'zoneType'],
        'commercial', '#ff4466',
        'transit', '#ff8844',
        'mixed-use', '#cc66ff',
        'civic', '#4488ff',
        'park', '#22cc55',
        'residential', '#44bbcc',
        'industrial', '#ccaa33',
        '#333344',
      ],
      'fill-opacity': 0, // toggled on via showZones
    },
  });

  // Building footprints — only visible when zoomed in enough to matter
  // playerInside: player is hiding in this building (blue)
  // hideAvailable: reachable for Hide Inside card (green)
  // hideHover: hovered during hide mode (bright green)
  map.addLayer({
    id: 'buildings-fill',
    type: 'fill',
    source: 'buildings',
    minzoom: 13,
    paint: {
      'fill-color': [
        'case',
        // Feature-state highlights take priority
        ['boolean', ['feature-state', 'playerInside'], false],
        theme.colors.playerMarker,
        ['boolean', ['feature-state', 'stakeoutActive'], false],
        'rgba(170, 136, 255, 0.55)',
        ['boolean', ['feature-state', 'hideHover'], false],
        '#66cc88',
        ['boolean', ['feature-state', 'hideAvailable'], false],
        'rgba(102, 204, 136, 0.5)',
        ['boolean', ['feature-state', 'hideLocked'], false],
        'rgba(204, 170, 68, 0.4)',
        ['boolean', ['feature-state', 'rooftopAvailable'], false],
        'rgba(170, 136, 255, 0.4)',
        // Default: tint commercial buildings slightly warm, residential stays dark
        ['in', ['get', 'type'], ['literal', ['commercial', 'retail', 'office', 'industrial', 'warehouse', 'public', 'civic', 'government', 'church', 'cathedral', 'chapel', 'mosque', 'synagogue', 'temple', 'hospital', 'hotel', 'school', 'university', 'train_station', 'supermarket', 'mall', 'basilica', 'service', 'parking', 'transportation', 'stadium', 'sports_centre', 'museum', 'library', 'theatre', 'cinema', 'fire_station', 'police', 'post_office', 'bank', 'marketplace']]],
        '#161a1e', // slightly warm/lighter for commercial
        theme.colors.buildingFill, // dark for residential/unknown
      ],
      'fill-opacity': ['interpolate', ['linear'], ['zoom'], 13, 0, 14, 0.9],
    },
  });

  map.addLayer({
    id: 'buildings-outline',
    type: 'line',
    source: 'buildings',
    minzoom: 14,
    paint: {
      'line-color': [
        'case',
        ['boolean', ['feature-state', 'playerInside'], false],
        theme.colors.playerMarker,
        ['boolean', ['feature-state', 'stakeoutActive'], false],
        '#ffffff',
        ['boolean', ['feature-state', 'hideHover'], false],
        '#88ddaa',
        ['boolean', ['feature-state', 'hideAvailable'], false],
        '#66cc88',
        ['boolean', ['feature-state', 'hideLocked'], false],
        '#ccaa44',
        ['boolean', ['feature-state', 'rooftopAvailable'], false],
        '#aa88ff',
        // Commercial buildings get a subtle warm outline
        ['in', ['get', 'type'], ['literal', ['commercial', 'retail', 'office', 'industrial', 'warehouse', 'public', 'civic', 'government', 'church', 'cathedral', 'chapel', 'mosque', 'synagogue', 'temple', 'hospital', 'hotel', 'school', 'university', 'train_station', 'supermarket', 'mall', 'basilica', 'service', 'parking', 'transportation', 'stadium', 'sports_centre', 'museum', 'library', 'theatre', 'cinema', 'fire_station', 'police', 'post_office', 'bank', 'marketplace']]],
        '#2a2520',
        theme.colors.buildingStroke,
      ],
      'line-width': [
        'case',
        ['boolean', ['feature-state', 'playerInside'], false],
        1.5,
        ['boolean', ['feature-state', 'stakeoutActive'], false],
        1.5,
        ['boolean', ['feature-state', 'hideAvailable'], false],
        1,
        ['boolean', ['feature-state', 'hideLocked'], false],
        1,
        ['boolean', ['feature-state', 'rooftopAvailable'], false],
        1,
        0.3,
      ],
      'line-opacity': ['interpolate', ['linear'], ['zoom'], 14, 0, 15, 0.45],
    },
  });

  // Streets — layered by classification, zoom-responsive widths
  // Footpaths — only when zoomed in close
  map.addLayer({
    id: 'streets-footpath',
    type: 'line',
    source: 'streets',
    minzoom: 13,
    filter: ['==', ['get', 'tier'], 'footpath'],
    paint: {
      'line-color': theme.colors.streetFootpath,
      'line-width': ['interpolate', ['linear'], ['zoom'], 13, 0.5, 15, 1.5, 17, 3.0],
      'line-opacity': ['interpolate', ['linear'], ['zoom'], 13, 0, 13.5, 0.4, 15, 0.6],
    },
  });

  // Residential — fade in at mid-zoom
  map.addLayer({
    id: 'streets-residential',
    type: 'line',
    source: 'streets',
    minzoom: 13,
    filter: ['==', ['get', 'tier'], 'residential'],
    paint: {
      'line-color': theme.colors.streetResidential,
      'line-width': ['interpolate', ['linear'], ['zoom'], 13, 0.8, 16, 2.8],
      'line-opacity': ['interpolate', ['linear'], ['zoom'], 13, 0, 13.5, 0.8],
    },
  });

  // Minor roads
  map.addLayer({
    id: 'streets-minor',
    type: 'line',
    source: 'streets',
    filter: ['==', ['get', 'tier'], 'minor'],
    paint: {
      'line-color': theme.colors.streetMinor,
      'line-width': ['interpolate', ['linear'], ['zoom'], 12, 1.5, 16, 4.5],
      'line-opacity': 0.9,
    },
  });

  // Major roads
  map.addLayer({
    id: 'streets-major',
    type: 'line',
    source: 'streets',
    filter: ['==', ['get', 'tier'], 'major'],
    paint: {
      'line-color': theme.colors.streetMajor,
      'line-width': ['interpolate', ['linear'], ['zoom'], 12, 2.0, 16, 6.0],
      'line-opacity': 0.95,
    },
  });

  // --- ROAD CLASSIFICATION OVERLAY ---
  // Colored lines showing signal weight per road class, toggled with zone overlay
  // Colors: red=major(high signal), orange=minor, yellow=residential, green=footpath(low signal)
  const ROAD_OVERLAY_COLORS: Record<string, string> = {
    footpath: '#4ade80',     // green — safest
    residential: '#facc15',  // yellow
    minor: '#fb923c',        // orange
    major: '#f87171',        // red — most exposed
  };

  for (const [tier, color] of Object.entries(ROAD_OVERLAY_COLORS)) {
    map.addLayer({
      id: `roads-overlay-${tier}`,
      type: 'line',
      source: 'streets',
      filter: ['==', ['get', 'tier'], tier],
      paint: {
        'line-color': color,
        'line-width': ['interpolate', ['linear'], ['zoom'],
          12, tier === 'major' ? 2.5 : tier === 'minor' ? 2 : 1.5,
          16, tier === 'major' ? 5 : tier === 'minor' ? 4 : tier === 'residential' ? 3 : 2,
        ],
        'line-opacity': 0, // toggled on via showZones
      },
    });
  }

  // --- STREET LABELS ---

  // Major road names — visible from zoomed-out
  map.addLayer({
    id: 'street-labels-major',
    type: 'symbol',
    source: 'streets',
    minzoom: 13,
    filter: ['all', ['==', ['get', 'tier'], 'major'], ['!=', ['get', 'name'], '']],
    layout: {
      'symbol-placement': 'line',
      'symbol-spacing': 250,
      'text-field': ['get', 'name'],
      'text-font': ['Noto Sans Regular'],
      'text-size': ['interpolate', ['linear'], ['zoom'], 13, 12, 16, 16],
      'text-max-angle': 45,
      'text-allow-overlap': false,
      'text-ignore-placement': false,
      'text-padding': 2,
      'text-transform': 'uppercase',
      'text-letter-spacing': 0.08,
    },
    paint: {
      'text-color': '#ffffff',
      'text-halo-color': '#000000',
      'text-halo-width': 2,
      'text-opacity': ['interpolate', ['linear'], ['zoom'], 13, 0.7, 15, 0.9],
    },
  });

  // Minor road names — visible at mid-zoom
  map.addLayer({
    id: 'street-labels-minor',
    type: 'symbol',
    source: 'streets',
    minzoom: 14.5,
    filter: ['all', ['==', ['get', 'tier'], 'minor'], ['!=', ['get', 'name'], '']],
    layout: {
      'symbol-placement': 'line',
      'symbol-spacing': 250,
      'text-field': ['get', 'name'],
      'text-font': ['Noto Sans Regular'],
      'text-size': ['interpolate', ['linear'], ['zoom'], 14.5, 11, 17, 14],
      'text-max-angle': 45,
      'text-allow-overlap': false,
      'text-ignore-placement': false,
      'text-padding': 2,
    },
    paint: {
      'text-color': '#6b7f99',
      'text-halo-color': theme.colors.background,
      'text-halo-width': 2,
      'text-opacity': ['interpolate', ['linear'], ['zoom'], 14.5, 0.5, 15.5, 0.8],
    },
  });

  // Residential road names — only when zoomed in close
  map.addLayer({
    id: 'street-labels-residential',
    type: 'symbol',
    source: 'streets',
    minzoom: 16,
    filter: ['all', ['==', ['get', 'tier'], 'residential'], ['!=', ['get', 'name'], '']],
    layout: {
      'symbol-placement': 'line',
      'symbol-spacing': 200,
      'text-field': ['get', 'name'],
      'text-font': ['Noto Sans Regular'],
      'text-size': ['interpolate', ['linear'], ['zoom'], 16, 10, 18, 13],
      'text-max-angle': 45,
      'text-allow-overlap': false,
      'text-ignore-placement': false,
      'text-padding': 2,
    },
    paint: {
      'text-color': '#556677',
      'text-halo-color': theme.colors.background,
      'text-halo-width': 1.5,
      'text-opacity': ['interpolate', ['linear'], ['zoom'], 16, 0.4, 16.5, 0.7],
    },
  });

  // Street highlight layer — all segments with matching name light up on hover
  map.addLayer({
    id: 'streets-highlight',
    type: 'line',
    source: 'streets',
    filter: ['==', ['get', 'name'], '__none__'],  // impossible match — nothing highlighted initially
    paint: {
      'line-color': '#ffffff',
      'line-width': [
        'interpolate', ['linear'], ['zoom'],
        12, 2,
        16, 5,
      ],
      'line-opacity': 0.35,
    },
  });

  // --- TRANSIT (on top of roads when visible) ---

  // Transit lines
  map.addLayer({
    id: 'transit-lines-layer',
    type: 'line',
    source: 'transit-lines',
    layout: { visibility: 'none' },
    paint: {
      'line-color': ['get', 'color'],
      'line-width': [
        'interpolate', ['linear'], ['zoom'],
        11, 0.8,
        14, 1.5,
        17, 2.5,
      ],
      'line-opacity': 0.75,
    },
  });

  // Transit lines highlight layer — same source, filtered to hovered lineId, brighter + thicker
  map.addLayer({
    id: 'transit-lines-highlight',
    type: 'line',
    source: 'transit-lines',
    filter: ['==', ['get', 'lineId'], ''],
    layout: { visibility: 'none' },
    paint: {
      'line-color': ['get', 'color'],
      'line-width': [
        'interpolate', ['linear'], ['zoom'],
        11, 1.5,
        14, 2.5,
        17, 4,
      ],
      'line-opacity': 1,
    },
  });

  // Transit stops — base layer (always visible when transit is on)
  map.addLayer({
    id: 'transit-stops-layer',
    type: 'circle',
    source: 'transit-stops',
    layout: { visibility: 'none' },
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 4, 14, 6, 17, 10],
      'circle-color': theme.colors.transitStop,
      'circle-opacity': 0.9,
      'circle-stroke-width': 1,
      'circle-stroke-color': 'rgba(255, 255, 255, 0.3)',
    },
  });

  // Transit stops — hover layer (bigger, brighter, on top)
  map.addLayer({
    id: 'transit-stops-hover',
    type: 'circle',
    source: 'transit-stops',
    layout: { visibility: 'none' },
    filter: ['==', ['get', 'id'], ''],  // nothing shown initially
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 7, 14, 10, 17, 15],
      'circle-color': '#ffffff',
      'circle-opacity': 0.95,
      'circle-stroke-width': 2,
      'circle-stroke-color': theme.colors.accentGreen,
    },
  });

  // Transit stop labels
  map.addLayer({
    id: 'transit-stops-labels',
    type: 'symbol',
    source: 'transit-stops',
    minzoom: 14,
    layout: {
      visibility: 'none',
      'text-field': ['get', 'name'],
      'text-font': ['Noto Sans Regular'],
      'text-size': [
        'interpolate', ['linear'], ['zoom'],
        14, 8,
        17, 11,
      ],
      'text-offset': [0, 1.2],
      'text-anchor': 'top',
      'text-max-width': 8,
      'text-optional': true,
      'text-allow-overlap': false,
    },
    paint: {
      'text-color': theme.colors.textSecondary,
      'text-halo-color': theme.colors.background,
      'text-halo-width': 1,
      'text-opacity': ['interpolate', ['linear'], ['zoom'], 14, 0, 15, 0.5, 17, 0.8],
    },
  });

  // Neighborhood labels (very dim, large text) — on top of everything
  map.addLayer({
    id: 'neighborhood-labels',
    type: 'symbol',
    source: 'neighborhoods',
    minzoom: 15,
    layout: {
      'text-field': ['get', 'name'],
      'text-font': ['Noto Sans Regular'],
      'text-size': 14,
      'text-transform': 'uppercase',
      'text-letter-spacing': 0.15,
      'text-max-width': 10,
    },
    paint: {
      'text-color': theme.colors.textDim,
      'text-opacity': 0.4,
    },
  });

  // --- GAME ELEMENTS (on top of everything) ---

  if (gameState) {
    // Item pickups — question marks at POI locations
    const itemFeatures: GeoJSON.Feature[] = gameState.placedItems
      .filter(pi => !pi.collected)
      .map(pi => {
        // Build loot table info for tooltip — encoded as "id::category::name::pct"
        // so the hover popup can render an item icon beside the name.
        const table = pi.lootTable ?? [];
        const totalWeight = table.reduce((s, e) => s + e.weight, 0);
        const lootInfo = table.map(e => {
          const item = ITEMS[e.item];
          const pct = totalWeight > 0 ? Math.round((e.weight / totalWeight) * 100) : 0;
          return `${item?.id ?? e.item}::${item?.category ?? 'tool'}::${item?.name ?? e.item}::${pct}%`;
        }).join('|');

        return {
          type: 'Feature' as const,
          properties: {
            id: pi.id,
            revealed: pi.revealed,
            category: pi.poi.category,
            categoryLabel: (pi.poi.category as string).charAt(0).toUpperCase() + (pi.poi.category as string).slice(1),
            poiName: pi.poi.name,
            pickupType: pi.pickupType ?? 'mixed',
            lootInfo,
          },
          geometry: {
            type: 'Point' as const,
            coordinates: [pi.poi.lng, pi.poi.lat],
          },
        };
      });
    map.addSource('game-items', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: itemFeatures },
      generateId: true,
    });
    // Data-driven color by pickup type:
    //   weapon   = red     (W)  — weapon cache
    //   gadget   = green   (G)  — gadget stash
    //   lockpick = brass   (L)  — guaranteed lock-pick drop
    //   scanner  = cyan    (S)  — guaranteed radio-scanner drop
    //   mixed    = yellow  (?)  — supply drop, any item
    const pickupColorExpr: maplibregl.ExpressionSpecification = [
      'match', ['get', 'pickupType'],
      'weapon', theme.colors.pickupWeapon,
      'gadget', theme.colors.pickupGadget,
      'lockpick', theme.colors.pickupLockpick,
      'scanner', theme.colors.pickupScanner,
      theme.colors.pickupMixed,
    ];
    const pickupStrokeExpr: maplibregl.ExpressionSpecification = [
      'match', ['get', 'pickupType'],
      'weapon', 'rgba(255, 80, 80, 0.4)',
      'gadget', 'rgba(68, 204, 102, 0.4)',
      'lockpick', 'rgba(204, 136, 68, 0.4)',
      'scanner', 'rgba(68, 204, 255, 0.4)',
      'rgba(255, 221, 68, 0.4)',
    ];
    map.addLayer({
      id: 'game-items-layer',
      type: 'circle',
      source: 'game-items',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 3, 14, 5, 17, 8],
        'circle-color': pickupColorExpr,
        'circle-opacity': 0.8,
        'circle-stroke-width': 1,
        'circle-stroke-color': pickupStrokeExpr,
      },
    });
    // Hover layer — same source, filtered to show only the hovered item at 2× size
    const pickupStrokeHoverExpr: maplibregl.ExpressionSpecification = [
      'match', ['get', 'pickupType'],
      'weapon', 'rgba(255, 80, 80, 0.6)',
      'gadget', 'rgba(68, 204, 102, 0.6)',
      'lockpick', 'rgba(204, 136, 68, 0.6)',
      'scanner', 'rgba(68, 204, 255, 0.6)',
      'rgba(255, 221, 68, 0.6)',
    ];
    map.addLayer({
      id: 'game-items-hover',
      type: 'circle',
      source: 'game-items',
      filter: ['==', ['get', 'id'], ''],  // initially matches nothing
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 6, 14, 10, 17, 16],
        'circle-color': pickupColorExpr,
        'circle-opacity': 1.0,
        'circle-stroke-width': 2,
        'circle-stroke-color': pickupStrokeHoverExpr,
      },
    });
    // Label glyph: W=weapon, G=gadget, L=lockpick, S=scanner, ?=mixed
    map.addLayer({
      id: 'game-items-labels',
      type: 'symbol',
      source: 'game-items',
      minzoom: 14,
      layout: {
        'text-field': [
          'match', ['get', 'pickupType'],
          'weapon', 'W',
          'gadget', 'G',
          'lockpick', 'L',
          'scanner', 'S',
          '?',
        ] as maplibregl.ExpressionSpecification,
        'text-font': ['Noto Sans Regular'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 14, 10, 17, 14],
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': theme.colors.background,
        'text-halo-color': pickupColorExpr,
        'text-halo-width': 0,
      },
    });

    // Landmarks — circular photo markers for tourist attractions with Wikipedia images.
    // Fetch images async, then add features only for POIs that have images.
    {
      const MAX_LANDMARKS = 10;
      const landmarkPois = cityData.pois
        .filter(p => {
          if (p.category !== 'landmark' || p.osmTags.tourism === 'museum') return false;
          // Require English Wikipedia article, or heritage/importance tags (UNESCO etc.)
          const wiki = p.osmTags.wikipedia || '';
          const hasEnglishWiki = wiki.startsWith('en:');
          const hasHeritage = !!p.osmTags.heritage || p.osmTags.importance === 'international' || p.osmTags.importance === 'national';
          return (hasEnglishWiki || hasHeritage) && (wiki || p.osmTags.wikidata);
        })
        .slice(0, MAX_LANDMARKS);

      // Add empty source + layers immediately (populated once images load)
      map.addSource('landmarks', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      // Border ring behind the icon (gold outline)
      map.addLayer({
        id: 'landmarks-ring',
        type: 'circle',
        source: 'landmarks',
        minzoom: 12,
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 12, 10, 15, 16, 17, 22],
          'circle-color': 'rgba(0,0,0,0)',
          'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 12, 1.5, 17, 2.5],
          'circle-stroke-color': 'rgba(204, 170, 102, 0.6)',
        },
      });
      // Symbol layer using per-feature image IDs
      map.addLayer({
        id: 'landmarks-icons',
        type: 'symbol',
        source: 'landmarks',
        minzoom: 12,
        layout: {
          'icon-image': ['get', 'imageId'],
          'icon-size': ['interpolate', ['linear'], ['zoom'], 12, 0.4, 15, 0.65, 17, 0.9],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });

      // Fetch Wikipedia thumbnails and create circular masked images
      const CIRCLE_SIZE = 48; // px (icon image size before pixelRatio)

      function createCircularImage(imgUrl: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = CIRCLE_SIZE;
            canvas.height = CIRCLE_SIZE;
            const ctx = canvas.getContext('2d')!;
            // Clip to circle
            ctx.beginPath();
            ctx.arc(CIRCLE_SIZE / 2, CIRCLE_SIZE / 2, CIRCLE_SIZE / 2, 0, Math.PI * 2);
            ctx.closePath();
            ctx.clip();
            // Draw image centered/cropped
            const scale = Math.max(CIRCLE_SIZE / img.width, CIRCLE_SIZE / img.height);
            const w = img.width * scale;
            const h = img.height * scale;
            ctx.drawImage(img, (CIRCLE_SIZE - w) / 2, (CIRCLE_SIZE - h) / 2, w, h);
            // Convert to Image for MapLibre
            const out = new Image();
            out.onload = () => resolve(out);
            out.onerror = reject;
            out.src = canvas.toDataURL();
          };
          img.onerror = reject;
          img.src = imgUrl;
        });
      }

      // Fetch all landmark images in parallel, then populate the source
      const validFeatures: GeoJSON.Feature[] = [];
      let loaded = 0;

      for (const poi of landmarkPois) {
        const wikipedia = poi.osmTags.wikipedia || '';
        const wikidata = poi.osmTags.wikidata || '';
        const imageId = `landmark-${poi.id}`;

        // Try Wikipedia API first (has direct thumbnail URL)
        let apiUrl: string | null = null;
        if (wikipedia) {
          const [lang, ...titleParts] = wikipedia.split(':');
          const title = titleParts.join(':');
          if (title) apiUrl = `https://${lang}.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`;
        } else if (wikidata) {
          // Fallback: use wikidata → get wikipedia article → get thumbnail
          apiUrl = `https://www.wikidata.org/wiki/Special:EntityData/${wikidata}.json`;
        }

        if (!apiUrl) { loaded++; continue; }

        (async () => {
          try {
            let thumbUrl: string | null = null;

            if (wikipedia) {
              const resp = await fetch(apiUrl!);
              const data = await resp.json();
              // Skip articles about people, events, or non-places
              const desc = (data.description || '').toLowerCase();
              const wikiType = (data.type || '');
              const isPlace = data.coordinates != null
                || desc.includes('building') || desc.includes('church') || desc.includes('cathedral')
                || desc.includes('museum') || desc.includes('palace') || desc.includes('castle')
                || desc.includes('bridge') || desc.includes('park') || desc.includes('square')
                || desc.includes('station') || desc.includes('monument') || desc.includes('tower')
                || desc.includes('market') || desc.includes('theatre') || desc.includes('theater')
                || desc.includes('arena') || desc.includes('stadium') || desc.includes('temple')
                || desc.includes('mosque') || desc.includes('synagogue') || desc.includes('basilica')
                || desc.includes('gate') || desc.includes('fountain') || desc.includes('garden')
                || desc.includes('quarter') || desc.includes('district') || desc.includes('street')
                || desc.includes('plaza') || desc.includes('landmark') || desc.includes('venue');
              const isPerson = wikiType === 'standard' && (
                desc.includes('born ') || desc.includes('politician') || desc.includes('monarch')
                || desc.includes('king ') || desc.includes('queen ') || desc.includes('emperor')
                || desc.includes('artist') || desc.includes('writer') || desc.includes('composer')
                || desc.includes('footballer') || desc.includes('actor') || desc.includes('singer')
              );
              const isEvent = desc.includes('bombing') || desc.includes('attack') || desc.includes('massacre')
                || desc.includes('battle ') || desc.includes('war ') || desc.includes('disaster');
              if (isPerson || isEvent || (!isPlace && !data.coordinates)) {
                // Not a place — skip
              } else {
                thumbUrl = data.thumbnail?.source ?? null;
              }
            } else if (wikidata) {
              // Get image filename from wikidata P18, build commons thumb URL
              const resp = await fetch(apiUrl!);
              const data = await resp.json();
              const entity = data.entities?.[wikidata];
              const imageFile = entity?.claims?.P18?.[0]?.mainsnak?.datavalue?.value;
              if (imageFile) {
                const encodedFile = encodeURIComponent(imageFile.replace(/ /g, '_'));
                thumbUrl = `https://commons.wikimedia.org/wiki/Special:FilePath/${encodedFile}?width=200`;
              }
            }

            if (thumbUrl && map.getStyle()) {
              const circImg = await createCircularImage(thumbUrl);
              if (!map.hasImage(imageId)) {
                map.addImage(imageId, circImg, { pixelRatio: 1 });
              }
              validFeatures.push({
                type: 'Feature',
                properties: { name: poi.name, wikipedia, wikidata, imageId },
                geometry: { type: 'Point', coordinates: [poi.lng, poi.lat] },
              });
            }
          } catch {
            // No image available — skip this landmark
          } finally {
            loaded++;
            // Once all fetches complete, update the source with features that have images
            if (loaded >= landmarkPois.length) {
              const src = map.getSource('landmarks') as maplibregl.GeoJSONSource;
              if (src) {
                src.setData({ type: 'FeatureCollection', features: validFeatures });
              }
            }
          }
        })();
      }

      // If no landmarks, mark as done
      if (landmarkPois.length === 0) {
        // No landmarks — source stays empty
      }
    }

    // Extraction points — glowing markers at map edges
    const extractionFeatures: GeoJSON.Feature[] = gameState.extractions.map(ext => ({
      type: 'Feature' as const,
      properties: {
        id: ext.id,
        name: ext.name,
        security: ext.security,
        mode: ext.mode,
      },
      geometry: {
        type: 'Point' as const,
        coordinates: [ext.lng, ext.lat],
      },
    }));
    map.addSource('game-extractions', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: extractionFeatures },
    });
    map.addLayer({
      id: 'game-extractions-glow',
      type: 'circle',
      source: 'game-extractions',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 8, 14, 12, 17, 18],
        'circle-color': [
          'match', ['get', 'security'],
          'low', theme.colors.extractionLow,
          'medium', theme.colors.extractionMed,
          'high', theme.colors.extractionHigh,
          theme.colors.extractionLow,
        ],
        'circle-opacity': 0.2,
        'circle-blur': 1,
      },
    });
    map.addLayer({
      id: 'game-extractions-layer',
      type: 'circle',
      source: 'game-extractions',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 4, 14, 6, 17, 10],
        'circle-color': [
          'match', ['get', 'security'],
          'low', theme.colors.extractionLow,
          'medium', theme.colors.extractionMed,
          'high', theme.colors.extractionHigh,
          theme.colors.extractionLow,
        ],
        'circle-opacity': 0.9,
        'circle-stroke-width': 1.5,
        'circle-stroke-color': 'rgba(255, 255, 255, 0.3)',
      },
    });
    map.addLayer({
      id: 'game-extractions-labels',
      type: 'symbol',
      source: 'game-extractions',
      minzoom: 12,
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Regular'],
        'text-size': 10,
        'text-offset': [0, 1.5],
        'text-anchor': 'top',
        'text-transform': 'uppercase',
        'text-letter-spacing': 0.1,
      },
      paint: {
        'text-color': [
          'match', ['get', 'security'],
          'low', theme.colors.extractionLow,
          'medium', theme.colors.extractionMed,
          'high', theme.colors.extractionHigh,
          theme.colors.extractionLow,
        ],
        'text-halo-color': theme.colors.background,
        'text-halo-width': 1,
        'text-opacity': 0.8,
      },
    });

    // Extraction reveal zones — dashed 300m radius circles around exits
    const extractZoneFeatures: GeoJSON.Feature[] = gameState.extractions.map(ext => ({
      type: 'Feature' as const,
      properties: { id: ext.id, security: ext.security },
      geometry: {
        type: 'Polygon' as const,
        coordinates: [createCirclePolygon(ext.lng, ext.lat, tuning.extractRevealRange)],
      },
    }));
    map.addSource('extraction-zones', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: extractZoneFeatures },
    });
    map.addLayer({
      id: 'extraction-zones-fill',
      type: 'fill',
      source: 'extraction-zones',
      paint: {
        'fill-color': '#ff4040',
        'fill-opacity': 0.03,
      },
    });
    map.addLayer({
      id: 'extraction-zones-outline',
      type: 'line',
      source: 'extraction-zones',
      paint: {
        'line-color': '#ff4040',
        'line-width': 1,
        'line-opacity': 0.25,
        'line-dasharray': [4, 4],
      },
    });

    // Passphrases — silver dead drop markers with key icon (always visible to player)
    if (gameState?.passphrases) {
      const ppFeatures: GeoJSON.Feature[] = gameState.passphrases
        .filter(p => !p.collected)
        .map(p => ({
          type: 'Feature' as const,
          properties: { id: p.id },
          geometry: { type: 'Point' as const, coordinates: [p.lng, p.lat] },
        }));
      map.addSource('game-passphrases', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: ppFeatures },
        generateId: true,
      });
      // Glow behind icon
      map.addLayer({
        id: 'game-passphrases-glow',
        type: 'circle',
        source: 'game-passphrases',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 8, 14, 12, 17, 18],
          'circle-color': '#ccddee',
          'circle-opacity': 0.15,
          'circle-blur': 0.8,
        },
      });
      // Hover glow — same source, filtered to hovered passphrase
      map.addLayer({
        id: 'game-passphrases-hover-glow',
        type: 'circle',
        source: 'game-passphrases',
        filter: ['==', ['get', 'id'], ''],
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 14, 14, 20, 17, 28],
          'circle-color': '#ccddee',
          'circle-opacity': 0.35,
          'circle-blur': 0.6,
        },
      });
      // Load passphrase icon as a tinted SVG
      const ppColor = '#ccddee';
      const ppSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="50" height="50"><path fill="${ppColor}" d="M 17 3 C 15.354545 3 14 4.3545455 14 6 L 14 18 L 6.5 18 C 5.12 18 4 19.12 4 20.5 L 4 28.5 C 4 29.88 5.12 31 6.5 31 L 14 31 L 14 44 C 14 45.645455 15.354545 47 17 47 L 37 47 C 38.645455 47 40 45.645455 40 44 L 40 6 C 40 4.3545455 38.645455 3 37 3 L 17 3 z M 16 10 L 38 10 L 38 39 L 16 39 L 16 31 L 24.5 31 C 25.88 31 27 29.88 27 28.5 L 27 20.5 C 27 19.12 25.88 18 24.5 18 L 16 18 L 16 10 z M 10.5 23 C 11.33 23 12 23.67 12 24.5 C 12 25.33 11.33 26 10.5 26 C 9.67 26 9 25.33 9 24.5 C 9 23.67 9.67 23 10.5 23 z M 15.5 23 C 16.33 23 17 23.67 17 24.5 C 17 25.33 16.33 26 15.5 26 C 14.67 26 14 25.33 14 24.5 C 14 23.67 14.67 23 15.5 23 z M 20.5 23 C 21.33 23 22 23.67 22 24.5 C 22 25.33 21.33 26 20.5 26 C 19.67 26 19 25.33 19 24.5 C 19 23.67 19.67 23 20.5 23 z M 24 42 L 30 42 C 30.552 42 31 42.448 31 43 C 31 43.552 30.552 44 30 44 L 24 44 C 23.448 44 23 43.552 23 43 C 23 42.448 23.448 42 24 42 z"/></svg>`;
      const ppDataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(ppSvg)}`;
      const ppImg = new Image();
      ppImg.onload = () => {
        if (!map.hasImage('passphrase-icon')) {
          map.addImage('passphrase-icon', ppImg, { pixelRatio: 2 });
        }
        if (!map.getLayer('game-passphrases-icon')) {
          map.addLayer({
            id: 'game-passphrases-icon',
            type: 'symbol',
            source: 'game-passphrases',
            layout: {
              'icon-image': 'passphrase-icon',
              'icon-size': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 14, 0.7, 17, 1.0],
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
            },
          });
        }
      };
      ppImg.src = ppDataUrl;
    }

    // Safehouses — icon markers, hidden from agent, only for player
    if (gameState?.safehouses) {
      const safehouseFeatures: GeoJSON.Feature[] = gameState.safehouses
        .filter(sh => !sh.visited)
        .map(sh => ({
          type: 'Feature' as const,
          properties: { id: sh.id },
          geometry: { type: 'Point' as const, coordinates: [sh.lng, sh.lat] },
        }));
      map.addSource('game-safehouses', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: safehouseFeatures },
        generateId: true,
      });
      // Glow behind icon
      map.addLayer({
        id: 'game-safehouses-glow',
        type: 'circle',
        source: 'game-safehouses',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 8, 14, 12, 17, 18],
          'circle-color': '#44cc88',
          'circle-opacity': 0.15,
          'circle-blur': 0.8,
        },
      });
      // Hover glow — same source, filtered to hovered safehouse
      map.addLayer({
        id: 'game-safehouses-hover-glow',
        type: 'circle',
        source: 'game-safehouses',
        filter: ['==', ['get', 'id'], ''],
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 14, 14, 20, 17, 28],
          'circle-color': '#44cc88',
          'circle-opacity': 0.35,
          'circle-blur': 0.6,
        },
      });
      // Load safehouse icon as a tinted SVG
      const shColor = '#44cc88';
      const shSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 26" width="52" height="52"><path fill="${shColor}" d="M 1 0 C 0.445313 0 0 0.449219 0 1 L 0 12 C 0 12.550781 0.445313 13 1 13 L 1 25 C 1 25.550781 1.449219 26 2 26 L 24 26 C 24.550781 26 25 25.550781 25 25 L 25 13 C 25.554688 13 26 12.550781 26 12 L 26 1 C 26 0.449219 25.554688 0 25 0 Z M 13 6 L 18 13 L 23 13 L 23 24 L 16 24 L 16 19 C 16 17.34375 14.65625 16 13 16 C 11.34375 16 10 17.34375 10 19 L 10 24 L 3 24 L 3 13 L 8 13 Z M 5 16 L 5 21 L 8 21 L 8 16 Z M 18 16 L 18 21 L 21 21 L 21 16 Z"/></svg>`;
      const shDataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(shSvg)}`;
      const shImg = new Image();
      shImg.onload = () => {
        if (!map.hasImage('safehouse-icon')) {
          map.addImage('safehouse-icon', shImg, { pixelRatio: 2 });
        }
        if (!map.getLayer('game-safehouses-icon')) {
          map.addLayer({
            id: 'game-safehouses-icon',
            type: 'symbol',
            source: 'game-safehouses',
            layout: {
              'icon-image': 'safehouse-icon',
              'icon-size': ['interpolate', ['linear'], ['zoom'], 11, 0.6, 14, 0.9, 17, 1.3],
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
            },
          });
        }
      };
      shImg.src = shDataUrl;
    }

    // --- Signal circles ---
    map.addSource('signal-circles', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });

    // Signal fill — semi-transparent circle
    map.addLayer({
      id: 'signal-fill',
      type: 'fill',
      source: 'signal-circles',
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': ['get', 'opacity'],
      },
    });

    // Signal outline — solid for fresh, dashed effect via thinner stroke for old
    map.addLayer({
      id: 'signal-outline',
      type: 'line',
      source: 'signal-circles',
      paint: {
        'line-color': ['get', 'color'],
        'line-width': ['get', 'outlineWidth'],
        'line-opacity': ['get', 'outlineOpacity'],
      },
    });

    // Sweep result labels
    map.addSource('sweep-labels', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addLayer({
      id: 'sweep-labels-layer',
      type: 'symbol',
      source: 'sweep-labels',
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 14,
        'text-font': ['Noto Sans Bold'],
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': ['get', 'color'],
        'text-halo-color': 'rgba(12, 16, 24, 0.9)',
        'text-halo-width': 2,
        'text-opacity': ['get', 'opacity'],
      },
    });

    // Recon Plane strips — pre-create source + layers here so that the
    // per-strip useEffect only has to call setData() and never has to fight
    // with MapLibre's flaky isStyleLoaded() after dynamic addLayer calls.
    map.addSource('recon-plane-strips', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addLayer({
      id: 'recon-plane-strips-fill',
      type: 'fill',
      source: 'recon-plane-strips',
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': ['get', 'fillOpacity'],
      },
    });
    map.addLayer({
      id: 'recon-plane-strips-line',
      type: 'line',
      source: 'recon-plane-strips',
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 2,
        'line-opacity': ['get', 'lineOpacity'],
      },
    });

    // Bearing lines — same reasoning. Pre-create source + glow/main layers.
    map.addSource('bearing-lines', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addLayer({
      id: 'bearing-lines-glow',
      type: 'line',
      source: 'bearing-lines',
      paint: {
        'line-color': '#aa88ff',
        'line-width': 6,
        'line-opacity': ['*', ['get', 'opacity'], 0.25],
        'line-blur': 3,
      },
    });
    map.addLayer({
      id: 'bearing-lines-main',
      type: 'line',
      source: 'bearing-lines',
      paint: {
        'line-color': '#aa88ff',
        'line-width': 2,
        'line-opacity': ['get', 'opacity'],
        'line-dasharray': [2, 3],
      },
    });

    // Surveillance Van — patrol routes (deep purple), yellow vision cones,
    // and van markers. Pre-create sources + layers so the per-van useEffects
    // only have to call setData.
    map.addSource('surveillance-van-routes', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addLayer({
      id: 'surveillance-van-routes-glow',
      type: 'line',
      source: 'surveillance-van-routes',
      paint: {
        'line-color': '#7a5ed6',
        'line-width': 8,
        'line-opacity': 0.3,
        'line-blur': 3,
      },
    });
    map.addLayer({
      id: 'surveillance-van-routes-line',
      type: 'line',
      source: 'surveillance-van-routes',
      paint: {
        'line-color': '#7a5ed6',
        'line-width': 4,
        'line-dasharray': [3, 2],
        'line-opacity': 1.0,
      },
    });
    map.addSource('surveillance-van-cones', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addLayer({
      id: 'surveillance-van-cones-fill',
      type: 'fill',
      source: 'surveillance-van-cones',
      paint: {
        'fill-color': '#ffdd44',
        'fill-opacity': 0.14,
      },
    });
    map.addLayer({
      id: 'surveillance-van-cones-outline',
      type: 'line',
      source: 'surveillance-van-cones',
      paint: {
        'line-color': '#ffdd44',
        'line-width': 1.5,
        'line-opacity': 0.55,
        'line-dasharray': [2, 3],
      },
    });
    map.addSource('surveillance-van-markers', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    // Van icon loaded lazily in a dedicated useEffect below.
    map.addLayer({
      id: 'surveillance-van-marker-halo',
      type: 'circle',
      source: 'surveillance-van-markers',
      paint: {
        'circle-radius': 14,
        'circle-color': '#7a5ed6',
        'circle-opacity': 0.35,
        'circle-stroke-width': 1.5,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-opacity': 0.7,
      },
    });

    // Traffic Analysis — glowing red highlight along the full named road
    // the agent is currently on. Only renders when the read returned a hit
    // (the agent is on a named major/minor road), so red = enemy detected.
    // Cleared at end of turn.
    map.addSource('traffic-analysis-road', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addLayer({
      id: 'traffic-analysis-road-glow',
      type: 'line',
      source: 'traffic-analysis-road',
      paint: {
        'line-color': '#ff3c3c',
        'line-width': 14,
        'line-opacity': 0.28,
        'line-blur': 5,
      },
    });
    map.addLayer({
      id: 'traffic-analysis-road-line',
      type: 'line',
      source: 'traffic-analysis-road',
      paint: {
        'line-color': '#ff5050',
        'line-width': 5,
        'line-opacity': 0.95,
      },
    });

    // Wire Tap placed lines — pre-created here so the wireTaps useEffect
    // just needs to call setData. Dedicated source/layer keeps placed taps
    // visible during placement mode (the hover preview uses a separate
    // source, `wiretap-preview`). Paint colors by the feature's `triggered`
    // property: yellow while active, red while triggered (3-turn lifespan).
    map.addSource('wiretap-placed', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addLayer({
      id: 'wiretap-placed-line',
      type: 'line',
      source: 'wiretap-placed',
      paint: {
        'line-color': ['case', ['==', ['get', 'triggered'], true], '#ff4444', '#ffdd44'],
        'line-width': 5,
        'line-opacity': 0.85,
      },
    });

    // Overwatch segment — 300m road stretch the player is watching. Red +
    // dashed to distinguish from Wire Tap (yellow solid). Pre-created here
    // so a separate effect can drive it via setData based on
    // gameState.player.overwatch.segmentCoords.
    map.addSource('overwatch-segment', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addLayer({
      id: 'overwatch-segment-line',
      type: 'line',
      source: 'overwatch-segment',
      paint: {
        'line-color': '#ff5050',
        'line-width': 4,
        'line-dasharray': [4, 3],
        'line-opacity': 0.9,
      },
    });

    // Misdirect path — persistent yellow dashed line after placement. Pre-
    // creating the source + layer here (same pattern as Doppelgänger below)
    // guarantees the layer exists at a known position in the paint order.
    // Previously this was added lazily inside a useEffect, which meant the
    // layer could land below other later-added layers and be visually
    // hidden even though setData was writing valid coords.
    map.addSource('misdirect-path', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addLayer({
      id: 'misdirect-path-line',
      type: 'line',
      source: 'misdirect-path',
      paint: {
        'line-color': '#ffdd44',
        'line-width': 3,
        'line-dasharray': [4, 3],
        'line-opacity': 0.7,
      },
    });

    // Stage Exit path — glow + solid yellow line along the faked transit
    // ride. Same reason as the Misdirect block above: pre-registered here
    // so both layers stack at the known top of the paint order rather than
    // getting buried by later-added feature layers.
    map.addSource('stage-exit-path', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    // Soft outer glow for visibility over transit geometry
    map.addLayer({
      id: 'stage-exit-path-glow',
      type: 'line',
      source: 'stage-exit-path',
      paint: {
        'line-color': '#ffdd44',
        'line-width': 10,
        'line-opacity': 0.25,
        'line-blur': 4,
      },
    });
    map.addLayer({
      id: 'stage-exit-path-line',
      type: 'line',
      source: 'stage-exit-path',
      paint: {
        'line-color': '#ffdd44',
        'line-width': 4,
        'line-opacity': 0.9,
      },
    });

    // Jammer zone — persistent translucent disk at gameState.player.
    // jammerPosition for the duration of jammerTurnsLeft. Fill + dashed
    // outline so the player can see the safe zone they dropped. Pre-
    // registered here so the layer stacks above base geometry (same reason
    // as misdirect/stage-exit path layers).
    map.addSource('jammer-zone', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addLayer({
      id: 'jammer-zone-fill',
      type: 'fill',
      source: 'jammer-zone',
      paint: {
        'fill-color': '#44aaff',
        'fill-opacity': 0.1,
      },
    });
    map.addLayer({
      id: 'jammer-zone-outline',
      type: 'line',
      source: 'jammer-zone',
      paint: {
        'line-color': '#44aaff',
        'line-width': 1.5,
        'line-dasharray': [4, 3],
        'line-opacity': 0.8,
      },
    });

    // Doppelgänger path + marker. Path (faint blue dashed) shows where the
    // decoy is walking; marker is a blue dot at the decoy's current position.
    map.addSource('doppelganger-path', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addLayer({
      id: 'doppelganger-path-line',
      type: 'line',
      source: 'doppelganger-path',
      paint: {
        'line-color': '#ffdd44',
        'line-width': 3,
        'line-opacity': 0.75,
      },
    });
    map.addSource('doppelganger-marker', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addLayer({
      id: 'doppelganger-marker-halo',
      type: 'circle',
      source: 'doppelganger-marker',
      paint: {
        'circle-radius': 11,
        'circle-color': '#ffdd44',
        'circle-opacity': 0.3,
      },
    });
    map.addLayer({
      id: 'doppelganger-marker-dot',
      type: 'circle',
      source: 'doppelganger-marker',
      paint: {
        'circle-radius': 6,
        'circle-color': '#ffdd44',
        'circle-stroke-color': '#0a0a0f',
        'circle-stroke-width': 1.5,
        'circle-opacity': 0.95,
      },
    });
    map.addLayer({
      id: 'doppelganger-marker-label',
      type: 'symbol',
      source: 'doppelganger-marker',
      layout: {
        'text-field': 'DECOY',
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 10,
        'text-offset': [0, -1.4],
        'text-anchor': 'bottom',
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': '#ffdd44',
        'text-halo-color': '#0a0a0f',
        'text-halo-width': 1.5,
        'text-opacity': 0.95,
      },
    });

    // Agent vision cone — pre-created here so the per-render useEffect
    // just calls setData. Created AFTER streets in the layer stack so
    // the cone renders on top of road lines (fixes the cone-under-streets
    // z-order issue when lazy-creation timing raced with addDataLayers).
    map.addSource('agent-vision-cone', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addLayer({
      id: 'agent-vision-cone-fill',
      type: 'fill',
      source: 'agent-vision-cone',
      paint: {
        'fill-color': '#ff4444',
        'fill-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0.04, 14, 0.08, 17, 0.12],
      },
    });
    map.addLayer({
      id: 'agent-vision-cone-outline',
      type: 'line',
      source: 'agent-vision-cone',
      paint: {
        'line-color': '#ff4444',
        'line-width': 1.5,
        'line-opacity': 0.4,
        'line-dasharray': [3, 2],
      },
    });

    // Stakeout devices — purple cones + dots. Pre-create all sources + layers
    // here so the per-device useEffect only has to call setData.
    map.addSource('stakeout-cones', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addLayer({
      id: 'stakeout-cones-fill',
      type: 'fill',
      source: 'stakeout-cones',
      paint: {
        'fill-color': '#aa88ff',
        'fill-opacity': ['case', ['boolean', ['get', 'active'], false], 0.20, 0.08],
      },
    });
    map.addLayer({
      id: 'stakeout-cones-outline',
      type: 'line',
      source: 'stakeout-cones',
      paint: {
        'line-color': '#aa88ff',
        'line-width': 1.5,
        'line-opacity': ['case', ['boolean', ['get', 'active'], false], 0.9, 0.45],
      },
    });
    map.addSource('stakeout-markers', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    // Outer halo / outline so the dot reads clearly over the cone
    map.addLayer({
      id: 'stakeout-markers-halo',
      type: 'circle',
      source: 'stakeout-markers',
      paint: {
        'circle-radius': 8,
        'circle-color': '#aa88ff',
        'circle-opacity': 0.25,
      },
    });
    map.addLayer({
      id: 'stakeout-markers-dot',
      type: 'circle',
      source: 'stakeout-markers',
      paint: {
        'circle-radius': 5,
        'circle-color': '#aa88ff',
        'circle-stroke-width': 1.5,
        'circle-stroke-color': '#ffffff',
      },
    });

    // Vision cone — green arc showing player's field of view
    const initialConeCoords = createConePolygon(
      gameState.player.position.lng, gameState.player.position.lat,
      tuning.visionConeRange,
      gameState.player.facing,
      gameState.player.visionConeWidth,
    );

    map.addSource('vision-cone', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {},
          geometry: { type: 'Polygon', coordinates: [initialConeCoords] },
        }],
      },
    });

    map.addLayer({
      id: 'vision-cone-fill',
      type: 'fill',
      source: 'vision-cone',
      paint: {
        'fill-color': '#44cc66',
        'fill-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0.06, 14, 0.12, 17, 0.18],
      },
    });

    map.addLayer({
      id: 'vision-cone-outline',
      type: 'line',
      source: 'vision-cone',
      paint: {
        'line-color': '#44cc66',
        'line-width': 1.5,
        'line-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0.15, 14, 0.35, 17, 0.5],
      },
    });

    // Vision cone handle — draggable point at the tip of the cone
    const facingRad = (gameState.player.facing * Math.PI) / 180;
    const pLatRad = (gameState.player.position.lat * Math.PI) / 180;
    const handleDLat = (tuning.visionConeRange * Math.cos(facingRad)) / 111000;
    const handleDLng = (tuning.visionConeRange * Math.sin(facingRad)) / (111000 * Math.cos(pLatRad));

    map.addSource('vision-handle', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: { facing: gameState.player.facing },
          geometry: {
            type: 'Point',
            coordinates: [
              gameState.player.position.lng + handleDLng,
              gameState.player.position.lat + handleDLat,
            ],
          },
        }],
      },
    });

    // Transit route highlight — shown when the player is boarded on a line
    map.addSource('transit-route-highlight', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addLayer({
      id: 'transit-route-highlight-line',
      type: 'line',
      source: 'transit-route-highlight',
      paint: {
        'line-color': ['get', 'color'],
        'line-width': ['interpolate', ['linear'], ['zoom'], 11, 3, 17, 6],
        'line-opacity': 0.85,
      },
    });
    map.addLayer({
      id: 'transit-route-highlight-stops',
      type: 'circle',
      source: 'transit-route-highlight',
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 4, 17, 8],
        'circle-color': '#ffffff',
        'circle-stroke-color': ['get', 'color'],
        'circle-stroke-width': 2,
      },
    });

    // Create eyeball icon for vision handle
    if (!map.hasImage('eyeball-icon')) {
      const size = 32;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d')!;
      const cx = size / 2, cy = size / 2;

      // Outer eye shape (almond)
      ctx.beginPath();
      ctx.moveTo(2, cy);
      ctx.quadraticCurveTo(cx, 4, size - 2, cy);
      ctx.quadraticCurveTo(cx, size - 4, 2, cy);
      ctx.closePath();
      ctx.fillStyle = '#44cc66';
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Iris
      ctx.beginPath();
      ctx.arc(cx, cy, 6, 0, Math.PI * 2);
      ctx.fillStyle = '#228844';
      ctx.fill();

      // Pupil
      ctx.beginPath();
      ctx.arc(cx, cy, 3, 0, Math.PI * 2);
      ctx.fillStyle = '#0a1a0f';
      ctx.fill();

      // Highlight
      ctx.beginPath();
      ctx.arc(cx - 2, cy - 2, 1.5, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.8)';
      ctx.fill();

      const imageData = ctx.getImageData(0, 0, size, size);
      map.addImage('eyeball-icon', imageData, { sdf: false });
    }

    map.addLayer({
      id: 'vision-handle-layer',
      type: 'symbol',
      source: 'vision-handle',
      layout: {
        'icon-image': 'eyeball-icon',
        'icon-size': ['interpolate', ['linear'], ['zoom'], 11, 0.6, 14, 0.9, 17, 1.2],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-rotate': ['get', 'facing'],
      },
    });

    // Player marker — pulsing blue dot
    map.addSource('player-marker', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Point',
            coordinates: [gameState.player.position.lng, gameState.player.position.lat],
          },
        }],
      },
    });
    // Outer glow
    map.addLayer({
      id: 'player-glow',
      type: 'circle',
      source: 'player-marker',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 10, 14, 14, 17, 20],
        'circle-color': theme.colors.playerMarker,
        'circle-opacity': 0.15,
        'circle-blur': 1,
      },
    });
    // Core dot
    map.addLayer({
      id: 'player-dot',
      type: 'circle',
      source: 'player-marker',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 4, 14, 6, 17, 9],
        'circle-color': theme.colors.playerMarker,
        'circle-opacity': 0.95,
        'circle-stroke-width': 2,
        'circle-stroke-color': 'rgba(68, 170, 255, 0.4)',
      },
    });

    // Rooftop marker — shown when player is on a rooftop (replaces the blue dot)
    map.addSource('rooftop-marker', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    // Outer pulse ring
    map.addLayer({
      id: 'rooftop-glow',
      type: 'circle',
      source: 'rooftop-marker',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 12, 14, 18, 17, 24],
        'circle-color': '#00e5ff',
        'circle-opacity': 0.12,
        'circle-blur': 0.8,
      },
      layout: { 'visibility': 'none' },
    });
    // Inner diamond-ish marker
    map.addLayer({
      id: 'rooftop-dot',
      type: 'circle',
      source: 'rooftop-marker',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 5, 14, 7, 17, 10],
        'circle-color': '#00e5ff',
        'circle-opacity': 0.9,
        'circle-stroke-width': 2,
        'circle-stroke-color': 'rgba(0, 229, 255, 0.5)',
      },
      layout: { 'visibility': 'none' },
    });

    // Car icon — shown when player is in a vehicle (replaces the blue dot).
    // Build a pre-tinted SVG data URL so we avoid canvas compositing (which
    // was rendering transparent for some browsers/SVGs). Wrapped in a blue
    // badge with white outline so the icon reads clearly against the map.
    if (!map.hasImage('car-icon')) {
      const carColor = theme.colors.playerMarker;
      // Raw car SVG body so we can hand it to wrapVehicleIconAsBadge the
      // same way the fetched transit SVGs are wrapped.
      const rawCarSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 26" width="52" height="52"><path d="M 8 5 C 7.449219 5 6.90625 5.265625 6.78125 5.625 C 6.78125 5.625 4.953125 8.019531 4.21875 9 C 3.484375 9.980469 3.46875 10 2.5625 10 L 1 10 C 0.449219 10 0 10.449219 0 11 L 0 16 C 0 16.550781 0.449219 17 1 17 L 3 17 C 3.246094 15.277344 4.710938 13.9375 6.5 13.9375 C 8.289063 13.9375 9.753906 15.277344 10 17 L 16 17 C 16.246094 15.277344 17.710938 13.9375 19.5 13.9375 C 21.289063 13.9375 22.753906 15.277344 23 17 L 25 17 C 25.550781 17 26 16.550781 26 16 L 26 14 C 26 13.007813 24.972656 10 21 10 C 20.109375 10 20.117188 10.007813 19.21875 8.9375 C 18.402344 7.960938 16.53125 5.625 16.53125 5.625 C 16.234375 5.265625 15.550781 5 15 5 Z M 8.34375 7 L 11 7 L 11 10 L 5.84375 10 L 7.90625 7.25 C 7.90625 7.25 8.210938 7 8.34375 7 Z M 13 7 L 14.65625 7 C 14.957031 7 15.324219 7.1875 15.40625 7.28125 L 17.625 10 L 13 10 Z M 6.5 15.1875 C 5.222656 15.1875 4.1875 16.222656 4.1875 17.5 C 4.1875 18.777344 5.222656 19.8125 6.5 19.8125 C 7.777344 19.8125 8.8125 18.777344 8.8125 17.5 C 8.8125 16.222656 7.777344 15.1875 6.5 15.1875 Z M 19.5 15.1875 C 18.222656 15.1875 17.1875 16.222656 17.1875 17.5 C 17.1875 18.777344 18.222656 19.8125 19.5 19.8125 C 20.777344 19.8125 21.8125 18.777344 21.8125 17.5 C 21.8125 16.222656 20.777344 15.1875 19.5 15.1875 Z"/></svg>`;
      const badgeSvg = wrapVehicleIconAsBadge(rawCarSvg, carColor);
      const dataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(badgeSvg)}`;
      const carImg = new Image();
      carImg.onload = () => {
        if (!map.hasImage('car-icon')) {
          map.addImage('car-icon', carImg, { pixelRatio: 2 });
        }
        if (!map.getLayer('player-car')) {
          const isCurrentlyInVehicle = gameState?.player.isInVehicle ?? false;
          const isCurrentlyHiding = gameState?.player.isInBuilding ?? false;
          map.addLayer({
            id: 'player-car',
            type: 'symbol',
            source: 'player-marker',
            layout: {
              'icon-image': 'car-icon',
              'icon-size': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 14, 0.7, 17, 1.0],
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'visibility': (isCurrentlyInVehicle && !isCurrentlyHiding) ? 'visible' : 'none',
            },
          });
        }
      };
      carImg.src = dataUrl;
    }

    // Taxi icon — reuses the Transport-taxi SVG file, wrapped in a yellow
    // badge with a white outline so it reads as its own vehicle mode and
    // stays legible on a busy map.
    if (!map.hasImage('taxi-icon')) {
      fetch('./Icons/Transport-taxi.svg')
        .then(r => r.text())
        .then(svgText => {
          const badge = wrapVehicleIconAsBadge(svgText, '#ffcc33');
          const dataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(badge)}`;
          const img = new Image();
          img.onload = () => {
            if (!map.hasImage('taxi-icon')) {
              map.addImage('taxi-icon', img, { pixelRatio: 2 });
            }
            if (!map.getLayer('player-taxi')) {
              const isCurrentlyInTaxi = gameState?.player.isInTaxi ?? false;
              const isCurrentlyHiding = gameState?.player.isInBuilding ?? false;
              map.addLayer({
                id: 'player-taxi',
                type: 'symbol',
                source: 'player-marker',
                layout: {
                  'icon-image': 'taxi-icon',
                  'icon-size': ['interpolate', ['linear'], ['zoom'], 11, 1.0, 14, 1.4, 17, 2.0],
                  'icon-allow-overlap': true,
                  'icon-ignore-placement': true,
                  'visibility': (isCurrentlyInTaxi && !isCurrentlyHiding) ? 'visible' : 'none',
                },
              });
            }
          };
          img.src = dataUrl;
        })
        .catch(() => { /* ignore — taxi svg fetch failure is non-fatal */ });
    }

    // Surveillance Van icon — purple tinted. Rendered at each deployed van's
    // current position via the surveillance-van-markers symbol layer.
    if (!map.hasImage('surveillance-van-icon')) {
      fetch('./Icons/intel-surveillance-van.svg')
        .then(r => r.text())
        .then(svgText => {
          const tinted = svgText.replace(/<svg /i, `<svg style="fill: #aa88ff" `);
          const dataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(tinted)}`;
          const img = new Image();
          img.onload = () => {
            if (!map.hasImage('surveillance-van-icon')) {
              map.addImage('surveillance-van-icon', img, { pixelRatio: 2 });
            }
            if (!map.getLayer('surveillance-van-marker-icon')) {
              map.addLayer({
                id: 'surveillance-van-marker-icon',
                type: 'symbol',
                source: 'surveillance-van-markers',
                layout: {
                  'icon-image': 'surveillance-van-icon',
                  'icon-size': ['interpolate', ['linear'], ['zoom'], 11, 0.7, 14, 1.0, 17, 1.6],
                  'icon-allow-overlap': true,
                  'icon-ignore-placement': true,
                },
              });
            }
          };
          img.src = dataUrl;
        })
        .catch(() => { /* non-fatal */ });
    }

    // Transit icons — load 4 SVG files, wrap each in a blue badge with
    // white outline + white icon so they read clearly on the map.
    const transitColor = theme.colors.playerMarker;
    const transitIcons: Array<{ type: 'subway' | 'tram' | 'bus' | 'ferry'; file: string; layerId: string }> = [
      { type: 'subway', file: './Icons/transport-subway.svg', layerId: 'player-subway' },
      { type: 'tram',   file: './Icons/Transport-tram.svg',   layerId: 'player-tram' },
      { type: 'bus',    file: './Icons/Transport-bus.svg',    layerId: 'player-bus' },
      { type: 'ferry',  file: './Icons/Transport-ferry.svg',  layerId: 'player-ferry' },
    ];
    for (const entry of transitIcons) {
      const imageName = `${entry.type}-icon`;
      if (map.hasImage(imageName) && map.getLayer(entry.layerId)) continue;
      fetch(entry.file)
        .then(r => r.text())
        .then(svgText => {
          const badge = wrapVehicleIconAsBadge(svgText, transitColor);
          const dataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(badge)}`;
          const img = new Image();
          img.onload = () => {
            if (!map.hasImage(imageName)) {
              map.addImage(imageName, img, { pixelRatio: 2 });
            }
            if (!map.getLayer(entry.layerId)) {
              map.addLayer({
                id: entry.layerId,
                type: 'symbol',
                source: 'player-marker',
                layout: {
                  'icon-image': imageName,
                  'icon-size': ['interpolate', ['linear'], ['zoom'], 11, 1.0, 14, 1.4, 17, 2.0],
                  'icon-allow-overlap': true,
                  'icon-ignore-placement': true,
                  'visibility': 'none',
                },
              });
            }
          };
          img.src = dataUrl;
        })
        .catch(() => {/* ignore — icon missing */});
    }
  }

  // Fit map to data bounds
  map.fitBounds(
    [
      [cityData.bounds.west, cityData.bounds.south],
      [cityData.bounds.east, cityData.bounds.north],
    ],
    { padding: 40, duration: 0 }
  );
}

// Hover radius in pixels — matches the crosshair cursor diameter
const HOVER_RADIUS = 18;

function addProximityHover(map: maplibregl.Map, spaceHeldRef: React.RefObject<boolean>, movementModeRef: React.RefObject<MovementMode | null>, hideModeRef: React.RefObject<boolean>, isDraggingConeRef: React.RefObject<boolean>, onHover?: (info: HoverInfo | null) => void) {
  const popup = new maplibregl.Popup({
    closeButton: false,
    closeOnClick: false,
    className: 'transit-popup',
    offset: 10,
  });

  let hoveredStopFilterId: string | null = null;
  let hoveredStreetName: string | null = null;
  let hoveredLineId: string | null = null;
  let hoveredItemId: string | null = null;
  let hoveredSafehouseId: string | null = null;
  let hoveredPassphraseId: string | null = null;
  let hoveredLandmarkName: string | null = null;

  const transitLayers = ['transit-lines-layer'];
  const transitStopLayers = ['transit-stops-layer'];

  // Query nearby zone polygons and return the loudest (highest multiplier) one.
  // Uses a pixel buffer around the cursor to catch zones adjacent to roads.
  const ZONE_QUERY_BUFFER = 20; // pixels
  function getZoneAtPoint(point: maplibregl.PointLike): { zone?: string; signalMultiplier?: number } {
    if (!map.getLayer('zones-fill')) return {};
    // point can be a MapLibre Point {x, y} or a [number, number] tuple
    const px = 'x' in (point as any) ? (point as any).x : (point as [number, number])[0];
    const py = 'x' in (point as any) ? (point as any).y : (point as [number, number])[1];
    const zoneBbox: [maplibregl.PointLike, maplibregl.PointLike] = [
      [px - ZONE_QUERY_BUFFER, py - ZONE_QUERY_BUFFER],
      [px + ZONE_QUERY_BUFFER, py + ZONE_QUERY_BUFFER],
    ];
    const features = map.queryRenderedFeatures(zoneBbox, { layers: ['zones-fill'] });
    if (features.length === 0) return {};

    // Pick the zone with the highest signal multiplier (loudest takes precedence)
    let bestZone: string | undefined;
    let bestMult = -1;
    const seen = new Set<string>();
    for (const f of features) {
      const zoneType = f.properties?.zoneType as string | undefined;
      if (!zoneType || seen.has(zoneType)) continue;
      seen.add(zoneType);
      const mult = tuning.zoneMultipliers[zoneType as ZoneType] ?? 0;
      if (mult > bestMult) {
        bestMult = mult;
        bestZone = zoneType;
      }
    }
    if (bestZone) {
      return { zone: bestZone, signalMultiplier: bestMult };
    }
    return {};
  }

  function clearAll() {
    if (hoveredStopFilterId) {
      map.setFilter('transit-stops-hover', ['==', ['get', 'id'], '']);
      hoveredStopFilterId = null;
    }
    if (hoveredStreetName) {
      hoveredStreetName = null;
    }
    if (hoveredLineId) {
      hoveredLineId = null;
      map.setFilter('transit-lines-highlight', ['==', ['get', 'lineId'], '']);
    }
    if (hoveredItemId !== null) {
      hoveredItemId = null;
      if (map.getLayer('game-items-hover')) {
        map.setFilter('game-items-hover', ['==', ['get', 'id'], '']);
      }
    }
    if (hoveredSafehouseId !== null) {
      hoveredSafehouseId = null;
      if (map.getLayer('game-safehouses-hover-glow')) {
        map.setFilter('game-safehouses-hover-glow', ['==', ['get', 'id'], '']);
      }
    }
    if (hoveredPassphraseId !== null) {
      hoveredPassphraseId = null;
      if (map.getLayer('game-passphrases-hover-glow')) {
        map.setFilter('game-passphrases-hover-glow', ['==', ['get', 'id'], '']);
      }
    }
    if (hoveredLandmarkName !== null) {
      hoveredLandmarkName = null;
    }
    popup.remove();
    onHover?.(null);
  }

  let lastHoverTime = 0;
  map.on('mousemove', (e) => {
    if (spaceHeldRef.current || movementModeRef.current || hideModeRef.current || isDraggingConeRef.current) {
      clearAll();
      return;
    }

    // Throttle hover queries to ~30fps
    const now = performance.now();
    if (now - lastHoverTime < 33) return;
    lastHoverTime = now;

    const point = e.point;
    // Scale hover radius by zoom: center dot only when zoomed out, full cursor ring when zoomed in
    const zoom = map.getZoom();
    const hoverR = zoom < 13 ? 3 : zoom < 15 ? Math.round(3 + (HOVER_RADIUS - 3) * ((zoom - 13) / 2)) : HOVER_RADIUS;
    const bbox: [maplibregl.PointLike, maplibregl.PointLike] = [
      [point.x - hoverR, point.y - hoverR],
      [point.x + hoverR, point.y + hoverR],
    ];

    // Clear stale POI hover states if cursor is no longer over them
    if (hoveredItemId !== null) {
      const still = map.getLayer('game-items-layer') && map.queryRenderedFeatures(bbox, { layers: ['game-items-layer'] }).some(f => f.properties?.id === hoveredItemId);
      if (!still) {
        hoveredItemId = null;
        if (map.getLayer('game-items-hover')) map.setFilter('game-items-hover', ['==', ['get', 'id'], '']);
        popup.remove();
      }
    }
    if (hoveredSafehouseId !== null) {
      const shLayers = ['game-safehouses-icon', 'game-safehouses-glow'].filter(l => map.getLayer(l));
      const still = shLayers.length > 0 && map.queryRenderedFeatures(bbox, { layers: shLayers }).some(f => f.properties?.id === hoveredSafehouseId);
      if (!still) {
        hoveredSafehouseId = null;
        if (map.getLayer('game-safehouses-hover-glow')) map.setFilter('game-safehouses-hover-glow', ['==', ['get', 'id'], '']);
        popup.remove();
      }
    }
    if (hoveredPassphraseId !== null) {
      const ppLayers = ['game-passphrases-glow', 'game-passphrases-icon'].filter(l => map.getLayer(l));
      const still = ppLayers.length > 0 && map.queryRenderedFeatures(bbox, { layers: ppLayers }).some(f => f.properties?.id === hoveredPassphraseId);
      if (!still) {
        hoveredPassphraseId = null;
        if (map.getLayer('game-passphrases-hover-glow')) map.setFilter('game-passphrases-hover-glow', ['==', ['get', 'id'], '']);
        popup.remove();
      }
    }
    if (hoveredLandmarkName !== null) {
      const lmLayers = ['landmarks-icons', 'landmarks-ring'].filter(l => map.getLayer(l));
      const still = lmLayers.length > 0 && map.queryRenderedFeatures(bbox, { layers: lmLayers }).some(f => f.properties?.name === hoveredLandmarkName);
      if (!still) {
        hoveredLandmarkName = null;
        popup.remove();
      }
    }

    // Priority: items/safehouses/passphrases > transit stops > transit lines > streets > buildings

    // Check item pickups
    if (map.getLayer('game-items-layer')) {
      const itemFeats = map.queryRenderedFeatures(bbox, { layers: ['game-items-layer'] });
      if (itemFeats.length > 0) {
        const f = itemFeats[0];
        const itemId = f.properties?.id;
        if (itemId !== hoveredItemId) {
          clearAll();
          hoveredItemId = itemId;
          if (map.getLayer('game-items-hover')) {
            map.setFilter('game-items-hover', ['==', ['get', 'id'], itemId]);
          }
          // Show item loot tooltip with inline item icons. lootInfo is
          // encoded as "id::category::name::pct" per row so the hover can
          // render the actual weapon/gadget glyph next to each possible
          // drop instead of text-only names.
          const coords = (f.geometry as GeoJSON.Point).coordinates;
          const props = f.properties!;
          const pType: string = props.pickupType || 'mixed';
          const typeLabel =
            pType === 'weapon' ? 'WEAPON CACHE' :
            pType === 'gadget' ? 'GADGET STATION' :
            pType === 'lockpick' ? 'LOCKPICK DROP' :
            pType === 'scanner' ? 'SCANNER DROP' :
            'SUPPLY DROP';
          const typeColor =
            pType === 'weapon' ? theme.colors.pickupWeapon :
            pType === 'gadget' ? theme.colors.pickupGadget :
            pType === 'lockpick' ? theme.colors.pickupLockpick :
            pType === 'scanner' ? theme.colors.pickupScanner :
            theme.colors.pickupMixed;
          const lootInfo: string = props.lootInfo || '';
          const lootLines = lootInfo.split('|').filter(Boolean);
          let html = `<div style="font-family:monospace;font-size:11px;max-width:220px;">`;
          html += `<div style="font-weight:700;color:${typeColor};margin-bottom:4px;">${typeLabel}</div>`;
          if (props.poiName) {
            html += `<div style="font-size:10px;color:#aaa;margin-bottom:6px;">${props.poiName}</div>`;
          }
          html += `<div style="font-size:9px;color:#ccc;margin-bottom:4px;letter-spacing:1px;">POSSIBLE LOOT:</div>`;
          for (const line of lootLines) {
            // Parse "id::category::name::pct" — falls back to the legacy
            // "name pct" format if encountered (shouldn't happen but safe).
            const parts = line.split('::');
            if (parts.length === 4) {
              const [itemId, category, name, pct] = parts;
              const iconSvg = getItemIconSvgHtml(itemId, category as ItemCategory, 14);
              html += `<div style="display:flex;align-items:center;gap:6px;font-size:10px;padding:1px 0;">`;
              html += `<span style="display:inline-flex;align-items:center;width:14px;height:14px;flex-shrink:0;">${iconSvg}</span>`;
              html += `<span style="color:#ddd;flex:1;">${name}</span>`;
              html += `<span style="color:${typeColor};">${pct}</span>`;
              html += `</div>`;
            } else {
              // Legacy fallback
              const legacy = line.match(/^(.+?)\s+(\d+%)$/);
              if (legacy) {
                html += `<div style="display:flex;justify-content:space-between;font-size:10px;padding:1px 0;">`;
                html += `<span style="color:#ddd;">${legacy[1]}</span>`;
                html += `<span style="color:#ffdd44;margin-left:8px;">${legacy[2]}</span>`;
                html += `</div>`;
              }
            }
          }
          html += `</div>`;
          popup.setLngLat(coords as [number, number]).setHTML(html).addTo(map);
        }
        onHover?.({ type: 'street', name: f.properties?.poiName || 'Item Pickup' });
        return;
      }
    }

    // Check safehouses
    if (map.getLayer('game-safehouses-icon') || map.getLayer('game-safehouses-glow')) {
      const shLayers = ['game-safehouses-icon', 'game-safehouses-glow'].filter(l => map.getLayer(l));
      const shFeats = map.queryRenderedFeatures(bbox, { layers: shLayers });
      if (shFeats.length > 0) {
        const f = shFeats[0];
        const shId = f.properties?.id;
        if (shId !== hoveredSafehouseId) {
          clearAll();
          hoveredSafehouseId = shId;
          if (map.getLayer('game-safehouses-hover-glow')) {
            map.setFilter('game-safehouses-hover-glow', ['==', ['get', 'id'], shId]);
          }
          const coords = (f.geometry as GeoJSON.Point).coordinates;
          popup
            .setLngLat(coords as [number, number])
            .setHTML(`<div style="font-family:monospace;max-width:200px;padding:6px 10px;background:rgba(8,12,20,0.95);border:1px solid rgba(68,204,136,0.4);border-radius:4px;"><div style="font-size:11px;color:#44cc88;font-weight:700;letter-spacing:1px;margin-bottom:4px;">SAFEHOUSE</div><div style="font-size:9px;color:#8899aa;line-height:1.4;">Enter to hide and draft a free card. Zero signal while inside. 2 turns, then auto-exit.</div></div>`)
            .addTo(map);
        }
        onHover?.({ type: 'street', name: 'Safehouse' });
        return;
      }
    }

    // Check passphrases
    {
      const ppLayers = ['game-passphrases-glow', 'game-passphrases-icon'].filter(l => map.getLayer(l));
      if (ppLayers.length > 0) {
        const ppFeats = map.queryRenderedFeatures(bbox, { layers: ppLayers });
        if (ppFeats.length > 0) {
          const f = ppFeats[0];
          const ppId = f.properties?.id;
          if (ppId !== hoveredPassphraseId) {
            clearAll();
            hoveredPassphraseId = ppId;
            if (map.getLayer('game-passphrases-hover-glow')) {
              map.setFilter('game-passphrases-hover-glow', ['==', ['get', 'id'], ppId]);
            }
            const coords = (f.geometry as GeoJSON.Point).coordinates;
            popup
              .setLngLat(coords as [number, number])
              .setHTML(`<div style="font-family:monospace;max-width:200px;padding:6px 10px;background:rgba(8,12,20,0.95);border:1px solid rgba(204,221,238,0.4);border-radius:4px;"><div style="font-size:11px;color:#ccddee;font-weight:700;letter-spacing:1px;margin-bottom:4px;">PASSPHRASE</div><div style="font-size:9px;color:#8899aa;line-height:1.4;">Collect 2 passphrases to unlock extraction points. Walk to this location to pick up.</div></div>`)
              .addTo(map);
          }
          onHover?.({ type: 'street', name: 'Passphrase' });
          return;
        }
      }
    }

    // Check landmarks — show name + larger image on hover
    if (map.getLayer('landmarks-icons') || map.getLayer('landmarks-ring')) {
      const lmLayers = ['landmarks-icons', 'landmarks-ring'].filter(l => map.getLayer(l));
      const lmFeats = map.queryRenderedFeatures(bbox, { layers: lmLayers });
      if (lmFeats.length > 0) {
        const f = lmFeats[0];
        const name = f.properties?.name || 'Landmark';
        const wikipedia = f.properties?.wikipedia || '';
        const coords = (f.geometry as GeoJSON.Point).coordinates as [number, number];

        if (name === hoveredLandmarkName) {
          // Already hovering this landmark — skip re-creating popup
          onHover?.({ type: 'street', name });
          return;
        }

        clearAll();
        hoveredLandmarkName = name;

        // Build tooltip — show name immediately, fetch larger image
        const popupId = `lm-popup-${Date.now()}`;
        let html = `<div id="${popupId}" style="font-family:monospace;max-width:220px;padding:6px 10px;background:rgba(8,12,20,0.95);border:1px solid rgba(204,170,102,0.4);border-radius:6px;">`;
        html += `<div id="${popupId}-img" style="width:200px;height:130px;background:rgba(255,255,255,0.03);border-radius:4px;margin-bottom:6px;overflow:hidden;"></div>`;
        html += `<div style="font-size:11px;color:#ccaa66;font-weight:700;letter-spacing:1px;">${name}</div>`;
        html += `</div>`;

        popup.setLngLat(coords).setHTML(html).addTo(map);

        // Fetch larger Wikipedia image for the tooltip. Prefer originalimage
        // (the full-size source), fall back to thumbnail.source as-is. The
        // previous version rewrote the thumbnail URL's size segment from
        // e.g. "/220px-" to "/400px-" — which often produced a 404 when
        // the source couldn't be upscaled to the requested size (SVGs,
        // some thumbnail paths, or small originals), leaving a broken-
        // image icon in the popup. Also wire an onerror handler on the
        // <img> so we hide the container instead of showing a broken icon
        // in the rare cases where the URL is legitimately stale.
        if (wikipedia) {
          const [lang, ...titleParts] = wikipedia.split(':');
          const title = titleParts.join(':');
          if (title) {
            fetch(`https://${lang}.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`)
              .then(r => r.json())
              .then(data => {
                const imgEl = document.getElementById(`${popupId}-img`);
                if (!imgEl) return;
                const imgUrl = data.originalimage?.source ?? data.thumbnail?.source ?? null;
                if (imgUrl) {
                  imgEl.innerHTML = `<img src="${imgUrl}" style="width:100%;height:100%;object-fit:cover;" onerror="this.parentElement.style.display='none'" />`;
                } else {
                  imgEl.style.display = 'none';
                }
              })
              .catch(() => {
                const imgEl = document.getElementById(`${popupId}-img`);
                if (imgEl) imgEl.style.display = 'none';
              });
          }
        }

        onHover?.({ type: 'street', name });
        return;
      }
    }

    // Check transit stops first
    const stopFeatures = map.queryRenderedFeatures(bbox, { layers: transitStopLayers });
    if (stopFeatures.length > 0 && map.getZoom() >= 13) {
      const feature = stopFeatures[0];
      const name = feature.properties?.name;
      const stopId = feature.properties?.id;
      const lineId = feature.properties?.lineId;
      const lineName = feature.properties?.lineName;
      if (stopId) {
        clearAll();
        hoveredStopFilterId = stopId;
        map.setFilter('transit-stops-hover', ['==', ['get', 'id'], stopId]);
        // Also highlight the line this stop belongs to
        if (lineId) {
          hoveredLineId = lineId;
          map.setFilter('transit-lines-highlight', ['==', ['get', 'lineId'], lineId]);
        }
        if (name) {
          popup
            .setLngLat((feature.geometry as GeoJSON.Point).coordinates as [number, number])
            .setHTML(`<div class="transit-stop-label">${name}</div>`)
            .addTo(map);
        }
        const stopZone = getZoneAtPoint(point);
        onHover?.({ type: 'stop', name: name || 'Stop', lineName: lineName || undefined, ...stopZone });
      }
      return;
    }

    // Check transit lines
    const transitFeatures = map.queryRenderedFeatures(bbox, { layers: transitLayers });
    if (transitFeatures.length > 0) {
      const feature = transitFeatures[0];
      const lineId = feature.properties?.lineId;
      if (lineId && lineId !== hoveredLineId) {
        clearAll();
        hoveredLineId = lineId;
        map.setFilter('transit-lines-highlight', ['==', ['get', 'lineId'], lineId]);
      }
      const name = feature.properties?.name;
      if (name) {
        popup.setLngLat(e.lngLat).setHTML(`<div class="transit-line-label">${name}</div>`).addTo(map);
        const lineZone = getZoneAtPoint(point);
        onHover?.({ type: 'transit', name, ...lineZone });
      }
      return;
    }

    // Check streets — only query layers visible at current zoom
    const streetZoom = map.getZoom();
    const activeStreetLayers: string[] = ['streets-major']; // always hoverable
    if (streetZoom >= 13) activeStreetLayers.push('streets-minor');
    if (streetZoom >= 14.5) activeStreetLayers.push('streets-residential');
    if (streetZoom >= 13.5) activeStreetLayers.push('streets-footpath');

    const streetFeatures = map.queryRenderedFeatures(bbox, { layers: activeStreetLayers });
    if (streetFeatures.length > 0) {
      const feature = streetFeatures[0];
      const name = feature.properties?.name;
      if (name) {
        // Clear non-street hovers
        if (hoveredLineId) {
          hoveredLineId = null;
          map.setFilter('transit-lines-highlight', ['==', ['get', 'lineId'], '']);
        }

        hoveredStreetName = name;
        // Road name tooltip removed — keep hover highlight only
        // Use zone at cursor position first (most accurate for where the player is),
        // fall back to the zone baked into the street feature (from its midpoint)
        let zoneInfo = getZoneAtPoint(point);
        if (!zoneInfo.zone) {
          const featureZone = feature.properties?.zone as string | undefined;
          if (featureZone) {
            const mult = tuning.zoneMultipliers[featureZone as ZoneType];
            zoneInfo = { zone: featureZone, signalMultiplier: mult ?? undefined };
          }
        }
        const tier = feature.properties?.tier as 'major' | 'minor' | 'residential' | 'footpath' | undefined;
        const ROAD_SIGNAL_WEIGHTS: Record<string, number> = { major: 0.9, minor: 0.6, residential: 0.3, footpath: 0.1 };
        onHover?.({
          type: 'street',
          name,
          ...zoneInfo,
          roadClass: tier || undefined,
          roadSignalWeight: tier ? ROAD_SIGNAL_WEIGHTS[tier] : undefined,
        });
        return;
      }
    }

    // No street/transit/stop — but still show zone info if available
    clearAll();
    const zoneInfo = getZoneAtPoint(point);
    if (zoneInfo.zone) {
      onHover?.({ type: 'street', name: '', ...zoneInfo });
    }
  });
}
